import React, { useEffect, useMemo, useState, useRef, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useScreenInit } from '../useScreenInit.js';
import { trackSignupSlackConnected, trackSignupProfileCompleted } from '../lib/analytics';
import { SignupShowcase } from '../components/SignupShowcase';
import { DarkGreenTexture } from '../components/DarkGreenTexture';
import { WaitlistModal } from '../components/WaitlistModal';
import { BillingModal } from '../components/BillingModal';
import { Check, ChevronDown, Shield, Lock, FileText, ArrowRight, ArrowLeft, Info, X, Search, Hash, MessageSquare, Headphones, Users, Grid3x3, Bell, Star, CreditCard, Calendar, ShieldCheck, Loader2, ArrowLeftRight, Plug, UserPlus, Sparkles, CheckCircle, ChevronRight, LayoutGrid, Plus, Send, PanelLeft, Folder, SquarePen, Zap, Brain, MoreHorizontal, Gauge, Settings2, PhoneCall, Paperclip, Home, GraduationCap, Activity, FolderOpen, Database, Globe, Monitor, Network, UserCircle, Copy } from 'lucide-react';
import { TRACKS, GROUP_NAMES } from '../data/workspaceTracks';
import { ONBOARDING_INTERVIEW, ONBOARDING_OPENER, ONBOARDING_SUMMARY, stepChoices, INTERVIEW_ROLES, type InterviewRole } from '../data/onboardingInterview';
import { WorkspaceViews, WorkspaceViewId } from '../components/gigachat/WorkspaceViews';
import { TALK_TO_SALES_URL } from '../lib/links';
const ALL_TOOLS = [{
  id: 'slack',
  name: 'Slack',
  icon: 'slack',
  category: 'communication'
}, {
  id: 'gmail',
  name: 'Gmail',
  icon: 'gmail',
  category: 'communication'
}, {
  id: 'outlook',
  name: 'Outlook',
  icon: 'microsoftoutlook',
  category: 'communication'
}, {
  id: 'teams',
  name: 'Microsoft Teams',
  icon: 'microsoftteams',
  category: 'communication'
}, {
  id: 'discord',
  name: 'Discord',
  icon: 'discord',
  category: 'communication'
}, {
  id: 'googledrive',
  name: 'Google Drive',
  icon: 'googledrive',
  category: 'docs'
}, {
  id: 'googlecalendar',
  name: 'Google Calendar',
  icon: 'googlecalendar',
  category: 'docs'
}, {
  id: 'notion',
  name: 'Notion',
  icon: 'notion',
  category: 'docs'
}, {
  id: 'confluence',
  name: 'Confluence',
  icon: 'confluence',
  category: 'docs'
}, {
  id: 'coda',
  name: 'Coda',
  icon: 'coda',
  category: 'docs'
}, {
  id: 'dropbox',
  name: 'Dropbox',
  icon: 'dropbox',
  category: 'docs'
}, {
  id: 'github',
  name: 'GitHub',
  icon: 'github',
  category: 'engineering'
}, {
  id: 'gitlab',
  name: 'GitLab',
  icon: 'gitlab',
  category: 'engineering'
}, {
  id: 'bitbucket',
  name: 'Bitbucket',
  icon: 'bitbucket',
  category: 'engineering'
}, {
  id: 'sentry',
  name: 'Sentry',
  icon: 'sentry',
  category: 'engineering'
}, {
  id: 'datadog',
  name: 'Datadog',
  icon: 'datadog',
  category: 'engineering'
}, {
  id: 'linear',
  name: 'Linear',
  icon: 'linear',
  category: 'project'
}, {
  id: 'jira',
  name: 'Jira',
  icon: 'jira',
  category: 'project'
}, {
  id: 'asana',
  name: 'Asana',
  icon: 'asana',
  category: 'project'
}, {
  id: 'trello',
  name: 'Trello',
  icon: 'trello',
  category: 'project'
}, {
  id: 'monday',
  name: 'Monday.com',
  icon: 'mondaydotcom',
  category: 'project'
}, {
  id: 'salesforce',
  name: 'Salesforce',
  icon: 'salesforce',
  category: 'crm'
}, {
  id: 'hubspot',
  name: 'HubSpot',
  icon: 'hubspot',
  category: 'crm'
}, {
  id: 'pipedrive',
  name: 'Pipedrive',
  icon: 'pipedrive',
  category: 'crm'
}, {
  id: 'zendesk',
  name: 'Zendesk',
  icon: 'zendesk',
  category: 'support'
}, {
  id: 'intercom',
  name: 'Intercom',
  icon: 'intercom',
  category: 'support'
}, {
  id: 'front',
  name: 'Front',
  icon: 'front',
  category: 'support'
}, {
  id: 'stripe',
  name: 'Stripe',
  icon: 'stripe',
  category: 'finance'
}, {
  id: 'quickbooks',
  name: 'QuickBooks',
  icon: 'quickbooks',
  category: 'finance'
}, {
  id: 'figma',
  name: 'Figma',
  icon: 'figma',
  category: 'design'
}, {
  id: 'miro',
  name: 'Miro',
  icon: 'miro',
  category: 'design'
}, {
  id: 'googleanalytics',
  name: 'Google Analytics',
  icon: 'googleanalytics',
  category: 'analytics'
}, {
  id: 'mixpanel',
  name: 'Mixpanel',
  icon: 'mixpanel',
  category: 'analytics'
}, {
  id: 'amplitude',
  name: 'Amplitude',
  icon: 'amplitude',
  category: 'analytics'
}, {
  id: 'mailchimp',
  name: 'Mailchimp',
  icon: 'mailchimp',
  category: 'marketing'
}, {
  id: 'googleads',
  name: 'Google Ads',
  icon: 'googleads',
  category: 'marketing'
}, {
  id: 'hootsuite',
  name: 'Hootsuite',
  icon: 'hootsuite',
  category: 'marketing'
}];
function getScopesForTool(tool: any) {
  if (tool.id === 'slack') {
    return [{
      id: 'read_channels',
      label: 'Read channel messages',
      description: "View messages in public channels you're a member of.",
      required: true
    }, {
      id: 'search_history',
      label: 'Search history',
      description: "Search across messages so Giga can cite what's been said.",
      required: true
    }, {
      id: 'read_files',
      label: 'Read files & attachments',
      description: 'Read files shared in channels and DMs.',
      required: false
    }, {
      id: 'read_dms',
      label: 'Read your direct messages',
      description: 'Index your DMs so Giga can find specific conversations.',
      required: false
    }, {
      id: 'post',
      label: 'Post on your behalf',
      description: 'Reply in channels and send DMs when you ask Giga to.',
      required: false
    }];
  }
  if (tool.category === 'docs') {
    return [{
      id: 'read_files',
      label: 'Read your files & folders',
      description: `Index docs, sheets, slides and PDFs in your ${tool.name}.`,
      required: true
    }, {
      id: 'read_shared',
      label: 'Read shared drives',
      description: 'Include drives shared with you by your team.',
      required: true
    }, {
      id: 'write_docs',
      label: 'Create & edit documents',
      description: 'Draft new docs when you ask Giga to.',
      required: false
    }];
  }
  if (tool.category === 'communication') {
    return [{
      id: 'read_msgs',
      label: 'Read messages & channels',
      description: 'Index conversations you have access to.',
      required: true
    }, {
      id: 'send_msgs',
      label: 'Send messages',
      description: 'Post updates and replies on your behalf.',
      required: true
    }];
  }
  if (tool.category === 'engineering' || tool.category === 'project') {
    return [{
      id: 'read_issues',
      label: 'Read issues & projects',
      description: 'Index tickets, PRs, and project states.',
      required: true
    }, {
      id: 'write_issues',
      label: 'Update issues',
      description: 'Change statuses and add comments.',
      required: false
    }];
  }
  return [{
    id: 'read_data',
    label: `Read ${tool.name} data`,
    description: 'Index records and objects you have access to.',
    required: true
  }, {
    id: 'write_data',
    label: `Update ${tool.name} records`,
    description: 'Create and modify records on your behalf.',
    required: false
  }];
}
function SlackIcon({
  size = 20
}: {
  size?: number;
}) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-11602" data-mp="7indd182815i">
      
      <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zm1.271 0a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313z" fill="#E01E5A" data-id="element-11603" data-mp="bjh6c90d0659" />
      
      <path d="M8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zm0 1.271a2.527 2.527 0 0 1 2.521 2.521 2.527 2.527 0 0 1-2.521 2.521H2.522A2.527 2.527 0 0 1 0 8.834a2.528 2.528 0 0 1 2.523 2.522v6.312z" fill="#36C5F0" data-id="element-11604" data-mp="3kp71kzyahrkf" />
      
      <path d="M18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522v-2.521zm-1.272 0a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.161 0a2.528 2.528 0 0 1 2.523 2.522v6.312z" fill="#2EB67D" data-id="element-11605" data-mp="1mz5p0o6kyj09" />
      
      <path d="M15.161 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.161 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zm0-1.272a2.527 2.527 0 0 1-2.52-2.521 2.526 2.526 0 0 1 2.52-2.522h6.317A2.527 2.527 0 0 1 24 15.163a2.528 2.528 0 0 1-2.522 2.521h-6.317z" fill="#ECB22E" data-id="element-11606" data-mp="5t23wuijad1k" />
      
    </svg>;
}
function GoogleIcon({
  size = 20
}: {
  size?: number;
}) {
  return <svg width={size} height={size} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" data-id="element-11607" data-mp="3j4at7mw0mu86">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.76h3.57c2.08-1.92 3.27-4.74 3.27-8.09z" fill="#4285F4" data-id="element-11608" data-mp="3e8ls3f4cl59r" />
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.76c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z" fill="#34A853" data-id="element-11609" data-mp="1cfecs5oqinnv" />
      <path d="M5.84 14.11a6.6 6.6 0 0 1 0-4.22V7.05H2.18a11 11 0 0 0 0 9.9l3.66-2.84z" fill="#FBBC05" data-id="element-11610" data-mp="17e8k27v2r0ec" />
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.05l3.66 2.84c.87-2.6 3.3-4.51 6.16-4.51z" fill="#EA4335" data-id="element-11611" data-mp="1jynmk015kodg" />
    </svg>;
}

// The three Google Workspace tools whose scopes are requested inside the same
// Google consent, so a Google signup arrives with a useful brain already
// connected instead of an empty one.
const GOOGLE_WORKSPACE_TOOLS = ['gmail', 'googledrive', 'googlecalendar'];
const BUSINESS_TYPES = ['E-commerce / DTC', 'Consumer app', 'SaaS / B2B software', 'Marketplace', 'Fintech', 'Healthcare / health-tech', 'Education / ed-tech', 'Media & entertainment', 'Gaming', 'AI / ML', 'Hardware / IoT', 'Real estate / proptech', 'Travel & hospitality', 'Food & beverage', 'Retail (brick & mortar)', 'Logistics & supply chain', 'Professional services', 'Agency / consulting', 'Nonprofit', 'Government / public sector', 'Manufacturing', 'Energy & climate', 'Crypto / web3', 'Other'];

// The connected Slack workspace. In production this comes from the Slack OAuth
// handshake (team name) so the post-connect screens address the user by their
// real workspace instead of a placeholder. Mirrors the account the user
// "connects" earlier in the flow (trackrheadquarters.slack.com).
const WORKSPACE_NAME = 'Trackr HQ';
const WORKSPACE_INITIAL = WORKSPACE_NAME.charAt(0);
function SearchableSelect({
  value,
  onChange,
  options,
  placeholder = 'Select an option',
  searchPlaceholder = 'Search…',
  ariaLabel,
  searchable = true
}: {
  value: string;
  onChange: (val: string) => void;
  options: string[];
  placeholder?: string;
  searchPlaceholder?: string;
  ariaLabel?: string;
  searchable?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(o => o.toLowerCase().includes(q));
  }, [query, options]);
  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);
  // Focus the search field when the panel opens (only when searchable)
  useEffect(() => {
    if (open && searchable) {
      setQuery('');
      const id = window.setTimeout(() => inputRef.current?.focus(), 0);
      return () => window.clearTimeout(id);
    }
  }, [open, searchable]);
  return <div ref={wrapperRef} className="relative" data-id="element-11612" data-mp="32gzunyodljbu">
      <button type="button" onClick={() => setOpen(o => !o)} aria-haspopup="listbox" aria-expanded={open} aria-label={ariaLabel} className={`w-full flex items-center justify-between gap-2 px-4 py-3 rounded-xl border bg-white text-left focus:outline-none transition-colors ${open ? 'border-[#EA580C]' : 'border-neutral-200'} ${value ? 'text-[#333333]' : 'text-neutral-400'}`} data-id="element-11613" data-mp="32elyfmhym12p">
        
        <span className="truncate" data-id="element-11614" data-mp="mtm5980aru44">{value || placeholder}</span>
        <ChevronDown size={18} className={`shrink-0 text-neutral-400 transition-transform ${open ? 'rotate-180' : ''}`} data-id="element-11615" data-mp="qsl9eqe7fmui" />
        
      </button>

      <AnimatePresence data-id="element-11616" data-mp="1egunf656f6b3">
        {open && <motion.div initial={{
        opacity: 0,
        y: -6
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -6
      }} transition={{
        duration: 0.15,
        ease: [0.22, 1, 0.36, 1]
      }} className="absolute z-30 mt-2 w-full rounded-xl border border-neutral-200 bg-white shadow-xl overflow-hidden" data-id="element-11617" data-mp="1jhs7jo9qyaf8">
          
            {searchable && <div className="p-2 border-b border-neutral-100" data-id="element-11618" data-mp="3v7bb8m2858bx">
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-neutral-50 border border-neutral-200 focus-within:border-[#EA580C] transition-colors" data-id="element-11619" data-mp="3v5hyqq4xjtw0">
                  <Search size={16} className="text-neutral-400 shrink-0" data-id="element-11620" data-mp="3io772e6tnzwj" />
                  <input ref={inputRef} type="text" value={query} onChange={e => setQuery(e.target.value)} placeholder={searchPlaceholder} className="w-full bg-transparent text-sm text-[#333333] placeholder:text-neutral-400 focus:outline-none" data-id="element-11621" data-mp="jvs3orootf0" />
              
                </div>
              </div>}
            <ul role="listbox" className="max-h-60 overflow-y-auto py-1" data-id="element-11622" data-mp="2z1rcyv1tt5tf">
              {filtered.length === 0 ? <li className="px-4 py-3 text-sm text-neutral-400" data-id="element-11623" data-mp="3dq5pxysyianp">
                  No matches found
                </li> : filtered.map(option => {
            const isSelected = option === value;
            return <li key={option} role="option" aria-selected={isSelected} data-id="element-11624" data-mp="2l8tbcqysihhf">
                      <button type="button" onClick={() => {
                onChange(option);
                setOpen(false);
              }} className={`w-full flex items-center justify-between gap-2 px-4 py-2.5 text-left text-sm transition-colors ${isSelected ? 'bg-[#FFEDD5] text-[#333333] font-medium' : 'text-[#333333] hover:bg-neutral-50'}`} data-id="element-11625" data-mp="2garrw4xgcg5f">
                    
                        <span className="truncate" data-id="element-11626" data-mp="1shynoptjn4of">{option}</span>
                        {isSelected && <Check size={16} className="text-[#EA580C] shrink-0" strokeWidth={2.5} data-id="element-11627" data-mp="1xbt3hi612zi8" />}
                      </button>
                    </li>;
          })}
            </ul>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
function MultiSelectDropdown({
  values,
  onChange,
  options,
  placeholder = 'Select options'
}: {
  values: string[];
  onChange: (val: string) => void;
  options: string[];
  placeholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);
  return <div ref={wrapperRef} className="relative" data-id="element-11628" data-mp="4mk195h0xiiu">
      <button type="button" onClick={() => setOpen(o => !o)} className={`w-full flex items-center justify-between gap-2 px-4 py-3 rounded-xl border bg-white text-left focus:outline-none transition-colors ${open ? 'border-[#EA580C]' : 'border-neutral-200'} ${values.length > 0 ? 'text-[#333333]' : 'text-neutral-400'}`} data-id="element-11629" data-mp="kp4mea1ftbe">
        
        <span className="truncate" data-id="element-11630" data-mp="o8ymj544rwx9">
          {values.length === 0 ? placeholder : values.length === 1 ? values[0] : `${values.length} selected`}
        </span>
        <ChevronDown size={18} className={`shrink-0 text-neutral-400 transition-transform ${open ? 'rotate-180' : ''}`} data-id="element-11631" data-mp="3908poq0k25fd" />
        
      </button>

      <AnimatePresence data-id="element-11632" data-mp="340cp70r2gw4k">
        {open && <motion.div initial={{
        opacity: 0,
        y: -6
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -6
      }} transition={{
        duration: 0.15,
        ease: [0.22, 1, 0.36, 1]
      }} className="absolute z-30 mt-2 w-full rounded-xl border border-neutral-200 bg-white shadow-xl overflow-hidden" data-id="element-11633" data-mp="343idwevs9vzr">
          
            <ul role="listbox" className="max-h-60 overflow-y-auto py-1" data-id="element-11634" data-mp="30heyierd4noc">
              {options.map(option => {
            const isSelected = values.includes(option);
            return <li key={option} role="option" aria-selected={isSelected} data-id="element-11635" data-mp="2rjbnice079as">
                    <button type="button" onClick={() => onChange(option)} className={`w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm transition-colors ${isSelected ? 'bg-[#FFEDD5]' : 'hover:bg-neutral-50'}`} data-id="element-11636" data-mp="2cu5nwnb97h2d">
                    
                      <div className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 transition-colors ${isSelected ? 'bg-[#EA580C] border-[#EA580C]' : 'border-neutral-300 bg-white'}`} data-id="element-11637" data-mp="23tr11jin5twr">
                      
                        {isSelected && <Check size={12} className="text-white" strokeWidth={3} data-id="element-11638" data-mp="1k7p82e30jbet" />}
                      </div>
                      <span className={`truncate ${isSelected ? 'text-[#333333] font-medium' : 'text-[#333333]'}`} data-id="element-11639" data-mp="1y770r4jfj8tg">
                      
                        {option}
                      </span>
                    </button>
                  </li>;
          })}
            </ul>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
function VisibilityDropdown({
  value,
  onChange
}: {
  value: 'team' | 'private';
  onChange: (val: 'team' | 'private') => void;
}) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);
  const isPrivate = value === 'private';
  const OPTIONS: {
    id: 'team' | 'private';
    Icon: typeof Users;
    label: string;
  }[] = [{
    id: 'team',
    Icon: Users,
    label: 'Team-only'
  }, {
    id: 'private',
    Icon: Lock,
    label: 'Private (invite only)'
  }];
  return <div ref={wrapperRef} className="relative" data-id="element-11640" data-mp="3aja66wwfeu7p">
      <button type="button" onClick={() => setOpen(o => !o)} aria-haspopup="listbox" aria-expanded={open} aria-label="Who can use this connection?" className={`inline-flex items-center gap-1.5 pl-2 pr-1.5 py-1 rounded-full border text-[11px] font-medium transition-colors ${open ? 'border-[#333333] bg-white' : 'border-neutral-200 bg-neutral-50 hover:border-neutral-300'} text-neutral-600`} data-id="element-11641" data-mp="3age7v7go23iq">
        
        {isPrivate ? <Lock size={11} data-id="element-11642" data-mp="2vtl14ql7q625" /> : <Users size={11} data-id="element-11643" data-mp="2vt2tyyx0i7fg" />}
        {isPrivate ? 'Private' : 'Team-only'}
        <ChevronDown size={12} className={`text-neutral-400 transition-transform ${open ? 'rotate-180' : ''}`} data-id="element-11644" data-mp="2vr8brsd4ktf2" />
        
      </button>

      <AnimatePresence data-id="element-11645" data-mp="2jape4msyup3r">
        {open && <motion.div initial={{
        opacity: 0,
        y: -4,
        scale: 0.98
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: -4,
        scale: 0.98
      }} transition={{
        duration: 0.14,
        ease: [0.22, 1, 0.36, 1]
      }} className="absolute right-0 z-30 mt-2 w-60 rounded-xl border border-neutral-200 bg-white shadow-xl overflow-hidden" role="listbox" data-id="element-11646" data-mp="2jbs2oo2tgfgi">
          
            <div className="px-3 pt-2.5 pb-1.5 text-[11px] font-bold uppercase tracking-wider text-neutral-400" data-id="element-11647" data-mp="1vkmqciw345ay">
              Who should have access?
            </div>
            {OPTIONS.map(opt => {
          const active = opt.id === value;
          return <button key={opt.id} type="button" role="option" aria-selected={active} onClick={() => {
            onChange(opt.id);
            setOpen(false);
          }} className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-left text-sm transition-colors ${active ? 'text-[#333333] font-medium' : 'text-[#333333] hover:bg-neutral-50'}`} data-id="element-11648" data-mp="1mmswrfppfprh">
                
                  <opt.Icon size={15} className="text-neutral-500 shrink-0" data-id="element-11649" data-mp="3tlldejooom19" />
                  <span className="flex-1 truncate" data-id="element-11650" data-mp="3ool6k6k0ff93">{opt.label}</span>
                  {active && <Check size={15} className="text-[#EA580C] shrink-0" strokeWidth={2.5} data-id="element-11651" data-mp="3jp8hrg9kvy3v" />}
                </button>;
        })}
          </motion.div>}
      </AnimatePresence>
    </div>;
}
function StickyRevealBar({
  children
}: {
  children: React.ReactNode;
}) {
  // A persistent fixed action bar pinned to the bottom of the screen. It stays
  // visible no matter where the user is in the scroll, with a one-time slide-up
  // entrance on mount. Self-contained so it never affects the page hook order.
  return <motion.div initial={{
    y: 90,
    opacity: 0
  }} animate={{
    y: 0,
    opacity: 1
  }} transition={{
    type: 'spring',
    stiffness: 380,
    damping: 34,
    delay: 0.1
  }} className="fixed inset-x-0 bottom-0 z-40 border-t border-neutral-200 bg-white/95 backdrop-blur-sm shadow-[0_-8px_24px_-12px_rgba(0,0,0,0.12)]" data-id="element-11652" data-mp="2vymb9tf82lvz">
      
      <div className="max-w-4xl mx-auto px-6 py-4" data-id="element-11653" data-mp="2vtwtfbpii1x0">{children}</div>
    </motion.div>;
}
// The tool picker must show the real, familiar mark for every company — not
// a letter badge or a loosely drawn icon. Each entry maps directly to the
// product's domain; logo.dev serves the canonical brand asset and the brand
// favicon is a resilient fallback if that service is temporarily unavailable.
const TOOL_LOGO_DOMAINS: Record<string, string> = {
  slack: 'slack.com',
  gmail: 'mail.google.com',
  microsoftoutlook: 'outlook.com',
  microsoftteams: 'teams.microsoft.com',
  discord: 'discord.com',
  googledrive: 'drive.google.com',
  notion: 'notion.so',
  confluence: 'atlassian.com',
  coda: 'coda.io',
  dropbox: 'dropbox.com',
  github: 'github.com',
  gitlab: 'gitlab.com',
  bitbucket: 'bitbucket.org',
  sentry: 'sentry.io',
  datadog: 'datadoghq.com',
  linear: 'linear.app',
  jira: 'atlassian.com',
  asana: 'asana.com',
  trello: 'trello.com',
  mondaydotcom: 'monday.com',
  salesforce: 'salesforce.com',
  hubspot: 'hubspot.com',
  pipedrive: 'pipedrive.com',
  zendesk: 'zendesk.com',
  intercom: 'intercom.com',
  front: 'front.com',
  stripe: 'stripe.com',
  quickbooks: 'quickbooks.intuit.com',
  figma: 'figma.com',
  miro: 'miro.com',
  googleanalytics: 'analytics.google.com',
  mixpanel: 'mixpanel.com',
  amplitude: 'amplitude.com',
  mailchimp: 'mailchimp.com',
  googleads: 'ads.google.com',
  hootsuite: 'hootsuite.com'
};
const LOGO_DEV_TOKEN = 'pk_E8dUI9PzTHmmaUwDeUCyiQ';
function ToolLogo({
  name,
  icon,
  className = 'w-12 h-12 mx-auto mb-3'
}: {
  name: string;
  icon: string;
  className?: string;
  fallbackTextSize?: string;
}) {
  const [source, setSource] = useState<'canonical' | 'favicon'>('canonical');
  const domain = TOOL_LOGO_DOMAINS[icon];
  const src = source === 'canonical' ? `https://img.logo.dev/${domain}?token=${LOGO_DEV_TOKEN}&size=128` : `https://www.google.com/s2/favicons?domain=${domain}&sz=128`;
  return <img src={src} alt={name} onError={() => {
    if (source === 'canonical') setSource('favicon');
  }} className={`${className} object-contain`} data-id="element-11654" data-mp="2dbltumjzsmht" />;
}
function TeamsIcon({
  size = 20
}: {
  size?: number;
}) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-11655" data-mp="353b43xj4g4ak">
      
      <path d="M16.5 13.5c1.93 0 3.5-1.57 3.5-3.5s-1.57-3.5-3.5-3.5-3.5 1.57-3.5 3.5 1.57 3.5 3.5 3.5z" fill="#5059C9" data-id="element-11656" data-mp="3a0l42744zycn" />
      
      <path d="M22.5 19.5v-1.5c0-1.93-1.57-3.5-3.5-3.5h-5c-1.93 0-3.5 1.57-3.5 3.5v1.5h12z" fill="#5059C9" data-id="element-11657" data-mp="2w7vejspegbas" />
      
      <path d="M7.5 12c1.93 0 3.5-1.57 3.5-3.5S9.43 5 7.5 5 4 6.57 4 8.5 5.57 12 7.5 12z" fill="#7B83EB" data-id="element-11658" data-mp="3o148735hek9j" />
      
      <path d="M13.5 19.5v-1.5c0-1.93-1.57-3.5-3.5-3.5h-5c-1.93 0-3.5 1.57-3.5 3.5v1.5h12z" fill="#7B83EB" data-id="element-11659" data-mp="3ss45i9iij4ck" />
      
    </svg>;
}
function GigaHeader({
  hideOnMobile = false
}: {
  hideOnMobile?: boolean;
}) {
  return <div className={`border-b border-neutral-100 px-6 py-4 ${hideOnMobile ? 'hidden lg:block' : ''}`} data-id="element-11660" data-mp="2034s0ozj74nm">
      
      <Link to="/" aria-label="Back to Giga home" className="inline-flex items-center gap-2 group w-fit" data-id="element-11661" data-mp="1v5ut8iq95gn4">
        
        <div className="w-8 h-8 rounded-lg bg-[#EA580C] flex items-center justify-center group-hover:scale-105 transition-transform" data-id="element-11662" data-mp="1r52nhzgl9tvl">
          <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="" aria-hidden="true" className="w-5 h-5 object-contain [filter:brightness(0)_invert(1)]" data-id="element-11663" data-mp="2iunsop6326mh" />
          
        </div>
        <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/giga4x.png" alt="Giga" className="h-5 object-contain" data-id="element-11664" data-mp="2a0j8golgftfx" />
        
      </Link>
    </div>;
}
export function Signup() {
  const screenInit = useScreenInit();
  const location = useLocation();
  const navigate = useNavigate();
  // Returning users go through /app-screen-mockups/sign-in, which is its own
  // screen — this component is only the signup flow.
  const isLogin = false;
  // The feature isn't live yet — the public "Get started" CTAs send users here
  // with ?waitlist=1, which drops them on the Slack connect screen (step 2)
  // with the waitlist popup open over it.
  const waitlistParam = new URLSearchParams(location.search).get('waitlist') === '1';
  // Any screen in this flow can be opened directly with ?step=<n> (e.g.
  // ?step=22 for the Giga web chat landing), so reviewing a single screen
  // doesn't mean clicking through the whole dummy signup.
  const stepParam = Number(new URLSearchParams(location.search).get('step'));
  // A few screens also get their own real route, so they show up in the route
  // dropdown and can be opened without knowing a step number.
  const STEP_ROUTES: Record<string, number> = {
    '/app-screen-mockups/google-consent': 20,
    '/app-screen-mockups/google-permissions': 21,
    '/app-screen-mockups/giga-chat': 22
  };
  const stepFromPath = STEP_ROUTES[location.pathname];
  const stepFromUrl = stepFromPath ?? (Number.isFinite(stepParam) && stepParam > 0 ? stepParam : undefined);
  const [step, setStep] = useState(stepFromUrl ?? screenInit.step ?? (waitlistParam ? 2 : isLogin ? 10 : 1));
  const [showWaitlist, setShowWaitlist] = useState(waitlistParam);
  // Which front door the user came through. Slack signups end in Slack; Google
  // signups have no Slack to hand off to, so they land in Giga's web chat and
  // can add Slack later. Only the final destination differs — the setup steps
  // in between are shared.
  const [authProvider, setAuthProvider] = useState<'slack' | 'google'>(stepFromUrl && stepFromUrl >= 20 ? 'google' : 'slack');
  // Step 22: the web chat's nav stays out of the way. A collapsed rail holds
  // the brand mark and one toggle; the sidebar itself slides in over the page
  // only when asked for, so the composer is never competing with navigation.
  const [chatNavOpen, setChatNavOpen] = useState(false);
  const [chatNavMoreOpen, setChatNavMoreOpen] = useState(false);
  const [chatAccountOpen, setChatAccountOpen] = useState(false);
  const [chatBillingOpen, setChatBillingOpen] = useState(false);
  // Step 22 composer: what this chat is allowed to draw on. Empty by default —
  // Giga uses everything the user can see unless they narrow it.
  const [contextOpen, setContextOpen] = useState(false);
  const [contextTab, setContextTab] = useState<'All' | 'Groups' | 'Tracks' | 'Credentials' | 'Databases'>('All');
  const [contextQuery, setContextQuery] = useState('');
  const [chatContext, setChatContext] = useState<string[]>([]);
  // Which pane the chat shell is showing. 'chat' is the thread; everything
  // else is a workspace view rendered inside the same shell, so the nav never
  // navigates away.
  const [chatView, setChatView] = useState<'chat' | WorkspaceViewId>('chat');
  // Picking a destination does NOT close the nav — once opened it stays until
  // the user closes it, so moving between views doesn't cost a re-open.
  const openChatView = (v: 'chat' | WorkspaceViewId) => {
    setChatView(v);
    setChatNavMoreOpen(false);
  };
  // DEBUG: a brand-new workspace with nothing connected yet, so the context
  // picker's empty state can be reviewed without unpicking the setup steps.
  const [chatNoConnections, setChatNoConnections] = useState(false);
  const [chatNotifOpen, setChatNotifOpen] = useState(false);
  const [chatChecklistOpen, setChatChecklistOpen] = useState(false);
  const [chatChecklistStep, setChatChecklistStep] = useState<string | null>('Connect your tools');
  // Step 22: the live thread. Empty until the first prompt, at which point the
  // greeting is replaced by the conversation and the composer stays put.
  type ChatMsg = {
    id: number;
    role: 'user' | 'giga';
    text?: string;
    steps?: {
      icon: string;
      label: string;
    }[];
    answer?: string[];
    sources?: {
      icon: string;
      label: string;
    }[];
    thinking?: boolean;
    /** Onboarding interview: suggested answers for this question. */
    choices?: string[];
    multi?: boolean;
    /** Set once the question has been answered, so the chips retire. */
    answered?: boolean;
    /** Closing summary of what the interview produced. */
    created?: readonly {
      icon: string;
      title: string;
      detail: string;
    }[];
    /** A Giga concept being introduced alongside the question. */
    concept?: {
      label: string;
      title: string;
      lines: string[];
    };
  };
  const [chatMessages, setChatMessages] = useState<ChatMsg[]>([]);
  // Conversational onboarding. `null` = never started; an index = mid-interview;
  // 'done' = finished, which is what ticks the setup checklist.
  const [interviewIndex, setInterviewIndex] = useState<number | 'done' | null>(null);
  const [interviewPicks, setInterviewPicks] = useState<string[]>([]);
  // Captured at step one; every later question branches on it.
  const [interviewRole, setInterviewRole] = useState<InterviewRole | null>(null);
  // Derived rather than hardcoded, so the ring and the list can never disagree.
  const setupProgress = (() => {
    const done = 2 + (interviewIndex === 'done' ? 1 : 0) + (chatMessages.length > 0 ? 1 : 0);
    return {
      done,
      total: 6,
      ratio: done / 6
    };
  })();
  const [chatCreditNoticeSeen, setChatCreditNoticeSeen] = useState(false);
  const [chatDraft, setChatDraft] = useState('');
  const chatThreadRef = useRef<HTMLDivElement>(null);
  const chatScripts: Record<string, {
    steps: {
      icon: string;
      label: string;
    }[];
    answer: string[];
    sources: {
      icon: string;
      label: string;
    }[];
  }> = {
    "What changed in my inbox while I was out?": {
      steps: [{
        icon: 'gmail',
        label: 'Read 63 unread threads in Gmail'
      }, {
        icon: 'googlecalendar',
        label: 'Cross-checked against your calendar'
      }],
      answer: ['**Three things need you today.**', '**Acme renewal** — their legal team sent redlines on Tuesday and asked for a reply by Friday. Two clauses changed: liability cap and data residency.', '**Northwind pilot** — Marcus confirmed the pilot start for 8 Sep and is waiting on your sign-off for seat count.', '**Invoice #2214** — finance flagged it as 21 days overdue and has already chased twice.', 'The remaining 60 threads are newsletters, calendar noise and CC traffic. I can archive those if you want.'],
      sources: [{
        icon: 'gmail',
        label: '63 threads'
      }, {
        icon: 'googlecalendar',
        label: '4 events'
      }]
    },
    'Summarise the deck in Drive from yesterday': {
      steps: [{
        icon: 'googledrive',
        label: 'Found “Q3 Board Review.pdf” — edited yesterday'
      }, {
        icon: 'googledrive',
        label: 'Read 24 slides'
      }],
      answer: ['**Q3 Board Review — 24 slides, edited by Priya at 18:40.**', 'The argument is that growth held while spend fell: revenue up 34% quarter over quarter, CAC down 12%, and net retention at 118%.', 'Two risks are named — hiring is a quarter behind plan, and one enterprise renewal worth 8% of ARR is unsigned.', 'Slide 19 has a gap: the ask is stated but there is no numbers slide behind it. Worth filling before Thursday.'],
      sources: [{
        icon: 'googledrive',
        label: 'Q3 Board Review.pdf'
      }]
    },
    "Who am I meeting tomorrow, and what do I need?": {
      steps: [{
        icon: 'googlecalendar',
        label: 'Read tomorrow’s 4 events'
      }, {
        icon: 'gmail',
        label: 'Pulled the last thread with each attendee'
      }],
      answer: ['**Four meetings tomorrow. Two need prep.**', '**09:30 — Acme, legal review (45m).** Bring a position on the liability cap; their redlines are still unanswered.', '**11:00 — Internal standup (15m).** Nothing needed.', '**14:00 — Northwind, pilot kickoff (60m).** Marcus wants seat count confirmed. Last thread says 25, your notes say 40.', '**16:30 — 1:1 with Priya (30m).** She flagged the slide 19 gap, so expect that.', 'I can draft the Acme reply and a one-pager for Northwind now.'],
      sources: [{
        icon: 'googlecalendar',
        label: '4 events'
      }, {
        icon: 'gmail',
        label: '6 threads'
      }]
    }
  };
  // ── Conversational onboarding ──────────────────────────────────────────
  // Giga asks one question per turn. Answers arrive either from the suggestion
  // chips or from the composer, so the interview never blocks free typing.
  const pushInterviewQuestion = (index: number, role: InterviewRole | null, delay = 900) => {
    const step = ONBOARDING_INTERVIEW[index];
    const choices = stepChoices(step, role);
    const id = Date.now() + index + 2;
    setChatMessages(prev => [...prev, {
      id,
      role: 'giga',
      thinking: true,
      steps: step.work
    }]);
    setTimeout(() => {
      setChatMessages(prev => prev.map(m => m.id === id ? {
        ...m,
        thinking: false,
        answer: [...step.question],
        concept: step.concept ? {
          ...step.concept,
          lines: [...step.concept.lines]
        } : undefined,
        choices,
        multi: step.multi
      } : m));
      // Opt out, not in: the likely answers arrive already ticked.
      if (step.multi && step.preselectAll) setInterviewPicks(choices);
    }, delay);
  };
  const startOnboarding = () => {
    setChatChecklistOpen(false);
    setChatCreditNoticeSeen(true);
    setInterviewIndex(0);
    setInterviewPicks([]);
    setInterviewRole(null);
    setChatDraft('');
    // The composer is pre-populated and sent for them, so the interview starts
    // as a normal message in the thread rather than a modal takeover.
    setChatMessages(prev => [...prev, {
      id: Date.now(),
      role: 'user',
      text: ONBOARDING_OPENER
    }]);
    pushInterviewQuestion(0, null, 1100);
  };
  const answerInterview = (text: string) => {
    if (typeof interviewIndex !== 'number') return;
    const next = interviewIndex + 1;
    // Step one sets the branch for every question after it. Free text still
    // works — anything unrecognised falls back to the generalist set.
    let role = interviewRole;
    if (ONBOARDING_INTERVIEW[interviewIndex].key === 'role') {
      role = INTERVIEW_ROLES.find(r => r.toLowerCase() === text.trim().toLowerCase()) ?? null;
      setInterviewRole(role);
    }
    setChatDraft('');
    setInterviewPicks([]);
    setChatMessages(prev => [...prev.map(m => m.choices ? {
      ...m,
      answered: true
    } : m), {
      id: Date.now(),
      role: 'user',
      text
    }]);
    if (next < ONBOARDING_INTERVIEW.length) {
      setInterviewIndex(next);
      pushInterviewQuestion(next, role, 1000);
      return;
    }
    // Final turn: state what the interview actually produced.
    setInterviewIndex('done');
    const id = Date.now() + 1;
    setChatMessages(prev => [...prev, {
      id,
      role: 'giga',
      thinking: true,
      steps: [...ONBOARDING_SUMMARY.work]
    }]);
    setTimeout(() => {
      setChatMessages(prev => prev.map(m => m.id === id ? {
        ...m,
        thinking: false,
        answer: [...ONBOARDING_SUMMARY.answer],
        created: ONBOARDING_SUMMARY.created
      } : m));
    }, 1500);
  };
  const sendChatMessage = (text: string) => {
    const clean = text.trim();
    if (!clean) return;
    if (typeof interviewIndex === 'number') {
      answerInterview(clean);
      return;
    }
    // The credit welcome is a first-run message. Once they've asked something
    // it has done its job, so it never returns — even on a new chat.
    setChatCreditNoticeSeen(true);
    const script = chatScripts[clean] ?? {
      steps: [{
        icon: 'gmail',
        label: 'Searched your connected tools'
      }],
      answer: ["Here's what I found across Gmail, Drive and Calendar. Ask me to act on any of it and I'll take it from there."],
      sources: [{
        icon: 'gmail',
        label: 'Gmail'
      }, {
        icon: 'googledrive',
        label: 'Drive'
      }]
    };
    const base = Date.now();
    setChatDraft('');
    setChatMessages(prev => [...prev, {
      id: base,
      role: 'user',
      text: clean
    }, {
      id: base + 1,
      role: 'giga',
      thinking: true,
      steps: script.steps
    }]);
    // Reveal the steps, then the answer, so the thread reads as work being
    // done rather than a block of text appearing at once.
    setTimeout(() => {
      setChatMessages(prev => prev.map(m => m.id === base + 1 ? {
        ...m,
        thinking: false,
        answer: script.answer,
        sources: script.sources
      } : m));
    }, 1600);
  };
  useEffect(() => {
    if (chatMessages.length > 0) {
      chatThreadRef.current?.scrollTo({
        top: chatThreadRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  }, [chatMessages]);
  const [selectedChannels, setSelectedChannels] = useState<string[]>([]);
  const [selectedTools, setSelectedTools] = useState<string[]>([]);
  // Step 10: once the user signs out of all workspaces, the remembered
  // workspace list disappears and the screen falls back to a clean sign-in.
  const [signedOut, setSignedOut] = useState(false);
  // Step 10: only the most recently used workspace is shown by default. The
  // full list is one click away, so returning users get a single obvious way
  // back in rather than a wall of near-identical rows competing with the
  // auth buttons above them.
  const [showAllWorkspaces, setShowAllWorkspaces] = useState(false);
  // Step 10: Microsoft Teams isn't live yet, so the "Join Microsoft Teams
  // waitlist" button opens an inline email capture and confirms once submitted.
  const [teamsWaitlistOpen, setTeamsWaitlistOpen] = useState(false);
  const [teamsWaitlistEmail, setTeamsWaitlistEmail] = useState('');
  const [teamsWaitlistDone, setTeamsWaitlistDone] = useState(false);
  const submitTeamsWaitlist = (e: React.FormEvent) => {
    e.preventDefault();
    if (!teamsWaitlistEmail.trim()) return;
    setTeamsWaitlistDone(true);
  };
  // Toggle a tool in/out of the selected set (used by the step 3 tool grid)
  const toggleTool = (id: string) => {
    setSelectedTools(prev => prev.includes(id) ? prev.filter(t => t !== id) : [...prev, id]);
  };
  const [emailOptIn, setEmailOptIn] = useState(true);
  const [toolSearch, setToolSearch] = useState('');
  // Step 5: "Tell us more about you" select answers
  const [foundUs, setFoundUs] = useState('');
  const [role, setRole] = useState('');
  // Step 5: what kind of business the user runs (required, searchable)
  const [businessType, setBusinessType] = useState('');
  // Step 5: which AI Teammate(s) the user wants to create first (required, multi-select)
  const [firstTeammates, setFirstTeammates] = useState<string[]>([]);
  const toggleFirstTeammate = (option: string) => setFirstTeammates(prev => prev.includes(option) ? prev.filter(o => o !== option) : [...prev, option]);
  // Step 6: Functional roles for tool recommendations (multi-select)
  const [funcRoles, setFuncRoles] = useState<string[]>([]);
  const [roleMenuOpen, setRoleMenuOpen] = useState(false);
  // Step 6: integrations the user requested when not found in the catalog
  const [requestedTools, setRequestedTools] = useState<string[]>([]);
  // Step 6: optional link the user can paste for a tool we don't list yet
  const [requestedToolLink, setRequestedToolLink] = useState('');
  // Step 11: selected trial plan
  const [plan, setPlan] = useState('plus');
  // Step 11: mobile-only order-summary disclosure (Stripe Checkout pattern)
  const [payDetailsOpen, setPayDetailsOpen] = useState(false);
  // Step 7: per-tool OAuth-style connection flow
  const [connectedConns, setConnectedConns] = useState<string[]>([]);
  // Whether the user has kicked off the connection sequence with the start button
  const [connectionsStarted, setConnectionsStarted] = useState(false);
  const [permModal, setPermModal] = useState<null | {
    id: string;
    name: string;
    icon: string;
    scopes: any[];
  }>(null);
  const [connectPhase, setConnectPhase] = useState<'consent' | 'handing-off' | null>(null);
  // Step 3: Slack OAuth consent — per-permission checkbox state. The two
  // REQUIRED scopes stay locked on; the rest are user-toggleable.
  const [slackOAuthScopes, setSlackOAuthScopes] = useState<Record<string, boolean>>({
    readChannels: true,
    searchHistory: true,
    readFiles: true,
    readDMs: false,
    postOnBehalf: true
  });
  // Step 7: local state for optional permission checkboxes in the modal
  const [optionalScopes, setOptionalScopes] = useState<Record<string, boolean>>({});
  // Step 7: per-tool integration visibility — 'private' (only you) or 'team'
  // (shared with the whole workspace).
  const [connVisibility, setConnVisibility] = useState<Record<string, 'private' | 'team'>>({});
  const USE_CASE_RECS: Record<string, string[]> = {
    'Marketing & Ad Campaigns': ['googleanalytics', 'googleads', 'mailchimp', 'hubspot', 'notion', 'slack'],
    'Operations & Workflow Automation': ['notion', 'slack', 'googledrive', 'asana', 'monday', 'quickbooks'],
    'Data analysis & reporting': ['googleanalytics', 'mixpanel', 'amplitude', 'notion', 'slack', 'github'],
    'Sales & CRM': ['salesforce', 'hubspot', 'pipedrive', 'slack', 'gmail', 'googledrive'],
    'Content & Creative Production': ['figma', 'miro', 'notion', 'slack', 'googledrive', 'dropbox'],
    'Engineering & Development': ['github', 'linear', 'slack', 'sentry', 'googledrive', 'notion'],
    'Executive / Leadership Assistant': ['slack', 'gmail', 'notion', 'googledrive', 'stripe', 'salesforce'],
    'Agency/Consultancy & Client Operations': ['slack', 'notion', 'googledrive', 'asana', 'trello', 'zoom'],
    'eCommerce & Shopify': ['shopify', 'stripe', 'mailchimp', 'googleanalytics', 'slack', 'zendesk'],
    Other: ['slack', 'notion', 'googledrive', 'gmail']
  };
  useEffect(() => {
    if (step === 6 && selectedTools.length === 0) {
      const activeRoles = funcRoles.length > 0 ? funcRoles : firstTeammates.length > 0 ? firstTeammates : ['Engineering & Development'];
      if (funcRoles.length === 0) {
        setFuncRoles(activeRoles);
      }
      const initialTools = new Set<string>();
      activeRoles.forEach(r => {
        USE_CASE_RECS[r]?.forEach(t => initialTools.add(t));
      });
      if (initialTools.size === 0) {
        USE_CASE_RECS['Engineering & Development'].forEach(t => initialTools.add(t));
      }
      // A Google signup already granted Gmail, Drive and Calendar during
      // consent, so those always appear (and show as connected) rather than
      // being offered again as something to set up.
      if (authProvider === 'google') {
        GOOGLE_WORKSPACE_TOOLS.forEach(t => initialTools.add(t));
      }
      setSelectedTools(Array.from(initialTools));
    }
  }, [step, firstTeammates, funcRoles.length, selectedTools.length, authProvider]);
  // Step 7: connections to make — derived from the tools the user selected.
  // Falls back to a sensible default set when nothing was selected (e.g. when
  // the screen is opened directly via screen state).
  const step7Connections = useMemo(() => {
    const ids = selectedTools.length > 0 ? selectedTools : ['slack', 'googledrive', 'notion', 'github', 'linear', 'sentry'];
    return ids.map(id => {
      const tool = ALL_TOOLS.find(t => t.id === id) || {
        id,
        name: id,
        icon: id,
        category: 'other'
      };
      return {
        ...tool,
        scopes: getScopesForTool(tool)
      };
    });
  }, [selectedTools]);
  const step7ActiveIdx = step7Connections.findIndex(c => !connectedConns.includes(c.id));
  const step7AllDone = step7Connections.length > 0 && connectedConns.length === step7Connections.length;
  // Where setup ends. A Slack signup gets handed off to Slack; a Google signup
  // has nothing to hand off to, so it lands in Giga's own web chat.
  const finalStep = authProvider === 'google' ? 22 : 8;
  // Landing/left-pane animation state. These hooks MUST live above the
  // per-step early returns below — otherwise renders that return early would
  // run fewer hooks than renders that fall through, tripping React #300.
  const LANDING_TESTIMONIAL_COUNT = 3;
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  useEffect(() => {
    const testimonialInterval = setInterval(() => {
      setActiveTestimonial(prev => (prev + 1) % LANDING_TESTIMONIAL_COUNT);
    }, 7000);
    return () => clearInterval(testimonialInterval);
  }, []);
  // Setup checklist progress — the rows resolve one-by-one (pending → done) so
  // the card reads like a live, sub-60-second setup actually happening.
  const SETUP_STEPS = 3;
  const [setupStep, setSetupStep] = useState(0);
  useEffect(() => {
    const start = setTimeout(() => {
      const id = setInterval(() => {
        setSetupStep(prev => {
          if (prev >= SETUP_STEPS) {
            clearInterval(id);
            return prev;
          }
          return prev + 1;
        });
      }, 750);
      (window as any).__gigaSetupInterval = id;
    }, 600);
    return () => {
      clearTimeout(start);
      clearInterval((window as any).__gigaSetupInterval);
    };
  }, []);
  // Step 22: where a Google signup lands. There is no Slack to hand off to,
  // so Giga's own web chat is the product surface. Opens on a real composer
  // with starters drawn from what was just connected, so the first question is
  // one click rather than a blank page. Slack is offered, not required.
  if (step === 22) {
    const starters = ["What changed in my inbox while I was out?", 'Summarise the deck in Drive from yesterday', "Who am I meeting tomorrow, and what do I need?"];
    return <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      duration: 0.2,
      ease: [0.23, 1, 0.32, 1]
    }} className={`min-h-screen bg-[#FAF8F4] font-body transition-[padding] duration-250 ease-[cubic-bezier(0.23,1,0.32,1)] ${chatNavOpen ? 'md:pl-[272px]' : ''}`} data-id="element-11665" data-mp="237lkc2e2d4wa">
        {/* Collapsed rail: just the mark and the one control that reveals the
         nav. Mirrors the reference — brand top-left, nothing else claiming
         space until the user asks for it. Hidden while the nav is pinned open,
         since the panel carries its own header. */}
        <div className={`fixed left-0 top-0 z-30 flex items-center gap-1 p-3 ${chatNavOpen ? 'md:hidden' : ''}`} data-id="element-11666" data-mp="1th0m4g2ay2dw">
          <Link to="/" aria-label="Brightwave workspace" className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#1F3A3D] text-[13px] font-bold tracking-tight text-white transition-transform hover:scale-105" data-id="element-11667" data-mp="1yav3hcufq59t">
            BW
          </Link>
          <button type="button" onClick={() => setChatNavOpen(true)} aria-label="Open navigation" aria-expanded={chatNavOpen} className="flex h-9 w-9 items-center justify-center rounded-lg text-neutral-400 transition-colors hover:bg-white hover:text-[#2B211A]" data-id="element-11668" data-mp="3vbubwrj4zxax">
            <PanelLeft size={17} data-id="element-11669" data-mp="379zkky9szcf" />
          </button>
        </div>

        {/* Top-right: what needs your attention, and the one human escape
         hatch. Both stay out of the composer's way. */}
        <div className="fixed right-0 top-0 z-30 flex items-center gap-1.5 p-3" data-id="element-11670" data-mp="d5g0az8cbzfp">
          {/* Once a thread exists it has weight and an audience — its size and
           who can see it belong here, not buried in a settings panel. */}
          {chatView === 'chat' && chatMessages.length > 0 && <span className="mr-1 hidden items-center gap-2.5 text-[12.5px] text-neutral-500 sm:flex" data-id="element-11671" data-mp="mxdfsi6dtdk8">
              <span data-id="element-11672" data-mp="vvpe76dhpfn8">
                {(0.01 + chatMessages.length * 0.006).toFixed(2)} MB
              </span>
              <button type="button" className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1 transition-colors hover:bg-white hover:text-[#2B211A]" data-id="element-11673" data-mp="1fhqy7mb54nt1">
                <Copy size={12} className="text-neutral-400" data-id="element-11674" data-mp="1kfspguipztfl" />
                Private
              </button>
            </span>}
          {/* Setup progress sits with the other status affordances rather than
           floating over the composer. A ring and a count — status, not promo. */}
          <button type="button" onClick={() => setChatChecklistOpen(o => !o)} aria-expanded={chatChecklistOpen} className={`relative flex items-center gap-2 rounded-full border py-1.5 pl-2.5 pr-3 transition-colors ${chatChecklistOpen ? 'border-[#E2DACD] bg-white' : 'border-[#EFEAE1]/80 bg-white/70 backdrop-blur-sm hover:border-[#E2DACD] hover:bg-white'}`} data-id="element-11675" data-mp="wpext541rwlj">
            {/* A single unresolved to-do. Red rather than brand orange, because
             its job is to be the one thing on screen you want to clear. */}
            {interviewIndex === null && <span className="absolute -right-1 -top-1 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-[#DC2626] px-1 text-[10.5px] font-bold leading-none text-white ring-2 ring-[#FAF8F4]" data-id="element-11676" data-mp="30xbsti4rvstv">
                <span className="absolute inset-0 -z-10 animate-ping rounded-full bg-[#DC2626] opacity-60" data-id="element-11677" data-mp="3auq6xb3845j0" />
                1
              </span>}
            <svg viewBox="0 0 20 20" className="h-[15px] w-[15px] -rotate-90" aria-hidden="true" data-id="element-11678" data-mp="3fvnslxnfzhp9">
              <circle cx="10" cy="10" r="8" fill="none" stroke="#EFEAE1" strokeWidth="3" data-id="element-11679" data-mp="3jvo53cqzdito" />
              <circle cx="10" cy="10" r="8" fill="none" stroke="#2B211A" strokeWidth="3" strokeLinecap="round" strokeDasharray={2 * Math.PI * 8} strokeDashoffset={2 * Math.PI * 8 * (1 - setupProgress.ratio)} data-id="element-11680" data-mp="3otg7j0p94nc4" />
            </svg>
            <span className="hidden text-[12.5px] font-medium text-neutral-500 sm:inline" data-id="element-11681" data-mp="2hal33znu48z">
              Finish setup
            </span>
            <span className="text-[12.5px] text-neutral-300" data-id="element-11682" data-mp="bfcvga5aa98x">{setupProgress.done}/{setupProgress.total}</span>
          </button>
          <div className="relative" data-id="element-11683" data-mp="1vzqdlc9wt2rh">
            <button type="button" onClick={() => setChatNotifOpen(o => !o)} aria-label="Notifications" aria-expanded={chatNotifOpen} className="relative flex h-9 w-9 items-center justify-center rounded-lg text-neutral-400 transition-colors hover:bg-white hover:text-[#2B211A]" data-id="element-11684" data-mp="1rwlbhatqr33d">
              <Bell size={17} data-id="element-11685" data-mp="1i0iaw9ejfcij" />
              <span className="absolute right-2 top-2 h-[7px] w-[7px] rounded-full bg-[#EA580C] ring-2 ring-[#FAF8F4]" data-id="element-11686" data-mp="2flwekyykd9ko" />
            </button>
            <AnimatePresence data-id="element-11687" data-mp="2aoe3p30zkka1">
              {chatNotifOpen && <>
                  <div className="fixed inset-0 z-30" onClick={() => setChatNotifOpen(false)} data-id="element-11688" data-mp="25vtao3pwmb7k" />
                  <motion.div initial={{
                opacity: 0,
                y: -6,
                scale: 0.98
              }} animate={{
                opacity: 1,
                y: 0,
                scale: 1
              }} exit={{
                opacity: 0,
                y: -6,
                scale: 0.98
              }} transition={{
                duration: 0.18,
                ease: [0.23, 1, 0.32, 1]
              }} className="absolute right-0 top-[calc(100%+6px)] z-40 w-[300px] origin-top-right overflow-hidden rounded-2xl border border-[#EFEAE1] bg-white shadow-[0_24px_60px_-24px_rgba(43,33,26,0.35)]" data-id="element-11689" data-mp="20xrwnx7c522y">
                    <p className="border-b border-[#F4F0E9] px-4 py-2.5 text-[11px] font-bold uppercase tracking-wider text-neutral-400" data-id="element-11690" data-mp="mw3x5dbaniay">
                      Notifications
                    </p>
                    <ul className="divide-y divide-[#F4F0E9]" data-id="element-11691" data-mp="d125oatjq457">
                      {[{
                    title: 'Gmail, Drive and Calendar connected',
                    time: 'Just now'
                  }, {
                    title: 'Your $100 in credits is active',
                    time: 'Just now'
                  }].map(n => <li key={n.title} className="px-4 py-3" data-id="element-11692" data-mp="1tbnyy1cp2yf7">
                          <p className="text-[13.5px] font-medium leading-snug text-[#2B211A]" data-id="element-11693" data-mp="1ybitcevoi650">
                            {n.title}
                          </p>
                          <p className="mt-0.5 text-[12px] text-neutral-400" data-id="element-11694" data-mp="23hxko7syqvpy">
                            {n.time}
                          </p>
                        </li>)}
                    </ul>
                  </motion.div>
                </>}
            </AnimatePresence>
          </div>
        </div>

        {/* Setup checklist, hung off the top-right control. Only the step you
         are on expands, so the panel stays short. */}
        <div className="fixed right-3 top-[52px] z-40 w-[300px]" data-id="element-11695" data-mp="2m7dqnanf3gdr">
          <AnimatePresence initial={false} data-id="element-11696" data-mp="308gd3oibqsp7">
            {chatChecklistOpen && <motion.div key="checklist" initial={{
            opacity: 0,
            y: -8,
            scale: 0.98
          }} animate={{
            opacity: 1,
            y: 0,
            scale: 1
          }} exit={{
            opacity: 0,
            y: -8,
            scale: 0.98
          }} transition={{
            duration: 0.2,
            ease: [0.23, 1, 0.32, 1]
          }} className="origin-top-right overflow-hidden rounded-2xl border border-[#EFEAE1] bg-white shadow-[0_28px_70px_-28px_rgba(43,33,26,0.4)]" data-id="element-11697" data-mp="305lv03ms9jyy">
                <div className="border-b border-[#F4F0E9] px-4 py-3.5" data-id="element-11698" data-mp="1c9xi6ulb3on5">
                  <div className="flex items-center justify-between gap-3" data-id="element-11699" data-mp="1h9a2b7ksmhm1">
                    <span className="text-[14px] font-semibold text-[#2B211A]" data-id="element-11700" data-mp="1h761iyi6xypz">
                      Finish setup
                    </span>
                    <button type="button" onClick={() => setChatChecklistOpen(false)} aria-label="Close checklist" className="text-neutral-300 transition-colors hover:text-[#2B211A]" data-id="element-11701" data-mp="ok37ojogmc62">
                      <X size={15} data-id="element-11702" data-mp="ya4jabzmm243" />
                    </button>
                  </div>
                  <div className="mt-2.5 flex items-center gap-2.5" data-id="element-11703" data-mp="v0f4gsaeyws">
                    <div className="h-[3px] flex-1 overflow-hidden rounded-full bg-[#F0EBE2]" data-id="element-11704" data-mp="uqn6nnomhog">
                      <div className="h-full rounded-full bg-[#2B211A] transition-[width] duration-300 ease-out" style={{
                    width: `${setupProgress.ratio * 100}%`
                  }} data-id="element-11705" data-mp="5sino62e7c49" />
                    </div>
                    <span className="text-[12px] text-neutral-400" data-id="element-11706" data-mp="3pf2fl2d0zhtv">
                      {setupProgress.done} of {setupProgress.total}
                    </span>
                  </div>
                </div>

                {/* The one outstanding thing, lifted out of the list. Everything
                 below is a task; this is an offer to do the tasks for you. */}
                {interviewIndex === null && <div className="border-b border-[#F4F0E9] bg-[#FFF6F2] px-4 py-3.5" data-id="element-11707" data-mp="36okrjb351aeu">
                    <p className="flex items-center gap-2 text-[13.5px] font-semibold text-[#2B211A]" data-id="element-11708" data-mp="31p89d3n7oril">
                      <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#DC2626]" data-id="element-11709" data-mp="2wqddp145b8h2" />
                      Let Giga set you up
                    </p>
                    <p className="mt-1.5 text-[12.5px] leading-relaxed text-neutral-500" data-id="element-11710" data-mp="2nskydwyfc0to">
                      A short conversation. Giga learns your role, opens your
                      tracks and schedules your first pulses from the answers.
                    </p>
                    <button type="button" onClick={startOnboarding} className="mt-3 inline-flex items-center gap-1.5 rounded-xl bg-[#EA580C] px-3.5 py-2 text-[12.5px] font-bold text-white transition-colors hover:bg-[#C2410C]" data-id="element-11711" data-mp="245zye5uooech">
                      Start
                      <ArrowRight size={13} data-id="element-11712" data-mp="2wv735mkqe066" />
                    </button>
                  </div>}

                <ul className="divide-y divide-[#F4F0E9]" data-id="element-11713" data-mp="31sp3wpolh933">
                  {[{
                label: 'Create your Giga account',
                done: true
              }, {
                label: 'Onboard with Giga',
                done: interviewIndex === 'done',
                body: interviewIndex === 'done' ? 'Done — your tracks, pulses and memory files are running.' : 'Answer a few questions in the chat and Giga builds the rest for you.',
                cta: interviewIndex === null ? 'Start onboarding' : undefined,
                action: 'onboard'
              }, {
                label: 'Connect your tools',
                done: true,
                body: 'Gmail, Drive and Calendar are in. Add Notion, Slack or Linear so Giga can act across the rest of your stack.',
                cta: 'Connect other tools',
                view: 'integrations'
              }, {
                label: 'Ask your first question',
                done: chatMessages.length > 0,
                body: 'Try one of the prompts above — it answers from the tools you just connected.'
              }, {
                label: 'Set up a Pulse',
                done: false,
                body: 'Put a recurring job on a schedule, like a Monday pipeline recap.',
                cta: 'Create a Pulse',
                view: 'pulses'
              }, {
                label: 'Build your first Workflow',
                done: false,
                body: 'Chain a few steps together so one prompt runs the whole job.',
                cta: 'Build a Workflow',
                view: 'journeys'
              }].map(item => {
                const open = chatChecklistStep === item.label;
                return <li key={item.label} data-id="element-11714" data-mp="2yoeuqxr8nz4d">
                        <button type="button" onClick={() => setChatChecklistStep(open ? null : item.label)} className="flex w-full items-center gap-2.5 px-4 py-3 text-left" data-id="element-11715" data-mp="33gy6dp6d8k6j">
                          {item.done ? <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#2B211A]" data-id="element-11716" data-mp="3decacpb3rghy">
                              <Check size={11} strokeWidth={3.5} className="text-white" data-id="element-11717" data-mp="3idfi4higw0tm" />
                            </span> : <span className="h-5 w-5 shrink-0 rounded-full border-[1.5px] border-dashed border-neutral-300" data-id="element-11718" data-mp="3mfk9tapcundm" />}
                          <span className={`flex-1 text-[13.5px] font-semibold ${item.done ? 'text-neutral-400 line-through' : 'text-[#2B211A]'}`} data-id="element-11719" data-mp="1o18tafwnhliu">
                            {item.label}
                          </span>
                          <ChevronDown size={14} className={`shrink-0 text-neutral-300 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} data-id="element-11720" data-mp="1kjtgojv7y6oo" />
                        </button>
                        <AnimatePresence initial={false} data-id="element-11721" data-mp="1asgsc28mpyne">
                          {open && item.body && <motion.div initial={{
                      height: 0,
                      opacity: 0
                    }} animate={{
                      height: 'auto',
                      opacity: 1
                    }} exit={{
                      height: 0,
                      opacity: 0
                    }} transition={{
                      duration: 0.2,
                      ease: [0.23, 1, 0.32, 1]
                    }} className="overflow-hidden" data-id="element-11722" data-mp="16rn1azzw43qa">
                              <div className="px-4 pb-4 pl-[42px]" data-id="element-11723" data-mp="j1akezmo2wnk">
                                <p className="text-[12.5px] leading-relaxed text-neutral-500" data-id="element-11724" data-mp="iyemvceqe9zn">
                                  {item.body}
                                </p>
                                {item.cta && <button type="button" onClick={() => (item as any).action === 'onboard' ? startOnboarding() : openChatView((item as any).view === 'journeys' ? 'workflows' : (item as any).view === 'integrations' ? 'connections' : (item as any).view)} className="mt-2.5 inline-flex items-center gap-1.5 rounded-xl bg-[#2B211A] px-3.5 py-2 text-[12.5px] font-bold text-white transition-colors hover:bg-black" data-id="element-11725" data-mp="3akoqbft8fdsr">
                                    {item.cta}
                                    <ArrowRight size={13} data-id="element-11726" data-mp="3ph60w4tlkoe9" />
                                  </button>}
                              </div>
                            </motion.div>}
                        </AnimatePresence>
                      </li>;
              })}
                </ul>
              </motion.div>}
          </AnimatePresence>
        </div>

        {/* DEBUG: preview the picker with an unconnected workspace. */}
        <button type="button" onClick={() => setChatNoConnections(v => !v)} title="Debug: empty workspace" aria-label="Debug: empty workspace" aria-pressed={chatNoConnections} className={`fixed bottom-4 left-4 z-30 flex h-7 w-7 items-center justify-center rounded-full border transition-colors ${chatNoConnections ? 'border-[#2B211A] bg-[#2B211A] text-white' : 'border-[#EFEAE1] bg-white text-neutral-300 hover:text-[#2B211A]'}`} data-id="element-11727" data-mp="2416ydm6u65v4">
          <Settings2 size={13} data-id="element-11728" data-mp="2cp2wkctdheha" />
        </button>

        {/* The sidebar itself — Tracks and Chats are the content; everything
         else is one quiet "More" disclosure at the bottom. */}
        <AnimatePresence data-id="element-11729" data-mp="2hpyxchifgkih">
          {chatNavOpen && <>
              {/* Scrim only on narrow screens, where the pinned panel would
               cover the content. On desktop the layout makes room instead. */}
              <motion.div key="nav-scrim" initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} transition={{
            duration: 0.2,
            ease: 'easeOut'
          }} onClick={() => setChatNavOpen(false)} className="fixed inset-0 z-40 bg-[#2B211A]/20 md:hidden" data-id="element-11730" data-mp="s9hfv5evgqek" />
              <motion.aside key="nav-panel" initial={{
            x: -280,
            opacity: 0.6
          }} animate={{
            x: 0,
            opacity: 1
          }} exit={{
            x: -280,
            opacity: 0.6
          }} transition={{
            duration: 0.25,
            ease: [0.23, 1, 0.32, 1]
          }} className="fixed left-0 top-0 z-50 flex h-full w-[272px] flex-col border-r border-[#EFEAE1] bg-white" data-id="element-11731" data-mp="kacqmmks5jh2">
                <div className="flex items-center justify-between gap-2 px-3 py-3" data-id="element-11732" data-mp="1hwtn5plovt1r">
                  <span className="flex items-center gap-2" data-id="element-11733" data-mp="1cx77l9ie20ps">
                    <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#1F3A3D] text-[13px] font-bold tracking-tight text-white" data-id="element-11734" data-mp="1cxpceuj49n9b">
                      BW
                    </span>
                    <span className="text-[14px] font-bold text-[#2B211A]" data-id="element-11735" data-mp="1mx7vl8r7d50b">
                      Brightwave
                    </span>
                  </span>
                  <button type="button" onClick={() => setChatNavOpen(false)} aria-label="Close navigation" className="flex h-8 w-8 items-center justify-center rounded-lg text-neutral-400 transition-colors hover:bg-[#F6F2EA] hover:text-[#2B211A]" data-id="element-11736" data-mp="2aj0gil83g9od">
                    <PanelLeft size={16} data-id="element-11737" data-mp="21k6kavyodlbc" />
                  </button>
                </div>

                <div className="flex-1 overflow-y-auto px-3 pb-4" data-id="element-11738" data-mp="1mruzw3ubxdd0">
                  {/* Outlined rather than a solid black bar: it is the primary
                   action, but a filled slab dominates a panel this quiet. */}
                  <button type="button" onClick={() => {
                openChatView('chat');
                setChatMessages([]);
              }} className="mb-5 flex w-full items-center gap-2.5 rounded-xl border border-[#E7E0D4] bg-white px-3 py-2.5 text-[13.5px] font-semibold text-[#2B211A] transition-colors hover:border-[#2B211A]" data-id="element-11739" data-mp="1hu2xgfw268uk">
                    <SquarePen size={15} className="text-neutral-400" data-id="element-11740" data-mp="2b5rinxa4ng98" />
                    New chat
                  </button>

                  {/* The four surfaces people set up early and revisit often —
                   promoted out of the launcher so they're one click, not two. */}
                  <ul className="mb-4" data-id="element-11741" data-mp="1xg7hux03wt2k">
                    {[{
                  label: 'Assistants',
                  icon: UserCircle,
                  view: 'assistants'
                }, {
                  label: 'Pulses',
                  icon: Activity,
                  view: 'pulses'
                }, {
                  label: 'Connections',
                  icon: Plug,
                  view: 'connections'
                }, {
                  label: 'Groups',
                  icon: Users,
                  view: 'groups'
                }].map(item => <li key={item.label} data-id="element-11742" data-mp="38ul5c0qy76i2">
                        <button type="button" onClick={() => openChatView(item.view as WorkspaceViewId)} className={`flex w-full items-center gap-2.5 rounded-lg px-2 py-[7px] text-left text-[13.5px] transition-colors hover:bg-[#F6F2EA] hover:text-[#2B211A] ${chatView === item.view ? 'bg-[#F1ECE3] font-semibold text-[#2B211A]' : 'font-medium text-neutral-600'}`} data-id="element-11743" data-mp="38vmhy9nm411w">
                          <item.icon size={15} strokeWidth={1.9} className={`shrink-0 ${chatView === item.view ? 'text-[#2B211A]' : 'text-neutral-400'}`} data-id="element-11744" data-mp="3v2qllnpryc65" />
                          {item.label}
                        </button>
                      </li>)}
                    {/* The occasional surfaces continue the same list rather
                     than living in a popover that covered the sidebar. */}
                    {chatNavMoreOpen && [{
                  label: 'Workflows',
                  icon: Network,
                  view: 'workflows'
                }, {
                  label: 'Skills',
                  icon: GraduationCap,
                  view: 'skills'
                }, {
                  label: 'Power Ups',
                  icon: Zap,
                  view: 'power-ups'
                }, {
                  label: 'Files',
                  icon: FolderOpen,
                  view: 'files'
                }, {
                  label: 'Tables',
                  icon: Database,
                  view: 'tables'
                }, {
                  label: 'Browser',
                  icon: Globe,
                  view: 'browser'
                }, {
                  label: 'Viewer',
                  icon: Monitor,
                  view: 'viewer'
                }].map(item => <li key={item.label} data-id="element-11745" data-mp="2drrze8d9q6av">
                          <button type="button" onClick={() => setChatView(item.view as WorkspaceViewId)} className={`flex w-full items-center gap-2.5 rounded-lg px-2 py-[7px] text-left text-[13.5px] transition-colors hover:bg-[#F6F2EA] hover:text-[#2B211A] ${chatView === item.view ? 'bg-[#F1ECE3] font-semibold text-[#2B211A]' : 'font-medium text-neutral-600'}`} data-id="element-11746" data-mp="2dsjhcgdy05io">
                            <item.icon size={15} strokeWidth={1.9} className={`shrink-0 ${chatView === item.view ? 'text-[#2B211A]' : 'text-neutral-400'}`} data-id="element-11747" data-mp="3ry53vwzon0zu" />
                            {item.label}
                          </button>
                        </li>)}
                    <li data-id="element-11748" data-mp="3nxm8xu2htetf">
                      <button type="button" onClick={() => setChatNavMoreOpen(o => !o)} aria-expanded={chatNavMoreOpen} className="flex w-full items-center gap-1.5 rounded-lg px-2 py-[7px] text-left text-[13px] font-medium text-neutral-400 transition-colors hover:bg-[#F6F2EA] hover:text-[#2B211A]" data-id="element-11749" data-mp="3nx32pam7gev8">
                        {chatNavMoreOpen ? 'View less' : 'View more'}
                        <ChevronDown size={13} className={`transition-transform duration-200 ${chatNavMoreOpen ? 'rotate-180' : ''}`} data-id="element-11750" data-mp="5lzlqch4j42i" />
                      </button>
                    </li>
                  </ul>

                  {/* Six each, then a way to the full list — a sidebar that
                   scrolls for 18 rows stops being scannable. */}
                  <p className="px-2 pb-1.5 text-[10.5px] font-bold uppercase tracking-[0.09em] text-neutral-400" data-id="element-11751" data-mp="3mx9wk7z4xluu">
                    Tracks
                  </p>
                  <ul className="mb-1" data-id="element-11752" data-mp="3cxsstw3g1i1i">
                    {TRACKS.filter(t => t.status === 'active').slice(0, 6).map(t => <li key={t.name} data-id="element-11753" data-mp="37zr8z8y71eoz">
                        <button type="button" onClick={() => openChatView('tracks')} className="flex w-full items-center gap-2.5 rounded-lg px-2 py-[7px] text-left text-[13.5px] font-medium text-neutral-600 transition-colors hover:bg-[#F6F2EA] hover:text-[#2B211A]" data-id="element-11754" data-mp="380sq9v5syxf5">
                          <Folder size={14} strokeWidth={1.9} className="shrink-0 text-neutral-300" data-id="element-11755" data-mp="3vwey7n5ztvb8" />
                          <span className="truncate" data-id="element-11756" data-mp="3qx29sxzh4g6j">{t.name}</span>
                        </button>
                      </li>)}
                  </ul>
                  <button type="button" onClick={() => openChatView('tracks')} className="mb-5 w-full rounded-lg px-2 py-1 text-left text-[12.5px] font-medium text-neutral-400 transition-colors hover:bg-[#F6F2EA] hover:text-[#2B211A]" data-id="element-11757" data-mp="s9y2169b64h4">
                    All tracks
                  </button>

                  <p className="px-2 pb-1.5 text-[10.5px] font-bold uppercase tracking-[0.09em] text-neutral-400" data-id="element-11758" data-mp="ztp6vtwnkdd1">
                    Chats
                  </p>
                  <ul className="mb-1" data-id="element-11759" data-mp="19qlfs0qgr1i4">
                    {['Inbox catch-up', 'Tomorrow’s meetings', 'Q3 pipeline review', 'Renewal risk in Salesforce', 'Draft reply to Acme legal', 'Summarise the board deck'].map(c => <li key={c} data-id="element-11760" data-mp="1ip5sml9cegyf">
                        <button type="button" onClick={() => openChatView('chats')} className="flex w-full items-center rounded-lg px-2 py-[7px] text-left text-[13.5px] font-medium text-neutral-600 transition-colors hover:bg-[#F6F2EA] hover:text-[#2B211A]" data-id="element-11761" data-mp="1io44pg789zza">
                          <span className="truncate" data-id="element-11762" data-mp="1xgo5492zttd1">{c}</span>
                        </button>
                      </li>)}
                  </ul>
                  <button type="button" onClick={() => openChatView('chats')} className="w-full rounded-lg px-2 py-1 text-left text-[12.5px] font-medium text-neutral-400 transition-colors hover:bg-[#F6F2EA] hover:text-[#2B211A]" data-id="element-11763" data-mp="22c3f2pxvr546">
                    All chats
                  </button>
                </div>

                {/* Account — usage, billing and settings belong to the person,
                 not the workspace, so they sit under the avatar rather than in
                 the feature launcher. */}
                <div className="relative border-t border-[#EFEAE1] p-3" data-id="element-11764" data-mp="3rvoob68fvg94">
                  <button type="button" onClick={() => setChatAccountOpen(o => !o)} aria-expanded={chatAccountOpen} className="flex w-full items-center gap-2.5 rounded-lg px-2 py-1.5 transition-colors hover:bg-[#F6F2EA]" data-id="element-11765" data-mp="3rxsrtn2fed8v">
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#EA580C] text-[11px] font-bold text-white" data-id="element-11766" data-mp="9q3dly9x7do4">
                      SC
                    </span>
                    <span className="min-w-0 flex-1 text-left" data-id="element-11767" data-mp="1z7793fyyuooe">
                      <span className="block truncate text-[13px] font-semibold text-[#2B211A]" data-id="element-11768" data-mp="1z5e2u1s9m6gx">
                        Sarah Chen
                      </span>
                      <span className="block truncate text-[11.5px] text-neutral-400" data-id="element-11769" data-mp="28xa2hha024n9">
                        $100 credit · 0 used
                      </span>
                    </span>
                    <ChevronDown size={14} className={`shrink-0 text-neutral-400 transition-transform duration-200 ${chatAccountOpen ? 'rotate-180' : ''}`} data-id="element-11770" data-mp="1g7tagkqakprk" />
                  </button>
                  <AnimatePresence data-id="element-11771" data-mp="1q6iscdi6ua7t">
                    {chatAccountOpen && <motion.ul initial={{
                  opacity: 0,
                  y: 6,
                  scale: 0.98
                }} animate={{
                  opacity: 1,
                  y: 0,
                  scale: 1
                }} exit={{
                  opacity: 0,
                  y: 6,
                  scale: 0.98
                }} transition={{
                  duration: 0.18,
                  ease: [0.23, 1, 0.32, 1]
                }} className="absolute bottom-[calc(100%-4px)] left-3 right-3 z-10 origin-bottom rounded-xl border border-[#EFEAE1] bg-white p-1.5 shadow-[0_24px_60px_-24px_rgba(43,33,26,0.35)]" data-id="element-11772" data-mp="1q83pmavw079d">
                        {[{
                    label: 'Usage & billing',
                    icon: CreditCard,
                    onSelect: () => {
                      setChatAccountOpen(false);
                      setChatBillingOpen(true);
                    }
                  }, {
                    label: 'Settings',
                    icon: Settings2,
                    // No dedicated settings view exists yet, so this lands on
                    // the dashboard root rather than a blank screen.
                    onSelect: () => navigate('/app-screen-mockups/dashboard')
                  }].map(item => <li key={item.label} data-id="element-11773" data-mp="277dcwd9a908d">
                            <button type="button" onClick={item.onSelect} className="flex w-full items-center gap-2.5 rounded-lg px-2 py-1.5 text-left text-[13.5px] font-medium text-neutral-600 transition-colors hover:bg-[#F6F2EA] hover:text-[#2B211A]" data-id="element-11774" data-mp="276239wt5oy9c">
                              <item.icon size={15} className="shrink-0 text-neutral-400" data-id="element-11775" data-mp="fbavuef13j48" />
                              {item.label}
                            </button>
                          </li>)}
                      </motion.ul>}
                  </AnimatePresence>
                </div>
              </motion.aside>
            </>}
        </AnimatePresence>

        {/* Chat layout: greeting and starters occupy the empty thread area,
         composer parked at the bottom where it will stay once messages start
         filling the space above it. */}
        {chatView !== 'chat' ? <div className="min-h-screen" data-id="element-11776" data-mp="3ist0ye3f2c0y">
            <WorkspaceViews view={chatView} data-id="element-11777" data-mp="3irhpds3qbzza" />
          </div> : <div className="flex h-screen flex-col" data-id="element-11778" data-mp="3dtpvisjiy3sg">
          {chatMessages.length === 0 ? <div className="flex flex-1 items-center justify-center px-6 pt-20 md:pt-16" data-id="element-11779" data-mp="3dvsvo5ee5daw">
              <div className="w-full max-w-2xl" data-id="element-11780" data-mp="1hrd18s5nttat">
                <h1 className="text-center font-title text-3xl font-medium leading-tight tracking-tight text-[#2B211A] md:text-[38px]" data-id="element-11781" data-mp="1mq7nk42u5zzo">
                  {chatCreditNoticeSeen ? 'What can I help with?' : "You're all set, Sarah."}
                </h1>
                <p className="mx-auto mt-3.5 max-w-[38ch] text-center text-[15.5px] leading-relaxed text-neutral-500" data-id="element-11782" data-mp="1cxs2gxw8svak">
                  {chatCreditNoticeSeen ? 'Ask anything across your connected tools.' : <>
                      Ask anything across your connected tools. Your{' '}
                      <span className="font-semibold text-[#2B211A]" data-id="element-11783" data-mp="p3qp2vid9gcc">
                        $100 in credits
                      </span>{' '}
                      is ready — no card needed.
                    </>}
                </p>

                {/* The same offer as the red to-do, put where a new user is
                 already looking. One route, two entry points. */}
                {interviewIndex === null && <button type="button" onClick={startOnboarding} className="mx-auto mt-7 flex w-full max-w-md items-center gap-3 rounded-2xl border border-[#F3DECB] bg-[#FFF6F2] px-4 py-3.5 text-left transition-colors hover:border-[#EA580C]" data-id="element-11784" data-mp="cry5mfadt4n8">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#EA580C]" data-id="element-11785" data-mp="hq9ascpqeqz4">
                      <Sparkles size={15} className="text-white" data-id="element-11786" data-mp="vhedxosb61e7" />
                    </span>
                    <span className="min-w-0 flex-1" data-id="element-11787" data-mp="rhcdfiyb7e0i">
                      <span className="block text-[13.5px] font-semibold text-[#2B211A]" data-id="element-11788" data-mp="rinjxqj52zvb">
                        Let me set you up first
                      </span>
                      <span className="block text-[12.5px] leading-snug text-neutral-500" data-id="element-11789" data-mp="10ik9rvh9m4ry">
                        A short conversation, then your tracks and pulses are running.
                      </span>
                    </span>
                    <ArrowRight size={15} className="shrink-0 text-[#EA580C]" data-id="element-11790" data-mp="32aypqp090z5q" />
                  </button>}

                {/* Starters as light chips rather than full-width rows — they
                 sit closer to the composer and read as prompts, not a menu. */}
                <div className="mt-7 flex flex-wrap justify-center gap-2" data-id="element-11791" data-mp="1f5dzkib04f6b">
                  {starters.map(s => <button key={s} type="button" onClick={() => sendChatMessage(s)} className="rounded-full border border-[#EFEAE1] bg-white px-4 py-2 text-[13.5px] font-medium text-neutral-600 transition-colors hover:border-[#EA580C] hover:text-[#EA580C]" data-id="element-11792" data-mp="1k61q72vbiu00">
                      {s}
                    </button>)}
                </div>
              </div>
            </div> : <div ref={chatThreadRef} className="flex-1 overflow-y-auto px-6 pt-20 md:pt-16" data-id="element-11793" data-mp="22vi54v7r2ozy">
              <div className="mx-auto flex max-w-2xl flex-col gap-7 pb-6" data-id="element-11794" data-mp="27v34v7uqv1dq">
                {chatMessages.map(m => m.role === 'user' ? <div key={m.id} className="flex justify-end" data-id="element-11795" data-mp="2ct4nxss670p7">
                      <p className="max-w-[80%] rounded-2xl rounded-br-md bg-[#2B211A] px-4 py-2.5 text-[15px] leading-relaxed text-white" data-id="element-11796" data-mp="2crla08tzl1ql">
                        {m.text}
                      </p>
                    </div> : <motion.div key={m.id} initial={{
              opacity: 0,
              y: 6
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.25,
              ease: [0.23, 1, 0.32, 1]
            }} className="flex gap-3" data-id="element-11797" data-mp="2czove243k0yl">
                      <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-[#1F3A3D]" data-id="element-11798" data-mp="30ozkgmc346ww">
                        <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="" aria-hidden="true" className="h-3.5 w-3.5 object-contain [filter:brightness(0)_invert(1)]" data-id="element-11799" data-mp="2vx8392etjdjf" />
                      </span>
                      <div className="min-w-0 flex-1" data-id="element-11800" data-mp="3jmkd84hyzpms">
                        {/* What it did, before what it found. */}
                        {m.steps && <ul className="mb-3 space-y-1" data-id="element-11801" data-mp="bxhv2yd3fbop">
                            {m.steps.map(s => <li key={s.label} className="flex items-center gap-2 text-[12.5px] text-neutral-400" data-id="element-11802" data-mp="byt1l5xxaxji">
                                <img src={`https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/${s.icon}.svg`} alt="" aria-hidden="true" className="h-3.5 w-3.5 object-contain" data-id="element-11803" data-mp="3tc552upgkxf3" />
                                {s.label}
                                {m.thinking ? <Loader2 size={11} className="animate-spin text-neutral-300" data-id="element-11804" data-mp="uwm3c4wt91fb" /> : <Check size={11} strokeWidth={3} className="text-neutral-300" data-id="element-11805" data-mp="fz3gx97lv0b1" />}
                              </li>)}
                          </ul>}
                        {m.thinking && <p className="text-[14.5px] text-neutral-400" data-id="element-11806" data-mp="kwbqwmilny17">
                            Working…
                          </p>}
                        {m.answer && <div className="space-y-3" data-id="element-11807" data-mp="35onjjgwsevkk">
                            {m.answer.map((para, i) => <p key={i} className="text-[15px] leading-relaxed text-[#2B211A]" dangerouslySetInnerHTML={{
                    __html: para.replace(/\*\*(.+?)\*\*/g, '<strong class="font-semibold">$1</strong>')
                  }} data-id="element-11808" data-mp="1nqikhntsuf0l" data-mpi={i} />)}
                          </div>}
                        {/* A Giga idea introduced at the moment it becomes
                         relevant, rather than in a tour up front. */}
                        {m.concept && <div className="mt-3.5 rounded-xl border border-[#F3DECB] bg-[#FFF9F5] px-4 py-3.5" data-id="element-11809" data-mp="1512i0uyk0chj">
                            <p className="text-[11px] font-bold uppercase tracking-[0.09em] text-[#EA580C]" data-id="element-11810" data-mp="1030yk8x7ub5j">
                              {m.concept.label}
                            </p>
                            <p className="mt-1.5 text-[14px] font-semibold text-[#2B211A]" data-id="element-11811" data-mp="190b9tua1a0ag">
                              {m.concept.title}
                            </p>
                            {m.concept.lines.map((line, i) => <p key={i} className="mt-1.5 text-[13px] leading-relaxed text-neutral-600" data-id="element-11812" data-mp="ha6re15u3nbv" data-mpi={i}>
                                {line}
                              </p>)}
                          </div>}

                        {/* What the interview produced, as artefacts rather
                         than a paragraph claiming it happened. */}
                        {m.created && <ul className="mt-4 grid gap-2 sm:grid-cols-2" data-id="element-11813" data-mp="24744bat94ay4">
                            {m.created.map(c => <li key={c.title} className="rounded-xl border border-[#EFEAE1] bg-white px-3.5 py-3" data-id="element-11814" data-mp="1gh01v6rsuh8p">
                                <p className="text-[13px] font-semibold text-[#2B211A]" data-id="element-11815" data-mp="1bk05xoonyuvb">
                                  {c.title}
                                </p>
                                <p className="mt-0.5 text-[12px] leading-relaxed text-neutral-500" data-id="element-11816" data-mp="1lgczmmk7l8q2">
                                  {c.detail}
                                </p>
                              </li>)}
                          </ul>}

                        {/* Suggested answers. They retire once answered so the
                         thread reads as a transcript, not a live form. */}
                        {m.choices && m.choices.length > 0 && !m.answered && <div className="mt-4" data-id="element-11817" data-mp="39fym7qmdij93">
                            <div className="flex flex-wrap gap-2" data-id="element-11818" data-mp="2uhwingfcdxru">
                              {m.choices.map(choice => {
                      const picked = interviewPicks.includes(choice);
                      return <button key={choice} type="button" onClick={() => m.multi ? setInterviewPicks(prev => picked ? prev.filter(p => p !== choice) : [...prev, choice]) : answerInterview(choice)} aria-pressed={m.multi ? picked : undefined} className={`rounded-full border px-3.5 py-1.5 text-[13px] font-medium transition-colors ${picked ? 'border-[#2B211A] bg-[#2B211A] text-white' : 'border-[#EFEAE1] bg-white text-neutral-600 hover:border-[#EA580C] hover:text-[#EA580C]'}`} data-id="element-11819" data-mp="3n6bp3eker7rk">
                                    {m.multi && picked && <Check size={11} strokeWidth={3.5} className="mr-1.5 inline" data-id="element-11820" data-mp="eqiax6dju8do" />}
                                    {choice}
                                  </button>;
                    })}
                            </div>
                            {m.multi && <button type="button" disabled={interviewPicks.length === 0} onClick={() => answerInterview(interviewPicks.join(', '))} className="mt-3 inline-flex items-center gap-1.5 rounded-xl bg-[#2B211A] px-3.5 py-2 text-[12.5px] font-bold text-white transition-colors hover:bg-black disabled:bg-[#E7E0D4] disabled:text-neutral-400" data-id="element-11821" data-mp="jl1h9v5tby3a">
                                {interviewPicks.length === 0 ? 'Pick what applies' : `Continue with ${interviewPicks.length}`}
                                <ArrowRight size={13} data-id="element-11822" data-mp="3s3h466batj0z" />
                              </button>}
                          </div>}
                        {m.sources && <div className="mt-3.5 flex flex-wrap items-center gap-2 border-t border-[#EFEAE1] pt-3" data-id="element-11823" data-mp="3j29jir1pggpd">
                            <span className="text-[11.5px] font-medium uppercase tracking-wider text-neutral-400" data-id="element-11824" data-mp="3e2mw5os5xk2u">
                              Sources
                            </span>
                            {m.sources.map(s => <span key={s.label} className="inline-flex items-center gap-1.5 rounded-full border border-[#EFEAE1] bg-white px-2.5 py-1 text-[12px] font-medium text-neutral-600" data-id="element-11825" data-mp="1yjfjzm67kabr">
                                <img src={`https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/${s.icon}.svg`} alt="" aria-hidden="true" className="h-3 w-3 object-contain" data-id="element-11826" data-mp="27leyo7tr32bj" />
                                {s.label}
                              </span>)}
                          </div>}
                      </div>
                    </motion.div>)}
              </div>
            </div>}

          <div className="sticky bottom-0 bg-gradient-to-t from-[#FAF8F4] via-[#FAF8F4] to-transparent px-6 pb-6 pt-4" data-id="element-11827" data-mp="1olsphrcfea6o">
            <div className="mx-auto max-w-2xl" data-id="element-11828" data-mp="s4y56uz0eci5">
              <div className="rounded-2xl border border-[#EFEAE1] bg-white p-4 shadow-[0_18px_50px_-28px_rgba(43,33,26,0.35)]" data-id="element-11829" data-mp="s226v5j91lt6">
                <label htmlFor="giga-first-question" className="sr-only" data-id="element-11830" data-mp="d0vbpr1shd50">
                  Ask Giga a question
                </label>
                <textarea id="giga-first-question" rows={1} value={chatDraft} onChange={e => setChatDraft(e.target.value)} onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  sendChatMessage(chatDraft);
                }
              }} placeholder={typeof interviewIndex === 'number' ? ONBOARDING_INTERVIEW[interviewIndex].placeholder : 'Ask about anything in your connected tools…'} className="min-h-[28px] w-full resize-none bg-transparent text-[16px] leading-relaxed text-[#2B211A] outline-none placeholder:text-neutral-400" data-id="element-11831" data-mp="833csd2q8qrb" />
                <div className="mt-2.5 flex items-center justify-between gap-4" data-id="element-11832" data-mp="10r8uss2ec1ng">
                  {/* Scoping the chat, not advertising the tool count. Giga
                   already uses everything the user can see; this is for
                   narrowing it to one group, track or database. */}
                  <span className="relative flex flex-wrap items-center gap-1.5" data-id="element-11833" data-mp="1i0ju7imtfsu2">
                    {/* One paperclip for both photos and files — the OS picker
                     already handles the distinction. */}
                    <button type="button" aria-label="Attach a photo or file" title="Attach a photo or file" className="flex h-7 w-7 items-center justify-center rounded-full text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-[#2B211A]" data-id="element-11834" data-mp="18849weo1qs60">
                      <Paperclip size={15} data-id="element-11835" data-mp="13bdttrc8rwkl" />
                    </button>
                    {chatContext.map(c => <span key={c} className="inline-flex items-center gap-1 rounded-full bg-[#F4F0E9] py-1 pl-2.5 pr-1.5 text-[12.5px] font-medium text-[#2B211A]" data-id="element-11836" data-mp="pc5vf0nwkshg">
                        {c}
                        <button type="button" onClick={() => setChatContext(prev => prev.filter(v => v !== c))} aria-label={`Remove ${c}`} className="text-neutral-400 transition-colors hover:text-[#2B211A]" data-id="element-11837" data-mp="2jp41t96hf4zi">
                          <X size={12} data-id="element-11838" data-mp="2n8clumqcp82q" />
                        </button>
                      </span>)}
                    <button type="button" onClick={() => setContextOpen(o => !o)} aria-expanded={contextOpen} className="inline-flex items-center gap-1.5 rounded-full px-2 py-1 text-[12.5px] font-medium text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-[#2B211A]" data-id="element-11839" data-mp="2i76rkkh1lcr3">
                      <Plus size={13} data-id="element-11840" data-mp="35yc5uv7c3wza" />
                      Add context
                    </button>

                    <AnimatePresence data-id="element-11841" data-mp="30yg1uvyn05jq">
                      {contextOpen && <>
                          <div className="fixed inset-0 z-30" onClick={() => setContextOpen(false)} data-id="element-11842" data-mp="2w7pyfo9rucro" />
                          <motion.div initial={{
                        opacity: 0,
                        y: 6,
                        scale: 0.98
                      }} animate={{
                        opacity: 1,
                        y: 0,
                        scale: 1
                      }} exit={{
                        opacity: 0,
                        y: 6,
                        scale: 0.98
                      }} transition={{
                        duration: 0.18,
                        ease: [0.23, 1, 0.32, 1]
                      }} className="absolute bottom-[calc(100%+8px)] left-0 z-40 w-[380px] max-w-[calc(100vw-32px)] origin-bottom-left overflow-hidden rounded-2xl border border-[#EFEAE1] bg-white shadow-[0_24px_60px_-24px_rgba(43,33,26,0.35)]" data-id="element-11843" data-mp="2r96cvoz3b2zu">
                            {/* Tabs only earn their place once there is more
                             than one kind of context to switch between. With an
                             empty workspace they are five empty rooms. */}
                            {!chatNoConnections && <div className="flex gap-1 overflow-x-auto border-b border-[#F4F0E9] px-2 py-2" data-id="element-11844" data-mp="sqb7srcdv10f">
                                {(['All', 'Groups', 'Tracks', 'Credentials', 'Databases'] as const).map(t => <button key={t} type="button" onClick={() => setContextTab(t)} className={`shrink-0 rounded-full px-2.5 py-1 text-[12.5px] font-semibold transition-colors ${contextTab === t ? 'bg-[#2B211A] text-white' : 'text-neutral-500 hover:bg-neutral-100'}`} data-id="element-11845" data-mp="12psb5245x2t8">
                                      {t}
                                    </button>)}
                              </div>}
                            {!chatNoConnections && <div className="border-b border-[#F4F0E9] p-2" data-id="element-11846" data-mp="1rzzow6hinx63">
                                <input type="search" value={contextQuery} onChange={e => setContextQuery(e.target.value)} placeholder="Search context…" aria-label="Search context" className="w-full rounded-lg border border-[#EFEAE1] px-2.5 py-1.5 text-[13px] outline-none transition-colors placeholder:text-neutral-400 focus:border-neutral-400" data-id="element-11847" data-mp="14chsfxnb4aws" />
                              </div>}
                            {/* Fixed height so switching tabs never resizes the
                             panel under the pointer. */}
                            <div className={`overflow-y-auto p-2 ${chatNoConnections ? '' : 'h-[280px]'}`} data-id="element-11848" data-mp="gkt6cz12wioa">
                              {chatNoConnections ? (
                          /* Nothing set up yet. Each tab explains its own
                          primitive and offers the one action that creates
                          it, so the empty state is never a dead end. */
                          <div className="px-2 py-4" data-id="element-11849" data-mp="2o9gt1hfheuqj">
                                  <p className="text-[13.5px] font-semibold text-[#2B211A]" data-id="element-11850" data-mp="3by00juqpj3hk">
                                    Nothing to draw on yet
                                  </p>
                                  <p className="mt-1.5 text-[12.5px] leading-relaxed text-neutral-500" data-id="element-11851" data-mp="3fx8vh2xhh7ep">
                                    Context is what Giga is allowed to look at
                                    when it answers — the accounts it can sign
                                    into, the tracks and groups your work lives
                                    in, the tables it can query. Connect a tool
                                    and it starts filling in here.
                                  </p>
                                  <button type="button" onClick={() => {
                              setContextOpen(false);
                              setStep(6);
                            }} className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-[#2B211A] px-4 py-2.5 text-[13.5px] font-bold text-white transition-colors hover:bg-black" data-id="element-11852" data-mp="28z5l6e3xme0e">
                                    Connect your tools
                                    <ArrowRight size={14} data-id="element-11853" data-mp="1q8e8b2kqk5h9" />
                                  </button>
                                  <p className="mt-2 text-center text-[11.5px] text-neutral-400" data-id="element-11854" data-mp="1v78yipl18wbe">
                                    Takes about a minute
                                  </p>
                                </div>) : (() => {
                            const sections = [{
                              label: 'Groups',
                              items: GROUP_NAMES
                            }, {
                              label: 'Tracks',
                              items: TRACKS.filter(t => t.status === 'active').slice(0, 8).map(t => t.name)
                            }, {
                              label: 'Credentials',
                              items: ['Gmail — sarah@brightwave.io', 'Google Drive', 'Google Calendar']
                            }, {
                              label: 'Databases',
                              items: ['Pipeline (Notion)', 'Support tickets (Airtable)']
                            }].filter(s => contextTab === 'All' || s.label === contextTab).map(s => ({
                              ...s,
                              items: s.items.filter(i => i.toLowerCase().includes(contextQuery.trim().toLowerCase()))
                            })).filter(s => s.items.length > 0);
                            if (sections.length === 0) {
                              return <p className="px-2 py-6 text-center text-[13px] text-neutral-400" data-id="element-11855" data-mp="2e40rk34m84d8">
                                      Nothing matches “{contextQuery}”.
                                    </p>;
                            }
                            return sections.map(s => <div key={s.label} className="mb-2 last:mb-0" data-id="element-11856" data-mp="29dwzsudrpged">
                                      <p className="px-2 pb-1 text-[10.5px] font-bold uppercase tracking-wider text-neutral-400" data-id="element-11857" data-mp="1vhtbfhpks29t">
                                        {s.label}
                                      </p>
                                      {s.items.map(item => {
                                const on = chatContext.includes(item);
                                return <button key={item} type="button" onClick={() => setChatContext(prev => on ? prev.filter(v => v !== item) : [...prev, item])} className="flex w-full items-center justify-between gap-2 rounded-lg px-2 py-1.5 text-left text-[13.5px] font-medium text-[#2B211A] transition-colors hover:bg-[#FFF7F0]" data-id="element-11858" data-mp="2efmc0dcq8041">
                                            <span className="truncate" data-id="element-11859" data-mp="3ayjwgml0f7b0">{item}</span>
                                            {on && <Check size={14} strokeWidth={3} className="shrink-0 text-[#EA580C]" data-id="element-11860" data-mp="zgp3u53varyf" />}
                                          </button>;
                              })}
                                    </div>);
                          })()}
                            </div>
                          </motion.div>
                        </>}
                    </AnimatePresence>
                  </span>
                  <button type="button" onClick={() => sendChatMessage(chatDraft)} disabled={!chatDraft.trim()} aria-label="Send" className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl transition-colors ${chatDraft.trim() ? 'bg-[#2B211A] text-white hover:bg-black' : 'bg-[#F0EBE2] text-neutral-400'}`} data-id="element-11861" data-mp="gje6wr5pkam5">
                    <Send size={15} data-id="element-11862" data-mp="lkld4ls7d1qp" />
                  </button>
                </div>
              </div>

              {/* Slack is optional, so it is one quiet line under the composer. */}
              <p className="mt-3 flex flex-wrap items-center justify-center gap-1.5 text-center text-[13px] text-neutral-400" data-id="element-11863" data-mp="1wzi4417y610p">
                <SlackIcon size={14} data-id="element-11864" data-mp="bf3i8kravu68" />
                Your team already lives in Slack?
                <button type="button" onClick={() => {
                setAuthProvider('slack');
                setStep(2);
              }} className="font-semibold text-neutral-600 underline decoration-neutral-300 underline-offset-4 transition-colors hover:text-[#EA580C] hover:decoration-[#EA580C]" data-id="element-11865" data-mp="fgyl3tovqfm4">
                  Add Slack
                </button>
              </p>
            </div>
          </div>
        </div>}

        {/* Same Usage & billing surface as the dashboard, opened on the
         starter-credit model this account is actually on. */}
        <BillingModal open={chatBillingOpen} onClose={() => setChatBillingOpen(false)} initialVariant="B" data-id="element-11866" data-mp="1361d9vh53quv" />
      </motion.div>;
  }
  // Step 20: Google consent. Modelled on Google's own account-chooser +
  // scope screen. The Workspace scopes are requested here, in the same
  // consent, so the user arrives with Gmail, Drive and Calendar already
  // readable instead of facing an empty brain.
  if (step === 20) {
    return <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      duration: 0.15,
      ease: 'easeOut'
    }} className="min-h-screen bg-white sm:bg-[#FAF8F4] font-body flex flex-col sm:items-center sm:justify-center sm:p-6" data-id="element-11867" data-mp="1ibnhkah8y92e">
        <div className="w-full sm:max-w-lg bg-white sm:rounded-3xl sm:shadow-xl sm:border border-neutral-200 flex flex-col min-h-screen sm:min-h-0" data-id="element-11868" data-mp="25xu4s3z8rvd7">
          <div className="flex-1 overflow-auto p-6 sm:p-10" data-id="element-11869" data-mp="1w93s9y6ksozy">
            <div className="mb-6 flex items-center justify-center" data-id="element-11870" data-mp="1w7iyias35a35">
              <GoogleIcon size={28} data-id="element-11871" data-mp="1s6palhen7va9" />
            </div>
            <h1 className="mb-1 text-center font-title text-2xl font-bold text-[#333333]" data-id="element-11872" data-mp="1s5xlmpfjwvsy">
              Choose an account
            </h1>
            <p className="mb-7 text-center text-[14px] text-neutral-500" data-id="element-11873" data-mp="2jxve87tkob4a">
              to continue to <span className="font-semibold" data-id="element-11874" data-mp="i14d0fdmotee">Giga</span>
            </p>

            <ul className="mb-6 divide-y divide-neutral-100 overflow-hidden rounded-xl border border-neutral-200" data-id="element-11875" data-mp="n0h9lruaxeu6">
              {[{
              name: 'Sarah Chen',
              email: 'sarah.chen@brightwave.io',
              initial: 'S',
              tone: 'bg-[#EA580C]'
            }, {
              name: 'Sarah Chen',
              email: 'sarah@gmail.com',
              initial: 'S',
              tone: 'bg-[#3D2F25]'
            }].map(acct => <li key={acct.email} data-id="element-11876" data-mp="4ar70ikarorp">
                  <button type="button" onClick={() => setStep(21)} className="group flex w-full items-center gap-3 px-4 py-3.5 text-left transition-colors hover:bg-[#FFF7F0]" data-id="element-11877" data-mp="47mv0z0lboqh">
                    <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white ${acct.tone}`} data-id="element-11878" data-mp="3qhg0aks901hm">
                      {acct.initial}
                    </span>
                    <span className="min-w-0 flex-1" data-id="element-11879" data-mp="1onqfrp7zvvbo">
                      <span className="block truncate text-[15px] font-semibold text-[#333333]" data-id="element-11880" data-mp="3973mbfrlt5oj">
                        {acct.name}
                      </span>
                      <span className="block truncate text-[13px] text-neutral-500" data-id="element-11881" data-mp="3ixojqzvjk45v">
                        {acct.email}
                      </span>
                    </span>
                    <ChevronRight size={16} className="shrink-0 text-neutral-300 transition-colors group-hover:text-[#EA580C]" data-id="element-11882" data-mp="3e4vfse7wczuu" />
                  </button>
                </li>)}
              <li data-id="element-11883" data-mp="3nvzv5f1himq7">
                <button type="button" onClick={() => setStep(21)} className="flex w-full items-center gap-3 px-4 py-3.5 text-left transition-colors hover:bg-neutral-50" data-id="element-11884" data-mp="3nwi1j4hv2hbu">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-neutral-200 text-neutral-400" data-id="element-11885" data-mp="t3e0knjamhl">
                    <Plus size={16} data-id="element-11886" data-mp="27kcnba242ntq" />
                  </span>
                  <span className="text-[15px] font-medium text-neutral-600" data-id="element-11887" data-mp="22kq8wxajr1jc">
                    Use another account
                  </span>
                </button>
              </li>
            </ul>

            <p className="text-center text-[12.5px] leading-relaxed text-neutral-400" data-id="element-11888" data-mp="1lnrjxlmrp2nl">
              To continue, Google will share your name, email address and
              profile picture with Giga.
            </p>

            <button type="button" onClick={() => setStep(1)} className="mx-auto mt-6 block text-[13px] font-semibold text-neutral-400 transition-colors hover:text-[#2B211A]" data-id="element-11889" data-mp="1vh8dqpmwmhr9">
              Back
            </button>
          </div>
        </div>
      </motion.div>;
  }
  // Step 21: Google Workspace scope grant. Kept separate from the account
  // chooser so the permissions being granted are a deliberate read, not
  // something buried under an account row.
  if (step === 21) {
    return <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      duration: 0.15,
      ease: 'easeOut'
    }} className="min-h-screen bg-white sm:bg-[#FAF8F4] font-body flex flex-col sm:items-center sm:justify-center sm:p-6" data-id="element-11890" data-mp="2j483t90qm9ex">
        <div className="w-full sm:max-w-lg bg-white sm:rounded-3xl sm:shadow-xl sm:border border-neutral-200 flex flex-col min-h-screen sm:min-h-0" data-id="element-11891" data-mp="7h4n42ru5opr">
          <div className="flex-1 overflow-auto p-6 sm:p-10" data-id="element-11892" data-mp="2ol7z1di2liu">
            <div className="mb-5 flex items-center justify-center gap-4" data-id="element-11893" data-mp="3tw65ji9psmep">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-neutral-200 bg-white shadow-sm" data-id="element-11894" data-mp="3oye4nyr3dpyd">
                <GoogleIcon size={28} data-id="element-11895" data-mp="3kuptz775ye8w" />
              </div>
              <div className="flex gap-1" aria-hidden="true" data-id="element-11896" data-mp="3fwoboohkaiyh">
                <span className="h-1.5 w-1.5 rounded-full bg-neutral-300" data-id="element-11897" data-mp="3fzak0qca12hr" />
                <span className="h-1.5 w-1.5 rounded-full bg-neutral-300" data-id="element-11898" data-mp="3azo5mdkppg7d" />
                <span className="h-1.5 w-1.5 rounded-full bg-neutral-300" data-id="element-11899" data-mp="1e631z95a46s6" />
              </div>
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#EA580C] shadow-sm" data-id="element-11900" data-mp="196gtfd0ebepp">
                <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="" aria-hidden="true" className="h-7 w-7 object-contain [filter:brightness(0)_invert(1)]" data-id="element-11901" data-mp="s9wgas71q3ma" />
              </div>
            </div>

            <h1 className="mb-2 text-center font-title text-2xl font-bold text-[#333333]" data-id="element-11902" data-mp="169e7tfbq7uzc">
              Giga wants access to your Google Account
            </h1>
            <p className="mb-7 text-center text-[14px] text-neutral-500" data-id="element-11903" data-mp="dls3u5yl6oby">
              sarah.chen@brightwave.io
            </p>

            <p className="mb-3 text-[13px] font-bold uppercase tracking-wider text-neutral-400" data-id="element-11904" data-mp="ik3e2hssn0up">
              This will allow Giga to
            </p>
            <ul className="mb-6 space-y-2.5" data-id="element-11905" data-mp="ndo6zvfj54n1">
              {[{
              label: 'Read your Gmail messages and threads',
              detail: 'So it can answer from your inbox and draft replies.'
            }, {
              label: 'See and open files in Google Drive',
              detail: 'So documents and spreadsheets become part of your brain.'
            }, {
              label: 'View and manage your Google Calendar',
              detail: 'So it can prep for meetings and schedule work.'
            }].map(scope => <li key={scope.label} className="flex gap-3 rounded-xl border border-neutral-200 p-3.5" data-id="element-11906" data-mp="34ko4j4dg155y">
                  <Check size={16} strokeWidth={3} className="mt-0.5 shrink-0 text-[#EA580C]" data-id="element-11907" data-mp="30j2ya1vezp4q" />
                  <span data-id="element-11908" data-mp="2vrb1vapkup6o">
                    <span className="block text-[14.5px] font-semibold text-[#333333]" data-id="element-11909" data-mp="2vr2pdjxd9lw0">
                      {scope.label}
                    </span>
                    <span className="mt-0.5 block text-[13px] leading-relaxed text-neutral-500" data-id="element-11910" data-mp="2ltomyo8a2xmp">
                      {scope.detail}
                    </span>
                  </span>
                </li>)}
            </ul>

            <div className="flex flex-col gap-3 sm:flex-row-reverse" data-id="element-11911" data-mp="2csgq8aleu4uq">
              <button type="button" onClick={() => {
              // Consent granted here, so these arrive already connected.
              setConnectedConns(prev => Array.from(new Set([...prev, ...GOOGLE_WORKSPACE_TOOLS])));
              setStep(5);
            }} className="w-full rounded-xl bg-[#2B211A] px-6 py-3.5 font-bold text-white transition-colors hover:bg-black sm:w-auto" data-id="element-11912" data-mp="2cvcpc28zuzkr">
                Allow
              </button>
              <button type="button" onClick={() => setStep(20)} className="w-full rounded-xl border border-neutral-300 px-6 py-3.5 font-bold text-[#2B211A] transition-colors hover:bg-neutral-50 sm:w-auto" data-id="element-11913" data-mp="2lvm18jo0945f">
                Cancel
              </button>
            </div>

            <p className="mt-5 text-[12.5px] leading-relaxed text-neutral-400" data-id="element-11914" data-mp="2h55pd3zu1ql6">
              You can revoke access at any time from your Google Account, or
              disconnect any tool from Giga's settings.
            </p>
          </div>
        </div>
      </motion.div>;
  }
  // Step 2: Slack Consent (Workspace Selection)
  if (step === 2) {
    return <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.15,
      ease: 'easeOut'
    }} className="min-h-screen bg-white sm:bg-[#FAF8F4] font-body flex flex-col sm:items-center sm:justify-center sm:p-6" data-id="element-11915" data-mp="3iyxiuv245582">
        
        {/* Waitlist popup — opens immediately over the Slack connect screen
                                                                                  while the feature is invite-only. */}
        <AnimatePresence data-id="element-11916" data-mp="2w1dl9dyudv0v">
          {showWaitlist && <WaitlistModal referredBy={new URLSearchParams(location.search).get('ref')?.trim().toLowerCase()} onClose={() => {
          setShowWaitlist(false);
          navigate('/');
        }} data-id="element-11917" data-mp="39zajsfwuwbi6" />}
        </AnimatePresence>

        <div className="w-full sm:max-w-lg bg-white sm:rounded-3xl sm:shadow-xl sm:border border-neutral-200 flex flex-col min-h-screen sm:min-h-0" data-id="element-11918" data-mp="p8jt3w7bbz6p">
          <div className="flex-1 p-6 sm:p-10 overflow-auto" data-id="element-11919" data-mp="kccoc6qnktqc">
            <h1 className="text-2xl font-title font-bold text-[#333333] text-center mb-6" data-id="element-11920" data-mp="2pzu2qpc9zure">
              Sign in to Giga with Slack
            </h1>

            {/* Slack ⇄ Giga lockup */}
            <div className="flex items-center justify-center gap-4 mb-8" data-id="element-11921" data-mp="2zvnbdxmvj43w">
              <div className="w-14 h-14 rounded-2xl bg-[#2B211A] flex items-center justify-center shadow-sm" data-id="element-11922" data-mp="33y1rwb32lrs0">
                <SlackIcon size={28} data-id="element-11923" data-mp="38y6dgfiu5cp3" />
              </div>
              <ArrowLeftRight size={22} className="text-neutral-400" data-id="element-11924" data-mp="38vaceiboy7wf" />
              <div className="w-14 h-14 rounded-2xl bg-[#EA580C] flex items-center justify-center shadow-sm" data-id="element-11925" data-mp="3duwryyezs08e">
                <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="Giga" className="w-7 h-7 object-contain [filter:brightness(0)_invert(1)]" data-id="element-11926" data-mp="3inznhc3bxjr8" />
                
              </div>
            </div>

            {/* Account / workspace selector */}
            <p className="text-sm font-bold text-[#2B211A] mb-2" data-id="element-11927" data-mp="1l05cucox6csb">
              Select a Slack account or workspace
            </p>
            <button onClick={() => setStep(3)} className="w-full flex items-center justify-between gap-3 p-4 rounded-xl border border-neutral-200 hover:border-[#EA580C] hover:bg-[#FFEDD5]/30 transition-all group mb-6 text-left" data-id="element-11928" data-mp="1fyzfu2o883cz">
              
              <div className="flex items-center gap-3 min-w-0" data-id="element-11929" data-mp="23q3qd8bhky3c">
                <div className="w-11 h-11 rounded-lg bg-[#611f69] flex items-center justify-center text-white font-bold shrink-0" data-id="element-11930" data-mp="23pbvy0tml6en">
                  F
                </div>
                <div className="min-w-0" data-id="element-11931" data-mp="1uwrvcutrwrr6">
                  <div className="font-bold text-[#2B211A] text-[15px] truncate" data-id="element-11932" data-mp="1uvgqerolddyh">
                    Faith Alqahtani
                  </div>
                  <div className="text-xs text-neutral-500 truncate" data-id="element-11933" data-mp="2necijid1542u">
                    alqahtani.faith@gmail.com · trackrheadquarters.slack.com
                  </div>
                </div>
              </div>
              <ChevronDown size={18} className="text-neutral-400 group-hover:text-[#EA580C] shrink-0" data-id="element-11934" data-mp="31knzbf4j2wjw" />
              
            </button>

            {/* Account-details usage */}
            <div className="rounded-2xl bg-neutral-50 border border-neutral-100 p-5 mb-6" data-id="element-11935" data-mp="2srudszyhn0xl">
              <p className="text-sm font-bold text-[#2B211A] mb-4" data-id="element-11936" data-mp="2wsec46ussalb">
                Your account details will be used to:
              </p>
              <ul className="space-y-4" data-id="element-11937" data-mp="2irk2xlzxe5ll">
                <li className="flex items-start gap-3 text-sm text-neutral-600" data-id="element-11938" data-mp="2ns8430hvc2tj">
                  <Plug size={18} className="text-[#2B211A] shrink-0 mt-0.5" data-id="element-11939" data-mp="2nq55vt6kb3dq" />
                  <span data-id="element-11940" data-mp="29u1gcd6mvj7l">
                    Establish a verified connection between an existing Giga
                    account and Slack.
                  </span>
                </li>
                <li className="flex items-start gap-3 text-sm text-neutral-600" data-id="element-11941" data-mp="82etmntryagx">
                  <UserPlus size={18} className="text-[#2B211A] shrink-0 mt-0.5" data-id="element-11942" data-mp="c2yo1jmyqzzd" />
                  
                  <span data-id="element-11943" data-mp="1wlrf564hgsot">
                    Help you create a new Giga account, if you don't already
                    have one.
                  </span>
                </li>
                <li className="flex items-start gap-3 text-sm text-neutral-600" data-id="element-11944" data-mp="26b2kcho91d7f">
                  <Sparkles size={18} className="text-[#2B211A] shrink-0 mt-0.5" data-id="element-11945" data-mp="21jsw9licmhxn" />
                  
                  <span data-id="element-11946" data-mp="2g8p179p3zeu1">
                    Unlock Giga features in Slack, like configurable
                    notifications, rich link previews, and quick actions.
                  </span>
                </li>
              </ul>
            </div>

            {/* Privacy disclosure */}
            <p className="text-xs text-neutral-500 leading-relaxed mb-0 sm:mb-6" data-id="element-11947" data-mp="2p710e041jky3">
              Slack will share your name, email address, profile image, user ID,
              and team ID with Giga. View Slack's{' '}
              <a href="#" className="text-[#EA580C] hover:underline" data-id="element-11948" data-mp="pwj57xr51d9u">
                privacy policy
              </a>
              . By agreeing to allow Slack to share your identity, you are also
              agreeing to Giga's{' '}
              <a href="#" className="text-[#EA580C] hover:underline" data-id="element-11949" data-mp="90vrt831pqfr">
                privacy agreement
              </a>{' '}
              and{' '}
              <a href="#" className="text-[#EA580C] hover:underline" data-id="element-11950" data-mp="is0583d2p38h">
                terms of service
              </a>
              .
            </p>
          </div>

          <div className="p-6 border-t border-neutral-100 sm:border-none sm:p-0 sm:pb-10 sm:px-10 flex flex-col-reverse sm:flex-row items-center sm:justify-center gap-3 bg-white sticky bottom-0 z-10" data-id="element-11951" data-mp="3i8y5edanbo8i">
            <button onClick={() => setStep(1)} className="w-full sm:w-auto px-6 py-3.5 sm:py-3 rounded-xl border border-neutral-300 text-[#2B211A] font-bold hover:bg-neutral-50 transition-colors" data-id="element-11952" data-mp="3r6i8lrs2julr">
              
              Cancel
            </button>
            <button onClick={() => {
            trackSignupSlackConnected();
            setStep(3);
          }} className="w-full sm:w-auto px-6 py-3.5 sm:py-3 rounded-xl bg-[#EA580C] hover:bg-[#C2410C] text-white font-bold transition-colors shadow-sm" data-id="element-11953" data-mp="1bfw155utir47">

              Accept and Continue
            </button>
          </div>
        </div>
      </motion.div>;
  }
  // Step 3: Slack Permissions
  if (step === 3) {
    const viewItems = [{
      icon: <MessageSquare size={18} className="text-neutral-500" data-id="element-11954" data-mp="2h9jw078pkf2j" />,
      label: 'Content and info about channels & conversations'
    }, {
      icon: <LayoutGrid size={18} className="text-neutral-500" data-id="element-11955" data-mp="288lz08wot7tg" />,
      label: 'Content and info about your workspace'
    }];
    const actionItems = [{
      icon: <MessageSquare size={18} className="text-neutral-500" data-id="element-11956" data-mp="23aanzuunor9n" />,
      label: 'Perform actions in channels & conversations'
    }];
    return <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="min-h-screen bg-white font-body flex flex-col" data-id="element-11957" data-mp="1ifgzsjchaxr">
        
        {/* Thin top bar */}
        <div className="h-2 w-full bg-[#2B211A]" data-id="element-11958" data-mp="o98fgjatfi98" />

        {/* Slack header */}
        <header className="w-full border-b border-neutral-200 px-6 sm:px-10 py-5" data-id="element-11959" data-mp="jb6wdyde3ixr">
          <div className="flex items-center gap-2" data-id="element-11960" data-mp="j92svhjekly0">
            <SlackIcon size={28} data-id="element-11961" data-mp="xyi7y09kn2xv" />
            <span className="text-[22px] font-bold text-[#333333] tracking-tight" data-id="element-11962" data-mp="y1w8d2yod22o">
              slack
            </span>
          </div>
        </header>

        {/* Body */}
        <div className="flex-1 w-full max-w-5xl mx-auto px-6 sm:px-10 py-8 sm:py-16 pb-32 sm:pb-16" data-id="element-11963" data-mp="5bo0q51rm02c">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16" data-id="element-11964" data-mp="dm7wsejemdo">
            {/* Left column */}
            <div data-id="element-11965" data-mp="fan1z2k7atfb">
              <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-[#EA580C] flex items-center justify-center shadow-sm mb-5" data-id="element-11966" data-mp="f6z8g3era70l">
                <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="Giga" className="w-7 h-7 sm:w-8 sm:h-8 object-contain [filter:brightness(0)_invert(1)]" data-id="element-11967" data-mp="1qnphduakdcxw" />
                
              </div>

              <h1 className="text-2xl sm:text-[32px] leading-tight font-title font-bold text-[#333333] mb-4" data-id="element-11968" data-mp="1y89u5m8lshdj">
                Allow the "Giga" app to access Slack
              </h1>

              <div className="flex items-center gap-2 text-[#2B211A] font-bold text-sm sm:text-[15px] mb-2" data-id="element-11969" data-mp="1o9k8rjhi0esj">
                <CheckCircle size={18} className="text-[#2B211A]" data-id="element-11970" data-mp="2bywkaq0asyy0" />
                App is approved by Slack
              </div>
              <p className="text-sm sm:text-[15px] text-neutral-500 leading-relaxed max-w-md mb-8" data-id="element-11971" data-mp="2gx6fv1pxq1k5">
                Apps are reviewed for quality before they are listed in the
                Slack Marketplace.{' '}
                <a href="#" className="text-[#1264A3] hover:underline" data-id="element-11972" data-mp="276vb5cyffoc7">
                  Learn more about app reviews.
                </a>
              </p>

              <p className="text-sm sm:text-[15px] font-bold text-[#333333] mb-2" data-id="element-11973" data-mp="2zp8wid2kthue">
                Workspace
              </p>
              <button type="button" className="w-full max-w-md flex items-center justify-between gap-3 px-4 py-3.5 rounded-lg border border-neutral-300 bg-white hover:border-neutral-400 transition-colors text-left" data-id="element-11974" data-mp="39kfv1qut1uft">
                
                <div className="flex items-center gap-3 min-w-0" data-id="element-11975" data-mp="30m48bzx27gsn">
                  <div className="w-7 h-7 rounded-md bg-[#4A90D9] flex items-center justify-center text-white text-[12px] font-bold shrink-0" data-id="element-11976" data-mp="2vkok5yzl4u61">
                    T
                  </div>
                  <span className="font-bold text-[#333333] text-[16px] truncate" data-id="element-11977" data-mp="2luljrdq4wdxo">
                    Tonic
                  </span>
                </div>
                <ChevronDown size={18} className="text-neutral-500 shrink-0" data-id="element-11978" data-mp="2cuyrpijaokha" />
              </button>
            </div>

            {/* Right column */}
            <div data-id="element-11979" data-mp="27v42rkj2y4yv">
              <h2 className="text-lg sm:text-[20px] font-bold text-[#333333] mb-4 sm:mb-5" data-id="element-11980" data-mp="27xq6f92uo0bt">
                Review app permissions
              </h2>

              <div className="rounded-2xl bg-[#F8F8F8] border border-neutral-200/70 p-5 sm:p-6 space-y-6 sm:space-y-7" data-id="element-11981" data-mp="1stdnqjsesxu1">
                <div data-id="element-11982" data-mp="1xqdy36wakmqy">
                  <p className="text-sm font-bold text-[#333333] mb-3" data-id="element-11983" data-mp="1xtrzabt7ypwt">
                    Information "Giga" can view
                  </p>
                  <div className="divide-y divide-neutral-200/70" data-id="element-11984" data-mp="27qeg8pifcwtk">
                    {viewItems.map((item, i) => <div key={i} className="flex items-center gap-3 sm:gap-4 py-3 sm:py-4 first:pt-0" data-id="element-11985" data-mp="1a2minufgftlg" data-mpi={i}>
                      
                        <span className="shrink-0" data-id="element-11986" data-mp="1f1z4cbulaumg">{item.icon}</span>
                        <span className="flex-1 text-sm sm:text-[15px] text-[#333333] leading-snug" data-id="element-11987" data-mp="1jzqwn6z5cir6">
                          {item.label}
                        </span>
                        <ChevronRight size={16} className="text-neutral-400 shrink-0" data-id="element-11988" data-mp="1osttpp34uac4" />
                      
                      </div>)}
                  </div>
                </div>

                <div data-id="element-11989" data-mp="w8v58aylrpkg">
                  <p className="text-sm font-bold text-[#333333] mb-3" data-id="element-11990" data-mp="2xzvepwxsplft">
                    Actions "Giga" can take
                  </p>
                  <div className="divide-y divide-neutral-200/70" data-id="element-11991" data-mp="2t1tw1d4a7o4v">
                    {actionItems.map((item, i) => <div key={i} className="flex items-center gap-3 sm:gap-4 py-3 sm:py-4 first:pt-0" data-id="element-11992" data-mp="37qqdg0s8adi5" data-mpi={i}>
                      
                        <span className="shrink-0" data-id="element-11993" data-mp="32xnh5kw2gpy9">{item.icon}</span>
                        <span className="flex-1 text-sm sm:text-[15px] text-[#333333] leading-snug" data-id="element-11994" data-mp="3gpbs3kukx8dn">
                          {item.label}
                        </span>
                        <ChevronRight size={16} className="text-neutral-400 shrink-0" data-id="element-11995" data-mp="3qoarsdxd9toi" />
                      
                      </div>)}
                  </div>
                </div>
              </div>

              <p className="text-xs sm:text-[13px] text-neutral-500 leading-relaxed mt-5" data-id="element-11996" data-mp="1wa9s5qtzt2nd">
                Slack will share your granted permissions with Giga. View
                Slack's{' '}
                <a href="#" className="text-[#1264A3] hover:underline" data-id="element-11997" data-mp="6to0prxupa5k">
                  privacy policy
                </a>
                . By agreeing to allow Slack to share your granted permissions,
                you are also agreeing to Giga's{' '}
                <a href="#" className="text-[#1264A3] hover:underline" data-id="element-11998" data-mp="3t8p7ubaa4lf7">
                  privacy agreement
                </a>{' '}
                and{' '}
                <a href="#" className="text-[#1264A3] hover:underline" data-id="element-11999" data-mp="3k7rb8e267g6n">
                  terms of service
                </a>
                . Please review Giga's terms before installing.
              </p>

              <div className="fixed sm:static bottom-0 inset-x-0 p-4 sm:p-0 bg-white sm:bg-transparent border-t border-neutral-200 sm:border-none flex flex-col-reverse sm:flex-row items-center sm:justify-end gap-3 mt-0 sm:mt-6 z-10" data-id="element-12000" data-mp="3aawd3w9t7hsw">
                <button onClick={() => setStep(2)} className="w-full sm:w-auto px-6 py-3.5 sm:py-2.5 rounded-xl sm:rounded-lg border border-neutral-300 text-[#333333] text-[15px] font-bold hover:bg-neutral-50 transition-colors" data-id="element-12001" data-mp="18jjbbg351q12">
                  
                  Cancel
                </button>
                <button onClick={() => setStep(4)} className="w-full sm:w-auto px-7 py-3.5 sm:py-2.5 rounded-xl sm:rounded-lg bg-[#007A5A] hover:bg-[#148567] text-white text-[15px] font-bold transition-colors shadow-sm" data-id="element-12002" data-mp="1709nb4qmo4b9">
                  
                  Allow
                </button>
              </div>
            </div>
          </div>
        </div>
      </motion.div>;
  }
  // Step 4: Channel Selection
  if (step === 4) {
    return <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="min-h-screen bg-white font-body flex flex-col" data-id="element-12003" data-mp="1l1c9dhhmhem6">
        
        <GigaHeader data-id="element-12004" data-mp="1gxxr0k67om5k" />

        <div className="flex-1 flex items-center justify-center px-6 py-12" data-id="element-12005" data-mp="2di6hz0zbrf7c">
          <div className="w-full max-w-md" data-id="element-12006" data-mp="28qy8k4xzvtu6">
            <div className="mb-7 sm:mb-10" data-id="element-12007" data-mp="28pvghtoxrlco">
              <div className="mb-2.5" data-id="element-12008" data-mp="28p3muoewfxp1">
                <span className="text-xs sm:text-sm font-medium text-[#EA580C] uppercase tracking-wider" data-id="element-12009" data-mp="3reg8b4i1yg3i">
                  STEP 1 OF 4
                </span>
              </div>
              <h1 className="text-2xl sm:text-3xl font-title font-bold text-[#333333] mb-2.5 sm:mb-3" data-id="element-12010" data-mp="498h9e8esh5h">
                Which{' '}
                <span className="inline-flex items-center align-middle -mt-1" data-id="element-12011" data-mp="994jp91gk0ix">
                  <SlackIcon size={24} data-id="element-12012" data-mp="e7oapnuws2i5" />
                </span>{' '}
                channels should Giga join?
              </h1>
              <p className="text-sm sm:text-base text-neutral-600 leading-relaxed" data-id="element-12013" data-mp="j4y5lj12h6dd">
                We found 10 public channels in your workspace
              </p>
            </div>

            <div className="space-y-3 mb-6" data-id="element-12014" data-mp="ryt3q73rtwji">
              <button onClick={() => setStep(5)} className="w-full py-3.5 px-6 rounded-xl bg-[#2B211A] text-white font-medium hover:bg-black transition-colors" data-id="element-12015" data-mp="s0nl5bfu36iu">
                
                Invite Giga to public channels only
              </button>
              <button onClick={() => setStep(12)} className="w-full py-3.5 px-6 rounded-xl border-2 border-neutral-200 text-[#333333] font-medium hover:border-[#EA580C] hover:bg-[#FAF8F4]/30 transition-all" data-id="element-12016" data-mp="cssb5443pg6n">
                
                Select channels
              </button>
            </div>

            <div className="flex items-center gap-2.5 text-[13px] text-neutral-500 bg-neutral-50 rounded-xl px-4 py-3" data-id="element-12017" data-mp="hsenzcg0ouex">
              <Info size={16} className="shrink-0" data-id="element-12018" data-mp="3q8zsafxpezbp" />
              <p className="[text-wrap:balance]" data-id="element-12019" data-mp="3l9n862y7w6ct">
                You can remove Giga from any channel anytime.
              </p>
            </div>
          </div>
        </div>
      </motion.div>;
  }
  // Step 12: Specific Channel Selection
  if (step === 12) {
    const DUMMY_CHANNELS = ['all-faith-test', 'new-channel', 'social'];
    // Auto-select all by default to match the screenshot's "3 selected" state
    // if the user hasn't interacted yet.
    const activeChannels = selectedChannels.length > 0 || !selectedChannels.includes('initialized') ? selectedChannels.includes('initialized') ? selectedChannels.filter(c => c !== 'initialized') : DUMMY_CHANNELS : [];
    const toggleChannel = (ch: string) => {
      let next = [...activeChannels];
      if (next.includes(ch)) {
        next = next.filter(c => c !== ch);
      } else {
        next.push(ch);
      }
      // Add a hidden flag so we know the user has interacted and we shouldn't auto-fill
      if (!next.includes('initialized')) next.push('initialized');
      setSelectedChannels(next);
    };
    return <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="min-h-screen bg-white font-body flex flex-col" data-id="element-12020" data-mp="3g2gmf2j2np36">
        
        <GigaHeader data-id="element-12021" data-mp="364itw2u6j64r" />

        <div className="flex-1 flex items-center justify-center px-6 py-12" data-id="element-12022" data-mp="1ps3nqf9tqveb">
          <div className="w-full max-w-md" data-id="element-12023" data-mp="1pu81doxiz8rs">
            <div className="mb-7 sm:mb-10" data-id="element-12024" data-mp="1urq4gyov2trv">
              <div className="mb-2.5" data-id="element-12025" data-mp="1ushzo8ejqphm">
                <span className="text-xs sm:text-sm font-medium text-[#EA580C] uppercase tracking-wider" data-id="element-12026" data-mp="umqb38a38ioc">
                  STEP 1 OF 4
                </span>
              </div>
              <h1 className="text-2xl sm:text-3xl font-title font-bold text-[#333333] mb-2.5 sm:mb-3" data-id="element-12027" data-mp="zj786rz9m7dk">
                Which{' '}
                <span className="inline-flex items-center align-middle -mt-1" data-id="element-12028" data-mp="qkdf3bl57z4r">
                  <SlackIcon size={24} data-id="element-12029" data-mp="bvz60ix4vefr" />
                </span>{' '}
                channels should Giga join?
              </h1>
              <p className="text-sm sm:text-base text-neutral-600 leading-relaxed" data-id="element-12030" data-mp="gmyz1d34tccz">
                We found 10 public channels in your workspace
              </p>
            </div>

            <div className="bg-white rounded-2xl border border-neutral-200 shadow-sm overflow-hidden mb-4" data-id="element-12031" data-mp="6we5i42bey0x">
              {/* Search */}
              <div className="relative border-b border-neutral-200" data-id="element-12032" data-mp="3p3twgd15u4jo">
                <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" data-id="element-12033" data-mp="3u1vc0nzdnlqe" />
                
                <input type="text" placeholder={`Search ${WORKSPACE_NAME} workspace`} className="w-full pl-11 pr-4 py-4 text-[15px] text-[#2B211A] placeholder:text-neutral-400 focus:outline-none bg-transparent" data-id="element-12034" data-mp="3aglemtdcj0yq" />
                
              </div>

              {/* List */}
              <div className="bg-[#FAF8F4]/50 p-2" data-id="element-12035" data-mp="3fen724wnwcmv">
                <div className="px-3 py-2 text-sm font-medium text-[#2B211A]" data-id="element-12036" data-mp="3fgznueqoogyc">
                  {activeChannels.length} selected
                </div>
                <div className="flex flex-col gap-1 pb-2" data-id="element-12037" data-mp="3ogcuz0rc4tfw">
                  {DUMMY_CHANNELS.map(ch => {
                  const isSelected = activeChannels.includes(ch);
                  return <button key={ch} onClick={() => toggleChannel(ch)} className="flex items-center justify-between w-full px-3 py-2.5 rounded-lg hover:bg-white transition-colors group" data-id="element-12038" data-mp="209zxccsl174">
                        
                        <div className="flex items-center gap-3 text-[15px] text-[#2B211A]" data-id="element-12039" data-mp="byzbyoi0brfg">
                          <Hash size={16} className="text-neutral-400" data-id="element-12040" data-mp="j25tlb1ce3mr" />
                          {ch}
                        </div>
                        <div className={`w-5 h-5 rounded-full border flex items-center justify-center transition-colors ${isSelected ? 'bg-[#EA580C] border-[#EA580C] text-white' : 'border-neutral-300 text-transparent group-hover:border-neutral-400'}`} data-id="element-12041" data-mp="o0z733gmpumx">
                          
                          <Check size={12} strokeWidth={3} data-id="element-12042" data-mp="11qudbto3a2wy" />
                        </div>
                      </button>;
                })}
                </div>
              </div>
            </div>

            <button onClick={() => setStep(5)} disabled={activeChannels.length === 0} className="w-full py-4 px-6 rounded-xl bg-[#2B211A] text-white font-medium hover:bg-black transition-colors disabled:opacity-50 disabled:cursor-not-allowed mb-4 shadow-sm" data-id="element-12043" data-mp="1br3b3bxqbweg">
              
              Invite Giga to {activeChannels.length} channels
            </button>

            <div className="flex items-center justify-center gap-2 text-[13px] text-neutral-500" data-id="element-12044" data-mp="1zldemkaz9rqe">
              <Info size={14} data-id="element-12045" data-mp="1uuoop9ey52td" />
              <p data-id="element-12046" data-mp="1uuev7bup0dix">You can remove Giga from any channel anytime</p>
            </div>
          </div>
        </div>
      </motion.div>;
  }
  // Step 5: Tell us about you
  if (step === 5) {
    return <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="min-h-screen bg-white font-body flex flex-col" data-id="element-12047" data-mp="1luid5t2t6cwu">
        
        <GigaHeader data-id="element-12048" data-mp="2eixgvjghp8sv" />

        <div className="flex-1 flex items-center justify-center px-6 py-12" data-id="element-12049" data-mp="2eie5kllchint">
          <div className="w-full max-w-md" data-id="element-12050" data-mp="29jb398wr1qjj">
            <div className="mb-7 sm:mb-10" data-id="element-12051" data-mp="29hzxj3jqu8ps">
              <div className="mb-2.5" data-id="element-12052" data-mp="24kgiw2j4vtxb">
                <span className="text-xs sm:text-sm font-medium text-[#EA580C] uppercase tracking-wider" data-id="element-12053" data-mp="24joouw56q495">
                  STEP 2 OF 4
                </span>
              </div>
              <h1 className="text-2xl sm:text-3xl font-title font-bold text-[#333333] mb-2.5 sm:mb-3" data-id="element-12054" data-mp="t4rvja1ywsvz">
                Tell us more about you
              </h1>
              <p className="text-sm sm:text-base text-neutral-600 leading-relaxed" data-id="element-12055" data-mp="2h068ovvvvmik">
                A couple quick questions so Giga can hit the ground running.
              </p>
            </div>

            <div className="space-y-6 mb-8" data-id="element-12056" data-mp="27z868i13fn23">
              <div data-id="element-12057" data-mp="2czmf22air116">
                <label className="block text-sm font-medium text-[#333333] mb-2" data-id="element-12058" data-mp="2d174jdhz7ts6">
                  What best describes you?
                </label>
                <SearchableSelect value={role} onChange={setRole} searchable={false} options={['I am a founder or executive and wear many hats at my company', 'I am a real estate agent or real estate broker', 'I am a consultant or work with many different clients', 'I work in sales or rev ops', 'I work on a marketing or growth team', 'I work in finance or operations', 'I am a content creator or social media influencer', 'I am a product manager or work with an engineering team', 'Other']} placeholder="Select an option" ariaLabel="What best describes you?" data-id="element-12059" data-mp="34rlitatwj84d" />
                
              </div>

              <div data-id="element-12060" data-mp="17t4kkf5xbvan">
                <label className="block text-sm font-medium text-[#333333] mb-3" data-id="element-12061" data-mp="17txl2yeahokc">
                  What kind of business is it?
                </label>
                <SearchableSelect value={businessType} onChange={setBusinessType} options={BUSINESS_TYPES} placeholder="Select an option" searchPlaceholder="Search business types…" ariaLabel="What kind of business is it?" data-id="element-12062" data-mp="1lqt6lqbq98ha" />
                
              </div>

              <div data-id="element-12063" data-mp="p3o8qvosggex">
                <label className="block text-sm font-medium text-[#333333] mb-2" data-id="element-12064" data-mp="p47pi92pcyrd">
                  What would Giga help you with? Select from use cases below:
                </label>
                <MultiSelectDropdown values={firstTeammates} onChange={toggleFirstTeammate} options={['Marketing & Ad Campaigns', 'Operations & Workflow Automation', 'Data analysis & reporting', 'Sales & CRM', 'Content & Creative Production', 'Engineering & Development', 'Executive / Leadership Assistant', 'Agency/Consultancy & Client Operations', 'eCommerce & Shopify', 'Other']} placeholder="Select use cases" data-id="element-12065" data-mp="y2jgwdbe80kv" />
                
              </div>

              <div data-id="element-12066" data-mp="2f6qagajujehm">
                <label className="block text-sm font-medium text-[#333333] mb-2" data-id="element-12067" data-mp="2f6gfs9nuwj5l">
                  How did you find us?{' '}
                  <span className="text-neutral-400 font-normal" data-id="element-12068" data-mp="2k4i1718r8uk8">
                    (optional)
                  </span>
                </label>
                <div className="relative" data-id="element-12069" data-mp="1rh4avlzkax93">
                  <select value={foundUs} onChange={e => setFoundUs(e.target.value)} className={`w-full px-4 py-3 rounded-xl border border-neutral-200 appearance-none bg-white focus:outline-none focus:border-[#EA580C] transition-colors ${foundUs ? 'text-[#333333]' : 'text-neutral-400'}`} data-id="element-12070" data-mp="1ri71rtwvwzp0">
                    
                    <option value="" disabled data-id="element-12071" data-mp="37uue6qiuxhho">
                      Select an option
                    </option>
                    <option data-id="element-12072" data-mp="3bv60s8us5rvs">Google search</option>
                    <option data-id="element-12073" data-mp="10a5hw8t9w4lw">Slack Marketplace</option>
                    <option data-id="element-12074" data-mp="10bq8jncgv3eh">Community</option>
                    <option data-id="element-12075" data-mp="w7s9tdr1zuly">Podcast</option>
                    <option data-id="element-12076" data-mp="w8tw6edis3iz">Reddit</option>
                    <option data-id="element-12077" data-mp="rgse2bto7il1">Product Hunt</option>
                    <option data-id="element-12078" data-mp="rfhaod450cug">Facebook</option>
                    <option data-id="element-12079" data-mp="mih1vufwkvzn">YouTube</option>
                    <option data-id="element-12080" data-mp="mhpkbnj54ysd">Twitter / X</option>
                    <option data-id="element-12081" data-mp="hiw4vpkamxpk">Newsletter</option>
                    <option data-id="element-12082" data-mp="hjxju54fk4ck">Other</option>
                  </select>
                  <ChevronDown size={18} className="absolute right-4 top-1/2 -translate-y-1/2 text-neutral-400 pointer-events-none" data-id="element-12083" data-mp="cjjit718xuo1" />
                  
                </div>
              </div>

              <div data-id="element-12084" data-mp="3jwfgfoyiesc">
                <label className="block text-sm font-medium text-[#333333] mb-3" data-id="element-12085" data-mp="3kejvzlrvzbc">
                  Keep me posted with Giga tips and updates
                </label>
                <div className="flex items-center gap-4" data-id="element-12086" data-mp="1xxnlz6bzoijk">
                  <button onClick={() => setEmailOptIn(true)} className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${emailOptIn ? 'text-[#EA580C]' : 'text-neutral-500 hover:text-[#333333]'}`} data-id="element-12087" data-mp="1xy5r6sgmq73m">
                    
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${emailOptIn ? 'border-[#EA580C] bg-[#EA580C]' : 'border-neutral-300'}`} data-id="element-12088" data-mp="x28l4ww61rlj">
                      
                      {emailOptIn && <div className="w-2 h-2 bg-white rounded-full" data-id="element-12089" data-mp="o0hdhmi2fap1"></div>}
                    </div>
                    <span className="text-sm font-medium" data-id="element-12090" data-mp="s46y60vmd84s">Of course!</span>
                  </button>
                  <button onClick={() => setEmailOptIn(false)} className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${!emailOptIn ? 'text-[#EA580C]' : 'text-neutral-500 hover:text-[#333333]'}`} data-id="element-12091" data-mp="e4ebcgn7rc23">
                    
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${!emailOptIn ? 'border-[#EA580C] bg-[#EA580C]' : 'border-neutral-300'}`} data-id="element-12092" data-mp="28fax1enblszd">
                      
                      {!emailOptIn && <div className="w-2 h-2 bg-white rounded-full" data-id="element-12093" data-mp="3szod8b8dycfa"></div>}
                    </div>
                    <span className="text-sm font-medium" data-id="element-12094" data-mp="bjgc6z8wzpvs">No thanks</span>
                  </button>
                </div>
              </div>
            </div>

            <button onClick={() => {
            trackSignupProfileCompleted(role, businessType);
            setStep(6);
          }} disabled={!role || !businessType || firstTeammates.length === 0} className={`w-full py-4 px-6 rounded-xl font-medium transition-colors flex items-center justify-center gap-2 ${role && businessType && firstTeammates.length > 0 ? 'bg-[#2B211A] text-white hover:bg-black' : 'bg-neutral-300 text-white cursor-not-allowed'}`} data-id="element-12095" data-mp="6q52mqcpwv06">
              
              Continue
              <ArrowRight size={18} data-id="element-12096" data-mp="2w9fhf0ykrkup" />
            </button>
          </div>
        </div>
      </motion.div>;
  }
  // Step 6: Tool Selection
  if (step === 6) {
    const CATEGORIES: {
      key: string;
      label: string;
    }[] = [{
      key: 'communication',
      label: 'COMMUNICATION'
    }, {
      key: 'docs',
      label: 'DOCS & DRIVE'
    }, {
      key: 'engineering',
      label: 'ENGINEERING'
    }, {
      key: 'project',
      label: 'PROJECT MANAGEMENT'
    }, {
      key: 'crm',
      label: 'CRM & SALES'
    }, {
      key: 'support',
      label: 'SUPPORT'
    }, {
      key: 'finance',
      label: 'FINANCE'
    }, {
      key: 'design',
      label: 'DESIGN'
    }, {
      key: 'analytics',
      label: 'ANALYTICS'
    }, {
      key: 'marketing',
      label: 'MARKETING'
    }];
    const filteredTools = ALL_TOOLS.filter(t => t.name.toLowerCase().includes(toolSearch.toLowerCase()));
    const POPULAR_TOOL_IDS = ['slack', 'gmail', 'googledrive', 'notion', 'github', 'linear', 'jira', 'salesforce', 'hubspot', 'stripe'];
    const popularTools = POPULAR_TOOL_IDS.map(id => ALL_TOOLS.find(t => t.id === id)).filter(Boolean) as typeof ALL_TOOLS;
    const isSearching = toolSearch.trim() !== '';
    const renderToolCard = (tool: (typeof ALL_TOOLS)[number]) => {
      const isSelected = selectedTools.includes(tool.id);
      return <button key={tool.id} onClick={() => toggleTool(tool.id)} aria-pressed={isSelected} className={`group relative p-3 rounded-2xl border transition-all duration-200 ease-out active:scale-[0.97] ${isSelected ? 'border-[#F8C9A4] bg-[#FFF7ED] shadow-[0_1px_3px_rgba(234,88,12,0.08)]' : 'border-neutral-200/80 bg-white hover:border-[#F3D2BB] hover:bg-[#FFFBF7]'}`} data-id="element-12097" data-mp="3f54264b9tow1">
          
          <AnimatePresence data-id="element-12098" data-mp="2vhmvgc9m2093">
            {isSelected && <motion.div initial={{
            opacity: 0,
            scale: 0.5
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.5
          }} transition={{
            type: 'spring',
            stiffness: 500,
            damping: 30
          }} className="absolute top-1.5 right-1.5 w-[18px] h-[18px] bg-[#EA580C] rounded-full flex items-center justify-center shadow-[0_1px_2px_rgba(234,88,12,0.3)]" data-id="element-12099" data-mp="2vftkwlvvmvvt">
              
                <Check size={11} strokeWidth={2.25} className="text-white" data-id="element-12100" data-mp="27rigym8amtve" />
              </motion.div>}
          </AnimatePresence>
          <ToolLogo name={tool.name} icon={tool.icon} className="w-8 h-8 mx-auto mb-1.5 transition-transform duration-200 group-hover:scale-105" fallbackTextSize="text-sm" data-id="element-12101" data-mp="2gsgjf0332tbv" />
          
          <div className="text-xs font-medium text-[#333333] text-center leading-tight" data-id="element-12102" data-mp="1t0j95dfa89cv">
            {tool.name}
          </div>
        </button>;
    };
    return <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="min-h-screen bg-white font-body flex flex-col" data-id="element-12103" data-mp="22tgwpxz4soic">
        
        <GigaHeader hideOnMobile data-id="element-12104" data-mp="1abcmxgfa4zmb" />

        <div className="flex-1 px-6 pt-6 lg:pt-12 pb-36 overflow-auto" data-id="element-12105" data-mp="1aajrhblrtwjh">
          <div className="max-w-4xl mx-auto" data-id="element-12106" data-mp="15ci62k0vhl4u">
            <div className="mb-2.5" data-id="element-12107" data-mp="15bgr44gqkehu">
              <span className="text-xs sm:text-sm font-medium text-[#EA580C] uppercase tracking-wider" data-id="element-12108" data-mp="3h0eqeidfsf7x">
                STEP 3 OF 4
              </span>
            </div>

            <h1 className="text-2xl sm:text-3xl font-title font-bold text-[#333333] mb-2.5 sm:mb-3" data-id="element-12109" data-mp="3808doypkbj9t">
              Where does your work actually live?
            </h1>
            <p className="text-sm sm:text-base text-neutral-600 mb-6 sm:mb-8 leading-relaxed" data-id="element-12110" data-mp="3c1tpegqd1rif">
              GitHub, Linear, the docs you reference — pick everything. Giga
              reads across them with citations back to source.
            </p>

            <div className="relative mb-6 sm:mb-8" data-id="element-12111" data-mp="3qr9b3ib1b6r9">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" size={20} data-id="element-12112" data-mp="3qskcxckx64fq" />
              
              <input type="text" placeholder="Search tools..." value={toolSearch} onChange={e => setToolSearch(e.target.value)} className="w-full pl-12 pr-4 py-3 sm:py-3.5 rounded-xl border border-neutral-200 text-[#333333] bg-white focus:outline-none focus:border-[#EA580C] focus:ring-2 focus:ring-[#EA580C]/15 transition-all" data-id="element-12113" data-mp="2fbaw6xt6dqi4" />
              
            </div>

            <div className="relative inline-block mb-7 sm:mb-10" data-id="element-12114" data-mp="2urzqaxgvhesx">
              <button onClick={() => setRoleMenuOpen(!roleMenuOpen)} aria-expanded={roleMenuOpen} aria-label={`Suggested for ${funcRoles.join(', ')}. Change use cases`} className="group inline-flex items-center gap-2 pl-3.5 pr-3 py-2 rounded-full border border-neutral-200 bg-white text-sm shadow-[0_1px_2px_rgba(0,0,0,0.03)] hover:bg-neutral-50 hover:border-neutral-300 transition-colors" data-id="element-12115" data-mp="2ptfzojrc3eu8">
                
                <Sparkles size={14} className="text-neutral-400 shrink-0" data-id="element-12116" data-mp="39h5k25wxyctf" />
                <span className="text-neutral-500" data-id="element-12117" data-mp="34jndgm6ec9ol">Suggested for</span>
                <span className="font-semibold text-[#333333] max-w-[200px] truncate" data-id="element-12118" data-mp="34lghdtpmkfsw">
                  {funcRoles.length === 0 ? 'Select use cases' : funcRoles.length <= 1 ? funcRoles[0] : `${funcRoles.length} use cases`}
                </span>
                <ChevronDown size={15} className={`text-neutral-400 transition-transform ${roleMenuOpen ? 'rotate-180' : ''}`} data-id="element-12119" data-mp="8mz7zt5sqc8z" />
                
              </button>

              <AnimatePresence data-id="element-12120" data-mp="hmunubaaqvfc">
                {roleMenuOpen && <>
                    <div className="fixed inset-0 z-40" onClick={() => setRoleMenuOpen(false)} data-id="element-12121" data-mp="hki1llxi9ywh" />
                  
                    <motion.div initial={{
                  opacity: 0,
                  y: 4,
                  scale: 0.95
                }} animate={{
                  opacity: 1,
                  y: 0,
                  scale: 1
                }} exit={{
                  opacity: 0,
                  y: 4,
                  scale: 0.95
                }} transition={{
                  duration: 0.15
                }} className="absolute left-0 top-full mt-2 w-72 bg-white rounded-xl shadow-xl border border-neutral-200 py-2 z-50 max-h-80 overflow-y-auto" data-id="element-12122" data-mp="rce8nmhkkzcu">
                    
                      {Object.keys(USE_CASE_RECS).map(r => {
                    const isSelected = funcRoles.includes(r);
                    return <button key={r} onClick={e => {
                      e.preventDefault();
                      setFuncRoles(prev => {
                        const next = isSelected ? prev.filter(x => x !== r) : [...prev, r];
                        const newTools = new Set<string>();
                        next.forEach(role => {
                          USE_CASE_RECS[role]?.forEach(t => newTools.add(t));
                        });
                        setSelectedTools(Array.from(newTools));
                        return next;
                      });
                    }} className={`w-full flex items-center gap-3 text-left px-4 py-2.5 text-sm transition-colors ${isSelected ? 'bg-[#FFEDD5] text-[#333333] font-medium' : 'hover:bg-neutral-50 text-neutral-600'}`} data-id="element-12123" data-mp="9as16j58qfx7">
                          
                            <div className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 transition-colors ${isSelected ? 'bg-[#EA580C] border-[#EA580C]' : 'border-neutral-300 bg-white'}`} data-id="element-12124" data-mp="1b0nck9icqztr">
                            
                              {isSelected && <Check size={12} strokeWidth={3} className="text-white" data-id="element-12125" data-mp="1lkfwktid1x0f" />}
                            </div>
                            <span className="flex-1 leading-snug" data-id="element-12126" data-mp="1pl9nzwv0hty2">{r}</span>
                          </button>;
                  })}
                    </motion.div>
                  </>}
              </AnimatePresence>
            </div>

            {!isSearching && popularTools.length > 0 && <div className="mb-5 sm:mb-6" data-id="element-12127" data-mp="11wgpptqx3a0a">
                <h2 className="flex items-center gap-1.5 text-[11px] sm:text-xs font-bold text-[#EA580C] uppercase tracking-wider mb-2.5 sm:mb-3" data-id="element-12128" data-mp="11xrplih8rxm4">
                  <Sparkles size={13} className="shrink-0" data-id="element-12129" data-mp="svs7snyekpi4" />
                  Most popular
                </h2>
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2.5" data-id="element-12130" data-mp="xulmgjpfemjv">
                  {popularTools.map(tool => renderToolCard(tool))}
                </div>
              </div>}

            {CATEGORIES.map(cat => {
            const items = filteredTools.filter(t => t.category === cat.key);
            if (items.length === 0) return null;
            return <div key={cat.key} className="mb-5 sm:mb-6" data-id="element-12131" data-mp="13gs0sfufmoui">
                  <h2 className="text-[11px] sm:text-xs font-bold text-neutral-400 uppercase tracking-wider mb-2.5 sm:mb-3" data-id="element-12132" data-mp="zc262ubifgb6">
                    {cat.label}
                  </h2>
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2.5" data-id="element-12133" data-mp="pmsb96pa7jjd">
                    {items.map(tool => renderToolCard(tool))}
                  </div>
                </div>;
          })}

            {filteredTools.length === 0 && (() => {
            const term = toolSearch.trim();
            const requested = term !== '' && requestedTools.includes(term.toLowerCase());
            return <div className="text-center py-12 px-6 rounded-2xl border-2 border-dashed border-neutral-200 bg-[#FAF8F4]" data-id="element-12134" data-mp="6o69dplxxoee">
                    {requested ? <div className="max-w-md mx-auto" data-id="element-12135" data-mp="2ibi6uyu35yk1">
                        <div className="w-12 h-12 rounded-full bg-[#FFEDD5] flex items-center justify-center mx-auto mb-4" data-id="element-12136" data-mp="2na37v0h5wm9j">
                          <Check size={22} className="text-[#EA580C]" data-id="element-12137" data-mp="2rdrengxyzdtq" />
                        </div>
                        <h3 className="font-bold text-[#333333] text-lg mb-1" data-id="element-12138" data-mp="2wa9ec2eg0lz7">
                          On it
                        </h3>
                        <p className="text-neutral-600 text-sm" data-id="element-12139" data-mp="23mvftzv3jicz">
                          We&rsquo;ve got your request for{' '}
                          <span className="font-semibold" data-id="element-12140" data-mp="28lq8dtiv8laa">{term}</span>
                          {requestedToolLink ? ' and the link' : ''}. If it has
                          an API or a login page, our team usually wires it up{' '}
                          <span className="font-semibold text-[#333333]" data-id="element-12141" data-mp="1fwrn4njg530r">
                            within 1 hour
                          </span>{' '}
                          — we&rsquo;ll email you the moment it&rsquo;s live.
                        </p>
                      </div> : <div className="max-w-xl mx-auto text-left" data-id="element-12142" data-mp="5vnkrexpu0s4">
                        <h3 className="font-bold text-[#333333] text-lg mb-1 text-center" data-id="element-12143" data-mp="avi9pcxxkgaj">
                          {term ? `Don't see ${term}?` : 'Looking for something specific?'}
                        </h3>
                        <p className="text-neutral-600 text-sm mb-5 text-center" data-id="element-12144" data-mp="kllahzbamyjf">
                          Giga connects to virtually anything. Tell us what you
                          need — drop a link and we&rsquo;ll usually have it
                          connected within 1 hour.
                        </p>
                        <label className="block text-[13px] font-semibold text-[#333333] mb-1.5" data-id="element-12145" data-mp="ylcnpxtzkawd">
                          Link{' '}
                          <span className="font-normal text-neutral-400" data-id="element-12146" data-mp="2vf7d2dnfl5bg">
                            (optional)
                          </span>
                        </label>
                        <input type="url" value={requestedToolLink} onChange={e => setRequestedToolLink(e.target.value)} placeholder="https://yourtool.com" className="w-full mb-4 px-4 py-2.5 rounded-xl border border-neutral-200 bg-white text-sm text-[#333333] placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-[#333333]/20 focus:border-[#333333]" data-id="element-12147" data-mp="2stzuxsmi8c8y" />
                    
                        <button onClick={() => term && setRequestedTools(prev => [...prev, term.toLowerCase()])} disabled={!term} className={`w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-bold transition-colors ${term ? 'bg-[#2B211A] text-white hover:bg-black' : 'bg-neutral-200 text-neutral-400 cursor-not-allowed'}`} data-id="element-12148" data-mp="3bqrd1yqb8lbf">
                      
                          {term ? `Request ${term}` : 'Type a name to request it'}
                        </button>
                        <p className="flex items-start justify-center gap-1.5 text-xs text-neutral-400 leading-relaxed text-center mt-4" data-id="element-12149" data-mp="1lf3j601ons72">
                          <Info size={13} className="shrink-0 mt-0.5" data-id="element-12150" data-mp="dgpd247lxil2" />
                          <span data-id="element-12151" data-mp="hghnynkh40tl">
                            Connect anything later from Settings — API token,
                            MCP server, or OAuth.
                          </span>
                        </p>
                      </div>}
                  </div>;
          })()}

            <StickyRevealBar data-id="element-12152" data-mp="3px2rvpy903pu">
              <div className="flex items-center justify-between gap-3" data-id="element-12153" data-mp="3pvrq1vod561d">
                <button onClick={() => setStep(5)} aria-label="Back" className="flex items-center justify-center w-11 h-11 rounded-full text-neutral-500 hover:text-[#333333] hover:bg-neutral-100 transition-colors shrink-0" data-id="element-12154" data-mp="3uob04inudj1f">
                  
                  <ArrowLeft size={20} data-id="element-12155" data-mp="3ltmyd2hh6jhd" />
                </button>
                <div className="flex items-center gap-2 sm:gap-4" data-id="element-12156" data-mp="36zi5cpiyhh8e">
                  <button onClick={() => setStep(7)} className="px-2 sm:px-4 py-3 text-sm font-medium text-neutral-500 hover:text-[#333333] transition-colors whitespace-nowrap" data-id="element-12157" data-mp="3bxa525pue7n5">
                    
                    Later
                  </button>
                  <button onClick={() => setStep(7)} aria-label={selectedTools.length > 0 ? `Continue with ${selectedTools.length} tools` : 'Continue'} className="px-5 sm:px-7 py-3 rounded-xl bg-[#2B211A] text-white font-medium hover:bg-black transition-colors flex items-center gap-2 whitespace-nowrap" data-id="element-12158" data-mp="2obzgrqzizj7j">
                    
                    Continue
                    {selectedTools.length > 0 && <span className="hidden sm:inline text-sm" data-id="element-12159" data-mp="3789474wbhm47">
                        with {selectedTools.length}{' '}
                        {selectedTools.length === 1 ? 'tool' : 'tools'}
                      </span>}
                    <ArrowRight size={18} strokeWidth={2} data-id="element-12160" data-mp="3g3fd4cqvwkay" />
                  </button>
                </div>
              </div>
            </StickyRevealBar>
          </div>
        </div>
      </motion.div>;
  }
  // Step 7: Connections — per-tool OAuth permission flow
  if (step === 7) {
    const connections = step7Connections;
    const total = connections.length;
    const doneCount = connectedConns.length;
    const activeIdx = step7ActiveIdx;
    const allDone = step7AllDone;
    const allow = (conn: any) => {
      const next = connectedConns.includes(conn.id) ? connectedConns : [...connectedConns, conn.id];
      setConnectedConns(next);
      // Lock in the chosen visibility (default to shared with team if untouched)
      setConnVisibility(prev => ({
        ...prev,
        [conn.id]: prev[conn.id] ?? 'team'
      }));
      // Reset optional permission toggles for the next tool's fresh card
      setOptionalScopes({});
      if (next.length === total) {
        // All done — close the consent screen and advance.
        setConnectPhase(null);
        setPermModal(null);
        setTimeout(() => setStep(finalStep), 600);
      } else {
        // Visibility was set up front on the list, so once the user kicks off
        // the sequence we chain straight to the next tool's consent screen.
        const nextConn = connections.find(c => !next.includes(c.id));
        if (nextConn) {
          setPermModal(nextConn);
          setConnectPhase('consent');
        } else {
          setConnectPhase(null);
          setPermModal(null);
        }
      }
    };
    const handleAllowClick = (conn: any) => {
      setConnectPhase('handing-off');
      setTimeout(() => {
        allow(conn);
      }, 2000);
    };
    const startConnecting = () => {
      const firstConn = connections.find(c => !connectedConns.includes(c.id));
      if (!firstConn) return;
      setConnectionsStarted(true);
      setPermModal(firstConn);
      setConnectPhase('consent');
    };
    return <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="min-h-screen bg-white font-body flex flex-col" data-id="element-12161" data-mp="3hmjjjivafp6u">
        
        <GigaHeader hideOnMobile data-id="element-12162" data-mp="1ftvdl1r9ennd" />

        <div className="flex-1 px-4 sm:px-6 py-6 sm:py-12 pb-40 overflow-auto" data-id="element-12163" data-mp="3a9kecxaez8iy">
          <div className="max-w-4xl mx-auto" data-id="element-12164" data-mp="3a7hdfi7q3uzg">
            <div className="mb-2.5" data-id="element-12165" data-mp="3f0ithtjo20km">
              <span className="text-xs sm:text-sm font-medium text-[#EA580C] uppercase tracking-wider" data-id="element-12166" data-mp="3f1lke1gzo30j">
                STEP 4 OF 4
              </span>
            </div>

            <h1 className="text-2xl sm:text-3xl font-title font-bold text-[#333333] mb-2.5 sm:mb-3" data-id="element-12167" data-mp="35bisk4llsv3s">
              Let's plug Giga into your stack.
            </h1>
            <p className="text-sm sm:text-base text-neutral-600 mb-6 sm:mb-8 leading-relaxed" data-id="element-12168" data-mp="1th4qlzqr9rz">
              Approve each one to connect. You can adjust permissions anytime in
              Settings.
            </p>

            <div className="bg-neutral-50 rounded-2xl p-4 sm:p-6 mb-6" data-id="element-12169" data-mp="3o0fm2696fjky">
              <div className="flex items-center justify-between mb-4 sm:mb-5" data-id="element-12170" data-mp="27mp724girawj">
                <h2 className="text-lg sm:text-xl md:text-2xl font-title font-bold text-[#333333] italic" data-id="element-12171" data-mp="27otn1krp00d6">
                  {total} connections to make
                </h2>
                <span className="text-xs sm:text-sm font-medium text-neutral-500" data-id="element-12172" data-mp="umvvhowr13d9">
                  {doneCount}/{total} done
                </span>
              </div>

              {/* progress bar */}
              <div className="h-1.5 w-full rounded-full bg-neutral-200 mb-5 sm:mb-6 overflow-hidden" data-id="element-12173" data-mp="13obsmuyjz8zj">
                <motion.div className="h-full rounded-full bg-[#EA580C]" animate={{
                width: `${doneCount / total * 100}%`
              }} transition={{
                type: 'spring',
                stiffness: 120,
                damping: 20
              }} data-id="element-12174" data-mp="znrxty1gchgk" />
                
              </div>

              <div className="space-y-2" data-id="element-12175" data-mp="ks3vyfjlw6fu">
                {connections.map(conn => {
                const connected = connectedConns.includes(conn.id);
                return <div key={conn.id} className={`flex items-center gap-3 p-3 sm:p-3.5 rounded-xl bg-white border transition-all ${connected ? 'border-[#EA580C]' : 'border-neutral-200'}`} data-id="element-12176" data-mp="3oe16p5rfmqr6">
                      
                      {/* Tool info */}
                      <ToolLogo name={conn.name} icon={conn.icon} className="w-9 h-9 shrink-0" fallbackTextSize="text-sm" data-id="element-12177" data-mp="i1z0u3ctmrc1" />
                      
                      <div className="min-w-0 flex-1" data-id="element-12178" data-mp="m2snyuifw23v">
                        <div className="font-bold text-sm sm:text-base text-[#333333] truncate" data-id="element-12179" data-mp="r2nf8z64mtpg">
                          {conn.name}
                        </div>
                        <div className="text-xs text-neutral-500" data-id="element-12180" data-mp="w1hvpugmg66e">
                          {conn.scopes.length} permission
                          {conn.scopes.length > 1 ? 's' : ''}
                        </div>
                      </div>

                      {/* Status */}
                      <div className="shrink-0" data-id="element-12181" data-mp="19rv58195vsxu">
                        {connected ? <span className="inline-flex items-center gap-1.5 pl-1 pr-2.5 py-1 rounded-full bg-[#FFF0E6] text-xs font-semibold text-[#EA580C]" data-id="element-12182" data-mp="1esri34bhqpfe">
                            <span className="w-4 h-4 rounded-full bg-[#EA580C] flex items-center justify-center" data-id="element-12183" data-mp="1jp85fwav8zrf">
                              <Check size={11} strokeWidth={3} className="text-white" data-id="element-12184" data-mp="1tswppeur0j4y" />
                            
                            </span>
                            Connected
                          </span> : <span className="inline-flex items-center gap-1.5 pl-2 pr-2.5 py-1 rounded-full border border-neutral-200 bg-white text-xs font-medium text-neutral-500" data-id="element-12185" data-mp="1jywldf7ajhxa">
                            <span className="w-2 h-2 rounded-full border-[1.5px] border-neutral-300" data-id="element-12186" data-mp="2ckz5yp3h9xeo" />
                            Pending
                          </span>}
                      </div>
                    </div>;
              })}
              </div>
            </div>
          </div>
        </div>

        {/* Fixed bottom action bar — keeps the primary action reachable as the
                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                         tool list scrolls. Hidden while a consent screen is open. */}
        {!permModal && <StickyRevealBar data-id="element-12187" data-mp="otp1f79pxvvu">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-3" data-id="element-12188" data-mp="otyha211umnt">
              {allDone ? <button onClick={() => setStep(finalStep)} className="w-full sm:w-auto flex items-center justify-center gap-2 px-8 py-3 rounded-xl bg-[#2B211A] text-white font-bold hover:bg-black transition-colors shrink-0" data-id="element-12189" data-mp="juc41sl816f0">
              
                  Continue
                  <ArrowRight size={18} data-id="element-12190" data-mp="1ch2btuy2htaw" />
                </button> : <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center gap-2 sm:gap-4 shrink-0" data-id="element-12191" data-mp="1cide1qbv6szw">
                  <button onClick={() => setStep(finalStep)} className="w-full sm:w-auto px-4 py-3 rounded-xl text-sm font-semibold text-neutral-500 hover:text-[#333333] hover:bg-neutral-100 transition-colors" data-id="element-12192" data-mp="1he2jgaakf444">
                
                    {doneCount > 0 ? 'Finish the rest later' : "I'll connect later"}
                  </button>
                  <button onClick={startConnecting} className="w-full sm:w-auto flex items-center justify-center gap-2 px-8 py-3 rounded-xl bg-[#2B211A] text-white font-bold hover:bg-black transition-colors" data-id="element-12193" data-mp="yhudf49qap7r">
                
                    {doneCount > 0 ? 'Resume connecting' : 'Start connecting'}
                    <ArrowRight size={18} data-id="element-12194" data-mp="arggp0g7826u" />
                  </button>
                </div>}
            </div>
          </StickyRevealBar>}

        {/* OAuth permission modal / full-screen flow — sits OUTSIDE the page
                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                         wrapper concerns; full-screen cover. */}
        <AnimatePresence data-id="element-12195" data-mp="2mea86lbvqjrd">
          {permModal && <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="fixed inset-0 z-50 flex flex-col items-center bg-white md:bg-[#F5F5F5] overflow-y-auto" data-id="element-12196" data-mp="2mhfxa0kidln3">
            
              {connectPhase === 'consent' && <div className="w-full h-full flex flex-col items-center md:pt-12 md:pb-24 md:px-6" data-id="element-12197" data-mp="10tvhpjnkq8k8">
                  {/* Fake URL Bar - Full width header on mobile, floating pill on desktop */}
                  <div className="flex items-center justify-between w-full md:max-w-md bg-neutral-100 md:bg-white md:rounded-full px-4 py-3 md:py-2 md:shadow-sm border-b md:border border-neutral-200 mb-0 md:mb-8 shrink-0" data-id="element-12198" data-mp="1as2tzp6x42fw">
                    <div className="flex items-center gap-2 text-neutral-500" data-id="element-12199" data-mp="1olallgv00xxl">
                      <Lock size={14} className="text-[#EA580C]" data-id="element-12200" data-mp="1ondkkqe4q1eg" />
                      <span className="font-mono text-xs truncate max-w-[200px] sm:max-w-none" data-id="element-12201" data-mp="1tl6pzh7lyrdo">
                        {permModal.id}.com{' '}
                        <span className="text-neutral-400" data-id="element-12202" data-mp="3vc0ckog0k8v6">
                          /oauth/authorize
                        </span>
                      </span>
                    </div>
                    <span className="font-mono text-xs text-neutral-400 shrink-0" data-id="element-12203" data-mp="47k0qitb5mdq">
                      {doneCount + 1} of {total}
                    </span>
                  </div>

                  {/* Consent Card - Full width on mobile, rounded card on desktop */}
                  <div className="w-full max-w-md bg-white md:rounded-3xl md:shadow-xl md:border border-neutral-200 flex flex-col min-h-screen md:min-h-0" data-id="element-12204" data-mp="23mzbifmrdwl8">
                    <div className="flex-1 overflow-auto" data-id="element-12205" data-mp="1aybuwxcyfve0">
                      <div className="p-6 md:p-8 pb-6 flex flex-col items-center" data-id="element-12206" data-mp="1awr2pee44ojb">
                        <div className="flex items-center justify-center gap-4 mb-6" data-id="element-12207" data-mp="16yt8ty9ede9l">
                          <div className="w-14 h-14 md:w-16 md:h-16 rounded-2xl border border-neutral-200 flex items-center justify-center shadow-sm bg-white" data-id="element-12208" data-mp="1kpewe09sqcdt">
                            <ToolLogo name={permModal.name} icon={permModal.icon} className="w-7 h-7 md:w-8 md:h-8" data-id="element-12209" data-mp="s82h0q7t2f6h" />
                        
                          </div>
                          <ArrowRight size={16} className="text-neutral-300" data-id="element-12210" data-mp="n7et4deviegh" />
                          <div className="w-14 h-14 md:w-16 md:h-16 rounded-2xl bg-[#EA580C] flex items-center justify-center shadow-sm" data-id="element-12211" data-mp="2yvbd2aniz6j1">
                            <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="Giga" className="w-7 h-7 md:w-8 md:h-8 object-contain [filter:brightness(0)_invert(1)]" data-id="element-12212" data-mp="2pw6fb6jsfh7x" />
                        
                          </div>
                        </div>

                        <h3 className="text-xl md:text-2xl font-title font-bold text-[#333333] text-center mb-3 leading-tight" data-id="element-12213" data-mp="3mldxq5p41a7m">
                          Gigabrain is requesting access to your{' '}
                          {permModal.name} account
                        </h3>

                        <p className="text-[13px] md:text-[14px] text-neutral-500 text-center mb-6 max-w-sm leading-relaxed" data-id="element-12214" data-mp="1x6h82bghikxv">
                          Choose what Giga can do. You can change this anytime
                          in Settings → Connections, and revoke from{' '}
                          {permModal.name} directly.
                        </p>

                        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-neutral-200 bg-neutral-50" data-id="element-12215" data-mp="19bdxe10d7y39">
                          <div className="w-5 h-5 rounded-full bg-[#2B211A] text-white flex items-center justify-center text-[10px] font-bold" data-id="element-12216" data-mp="14cb39blxbca2">
                            L
                          </div>
                          <span className="text-sm text-neutral-700" data-id="element-12217" data-mp="ud3gxyx7o7at">
                            Signed in as{' '}
                            <span className="font-semibold" data-id="element-12218" data-mp="qf5psqjvrb4s">l@acme.co</span>
                          </span>
                        </div>
                      </div>

                      <div className="px-6 md:px-8 py-6 bg-white border-t border-neutral-100" data-id="element-12219" data-mp="2kprhqvvwtvej">
                        <div className="flex items-center justify-between mb-4" data-id="element-12220" data-mp="2ft0zc1wmunpy">
                          <span className="text-sm font-semibold text-[#333333]" data-id="element-12221" data-mp="2ncabu40fr2j8">
                            This will allow Giga to:
                          </span>
                          <span className="text-xs font-mono text-neutral-400" data-id="element-12222" data-mp="2x1bqnqv8vdvm">
                            {permModal.scopes.filter((s: any) => s.required || optionalScopes[s.id]).length}
                            /{permModal.scopes.length} permissions
                          </span>
                        </div>

                        <div className="space-y-0 divide-y divide-neutral-100 border-b border-neutral-100 mb-6" data-id="element-12223" data-mp="29b7nfklyxg55">
                          {permModal.scopes.map((scope: any) => <label key={scope.id} className={`flex items-start gap-4 py-4 ${scope.required ? 'cursor-default' : 'cursor-pointer'}`} data-id="element-12224" data-mp="2e9iw3s0j1kls">
                        
                              <div className="mt-0.5" data-id="element-12225" data-mp="3poxwl5ry2uln">
                                <div className={`w-5 h-5 rounded flex items-center justify-center ${scope.required || !!optionalScopes[scope.id] ? 'bg-[#2B211A] border-[#2B211A]' : 'border-neutral-300 border'} ${scope.required ? 'opacity-100' : ''}`} data-id="element-12226" data-mp="3trkot7sj2hjd">
                            
                                  {(scope.required || !!optionalScopes[scope.id]) && <Check size={12} className="text-white" strokeWidth={3} data-id="element-12227" data-mp="telf09kwr1u5" />}
                                </div>
                              </div>
                              <div data-id="element-12228" data-mp="12e84py49yj7d">
                                <div className="flex items-center gap-2 mb-0.5" data-id="element-12229" data-mp="12d6v7y2wq4rr">
                                  <span className="text-[15px] font-bold text-[#333333]" data-id="element-12230" data-mp="xfn5a0wuk3jz">
                                    {scope.label}
                                  </span>
                                  {scope.required && <span className="px-1.5 py-0.5 rounded bg-neutral-100 text-[10px] font-bold text-neutral-500 tracking-wide uppercase" data-id="element-12231" data-mp="17crpvwb1nqyo">
                                      Required
                                    </span>}
                                </div>
                                <p className="text-sm text-neutral-500 leading-relaxed" data-id="element-12232" data-mp="1omvpt63txbez">
                                  {scope.description}
                                </p>
                              </div>
                            </label>)}
                        </div>

                        <div className="p-4 rounded-xl border border-neutral-200 bg-neutral-50 flex gap-3" data-id="element-12233" data-mp="1euzqxstx5h9p">
                          <ShieldCheck className="w-5 h-5 text-[#EA580C] shrink-0 mt-0.5" data-id="element-12234" data-mp="15w5m5f6fpkl2" />
                          <p className="text-[13px] text-neutral-600 leading-relaxed" data-id="element-12235" data-mp="15yie8l5wpbbu">
                            Giga stores access tokens encrypted at rest and
                            isolated per workspace. Your {permModal.name} data
                            is never shared with other Gigabrain customers or
                            used to train shared models.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="p-4 md:p-6 bg-white border-t border-neutral-100 flex flex-col md:flex-row items-center justify-between sticky bottom-0 z-10" data-id="element-12236" data-mp="2v8zmyspn8mro">
                      <p className="hidden md:block text-[11px] text-neutral-500 max-w-[180px] leading-relaxed" data-id="element-12237" data-mp="7hm9f01w7avc">
                        By continuing, you agree to Gigabrain's{' '}
                        <a href="#" className="text-neutral-700 hover:underline font-medium" data-id="element-12238" data-mp="3tvdtrqcx50dn">
                      
                          Terms
                        </a>{' '}
                        and{' '}
                        <a href="#" className="text-neutral-700 hover:underline font-medium" data-id="element-12239" data-mp="qedrx79m1lyc">
                      
                          Privacy Notice
                        </a>
                        .
                      </p>
                      <div className="flex flex-col-reverse md:flex-row md:items-center gap-3 w-full md:w-auto" data-id="element-12240" data-mp="hgv0nl5dibe0">
                        <button onClick={() => {
                    setPermModal(null);
                    setConnectPhase(null);
                    setConnectionsStarted(false);
                  }} className="w-full md:w-auto px-5 py-3.5 md:py-2.5 rounded-xl text-sm font-semibold text-neutral-600 hover:bg-neutral-100 transition-colors" data-id="element-12241" data-mp="324zlc9nocg9d">
                      
                          Cancel
                        </button>
                        <button onClick={() => handleAllowClick(permModal)} className="w-full md:w-auto px-6 py-3.5 md:py-2.5 rounded-xl bg-[#2B211A] text-white text-sm font-semibold hover:bg-[#EA580C] transition-colors shadow-sm whitespace-nowrap" data-id="element-12242" data-mp="2ymsep78anbr1">
                      
                          Allow access
                        </button>
                      </div>
                    </div>
                  </div>

                  <p className="hidden md:block mt-6 font-mono text-xs text-neutral-400 text-center max-w-sm leading-relaxed" data-id="element-12243" data-mp="2fvsk9qi159qd">
                    You'll be returned to Gigabrain automatically. This stays in
                    the same tab.
                  </p>
                </div>}

              {connectPhase === 'handing-off' && <div className="flex-1 flex flex-col items-center justify-center w-full" data-id="element-12244" data-mp="3n9w94qa01l8y">
                  <div className="flex items-center gap-6 mb-6" data-id="element-12245" data-mp="3s7o6w0xbs1l2">
                    <div className="w-12 h-12 rounded-xl border border-neutral-200 bg-white flex items-center justify-center shadow-sm" data-id="element-12246" data-mp="ubi4kod1bin">
                      <ToolLogo name={permModal.name} icon={permModal.icon} className="w-6 h-6" data-id="element-12247" data-mp="4vdhr2m6vpl5" />
                  
                    </div>
                    <Loader2 size={24} className="text-neutral-400 animate-spin" data-id="element-12248" data-mp="39a3nh9dbxv7u" />
                
                    <div className="w-12 h-12 rounded-xl bg-white border border-neutral-200 flex items-center justify-center shadow-sm" data-id="element-12249" data-mp="3dc8ee0ce8dqs">
                      <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="Giga" className="w-6 h-6 object-contain [filter:brightness(0)_saturate(100%)_invert(60%)_sepia(60%)_saturate(2500%)_hue-rotate(346deg)_brightness(100%)_contrast(96%)]" data-id="element-12250" data-mp="3ibus0aw4vw04" />
                  
                    </div>
                  </div>
                  <p className="font-mono text-sm text-neutral-500" data-id="element-12251" data-mp="12qg3bgm7aevs">
                    Handing off to {permModal.name}...
                  </p>
                </div>}
            </motion.div>}
        </AnimatePresence>
      </motion.div>;
  }
  // Step 8: Now go to Slack
  if (step === 8) {
    const navItems = [{
      label: 'Threads',
      icon: MessageSquare
    }, {
      label: 'Huddles',
      icon: Headphones
    }, {
      label: 'Recap',
      icon: ArrowRight
    }, {
      label: 'Directories',
      icon: Users
    }];
    return <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} className="min-h-screen bg-white font-body flex flex-col" data-id="element-12252" data-mp="ayrwtaq3smkv">
        
        {/* Top bar */}
        <div className="flex items-center justify-between px-6 md:px-10 py-6" data-id="element-12253" data-mp="29bs4hrai9qho">
          <Link to="/" aria-label="Back to Giga home" className="flex items-center gap-2 group w-fit" data-id="element-12254" data-mp="25db1njy4b262">
            
            <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="" aria-hidden="true" className="h-7 w-7 object-contain group-hover:scale-105 transition-transform" data-id="element-12255" data-mp="2cdwqs831oasa" />
            
            <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/giga4x.png" alt="Giga" className="h-6 object-contain" data-id="element-12256" data-mp="27iprdcgt5s96" />
            
          </Link>
        </div>

        <div className="flex-1 flex items-center" data-id="element-12257" data-mp="1jrtsxwhujysy">
          <div className="max-w-[1280px] mx-auto px-6 md:px-10 w-full grid lg:grid-cols-2 gap-12 lg:gap-16 items-center py-10" data-id="element-12258" data-mp="1jp7k7tj7zd95">
            {/* Left — call to action */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} data-id="element-12259" data-mp="1aqvxw3pdz1mi">
              
              <h1 className="text-5xl md:text-6xl font-title font-bold text-[#333333] mb-5 tracking-tight" data-id="element-12260" data-mp="r4u0mm2q5wyr">
                Giga's waiting in Slack.
              </h1>
              <p className="text-lg text-neutral-600 mb-8 max-w-md leading-relaxed" data-id="element-12261" data-mp="1528uq43xasx2">
                Open Slack and find Giga under{' '}
                <span className="font-semibold text-[#333333]" data-id="element-12262" data-mp="1f1zqsa84tfz9">"Apps"</span>, or
                just <span className="font-semibold text-[#333333]" data-id="element-12263" data-mp="1a3g2i362fs3q">@Giga</span>{' '}
                them in any channel in {WORKSPACE_NAME}.
              </p>
              <div className="flex flex-wrap items-center gap-4" data-id="element-12264" data-mp="1ort1l90g9r2g">
                <button onClick={() => setStep(9)} className="inline-flex items-center justify-center gap-2.5 bg-[#2B211A] text-white px-7 py-4 rounded-xl font-bold text-base hover:bg-black transition-colors shadow-lg shadow-black/20" data-id="element-12265" data-mp="1jspyvv7xzwxn">
                  
                  <SlackIcon size={20} data-id="element-12266" data-mp="1sruvgvzy1g76" />
                  Message Giga
                </button>
                <Link to="/" className="inline-flex items-center gap-2 text-neutral-500 font-medium hover:text-[#333333] transition-colors" data-id="element-12267" data-mp="qef3s7jifxxl">
                  
                  <ArrowLeft size={18} data-id="element-12268" data-mp="zg54k3za8981" />
                  Back to home
                </Link>
              </div>
            </motion.div>

            {/* Right — Slack window mock */}
            <motion.div initial={{
            opacity: 0,
            y: 30,
            scale: 0.97
          }} animate={{
            opacity: 1,
            y: 0,
            scale: 1
          }} transition={{
            duration: 0.6,
            delay: 0.25
          }} className="hidden lg:block rounded-2xl overflow-hidden shadow-2xl ring-1 ring-black/5 bg-white" data-id="element-12269" data-mp="197sqxqgzf02n">
              
              {/* macOS chrome */}
              <div className="flex items-center gap-2 px-4 py-3 bg-[#3F0E40]" data-id="element-12270" data-mp="3kxvalzfjucaq">
                <span className="w-3 h-3 rounded-full bg-[#ff5f57]" data-id="element-12271" data-mp="3oz6urpfnm5bj" />
                <span className="w-3 h-3 rounded-full bg-[#febc2e]" data-id="element-12272" data-mp="3hecqg5gx8lo3" />
                <span className="w-3 h-3 rounded-full bg-[#28c840]" data-id="element-12273" data-mp="3hgpgz70qw4cr" />
                <div className="flex-1 mx-4" data-id="element-12274" data-mp="3cn4gn8boiyae">
                  <div className="flex items-center gap-2 rounded-md bg-black/20 px-3 py-1.5 text-white/60 text-xs" data-id="element-12275" data-mp="37ot60vdk8jr4">
                    <Search size={12} data-id="element-12276" data-mp="33nrhb8h9rq3e" />
                    Search
                  </div>
                </div>
              </div>

              <div className="flex h-[420px]" data-id="element-12277" data-mp="46t7e6tw9qq6">
                {/* Sidebar */}
                <div className="w-40 shrink-0 bg-[#3F0E40] text-white/80 py-3 flex flex-col" data-id="element-12278" data-mp="3ve5o6kqv825k">
                  <div className="px-4 pb-3 mb-1 border-b border-white/10 flex items-center gap-1.5 font-bold text-white text-sm" data-id="element-12279" data-mp="3rcam3e141kqq">
                    {WORKSPACE_NAME}
                    <ChevronDown size={14} data-id="element-12280" data-mp="3mg265yf39nii" />
                  </div>
                  <div className="px-2 space-y-0.5 text-[13px]" data-id="element-12281" data-mp="2fz85wk2xax73">
                    {navItems.map(n => <div key={n.label} className="flex items-center gap-2.5 px-2 py-1.5 rounded hover:bg-white/10 transition-colors" data-id="element-12282" data-mp="2axrav574e4yf">
                      
                        <n.icon size={15} className="opacity-70" data-id="element-12283" data-mp="iwrcam2mwh9z" />
                        {n.label}
                      </div>)}
                    <div className="h-px bg-white/10 my-2 mx-2" data-id="element-12284" data-mp="4y9pcqkjz8t4" />
                    <div className="flex items-center gap-2.5 px-2 py-1.5 rounded hover:bg-white/10 transition-colors" data-id="element-12285" data-mp="9w2vxkprq4tx">
                      <Hash size={15} className="opacity-70" data-id="element-12286" data-mp="xlnei2i0f02o" />
                      Channels
                    </div>
                    <div className="flex items-center gap-2.5 px-2 py-1.5 rounded hover:bg-white/10 transition-colors" data-id="element-12287" data-mp="12joz4rv337g9">
                      <MessageSquare size={15} className="opacity-70" data-id="element-12288" data-mp="nvu5n9994z24" />
                      Direct messages
                    </div>
                    <div className="flex items-center gap-2.5 px-2 py-1.5 rounded hover:bg-white/10 transition-colors" data-id="element-12289" data-mp="1gl1dwzyid30k">
                      <Grid3x3 size={15} className="opacity-70" data-id="element-12290" data-mp="1lbriiaz411u7" />
                      Apps
                    </div>
                    {/* Active Giga DM */}
                    <div className="flex items-center justify-between gap-2 px-2 py-1.5 rounded bg-[#1164A3] text-white mt-0.5" data-id="element-12291" data-mp="35zk8buv2y245">
                      <span className="flex items-center gap-2.5" data-id="element-12292" data-mp="3euqfax636q89">
                        <span className="w-4 h-4 rounded bg-[#F97316] flex items-center justify-center" data-id="element-12293" data-mp="3ew1iaurpjtyb">
                          <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="" aria-hidden="true" className="w-2.5 h-2.5 object-contain [filter:brightness(0)_invert(1)]" data-id="element-12294" data-mp="2n315fnavdag1" />
                          
                        </span>
                        Giga
                      </span>
                      <span className="w-4 h-4 rounded-full bg-[#E01E5A] text-white text-[10px] font-bold flex items-center justify-center" data-id="element-12295" data-mp="2r6z3duogkf7i">
                        1
                      </span>
                    </div>
                  </div>
                </div>

                {/* Main column */}
                <div className="flex-1 min-w-0 bg-white flex flex-col" data-id="element-12296" data-mp="gzed1nv9zsdy">
                  {/* DM header + tabs */}
                  <div className="px-4 pt-3 border-b border-neutral-200" data-id="element-12297" data-mp="c01pf0wkyhab">
                    <div className="flex items-center gap-2 mb-3" data-id="element-12298" data-mp="c24r4i73hyuv">
                      <span className="w-6 h-6 rounded bg-[#F97316] flex items-center justify-center" data-id="element-12299" data-mp="72rsl06v33cg">
                        <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="" aria-hidden="true" className="w-3.5 h-3.5 object-contain [filter:brightness(0)_invert(1)]" data-id="element-12300" data-mp="14ofshv2j03sq" />
                        
                      </span>
                      <span className="font-bold text-[#333333] text-sm" data-id="element-12301" data-mp="py8ebrjjx6xu">
                        Giga
                      </span>
                      <ChevronDown size={14} className="text-neutral-400" data-id="element-12302" data-mp="1iogq91nhuv3z" />
                    </div>
                    <div className="flex items-center gap-5 text-sm" data-id="element-12303" data-mp="1q0og7mz2vqnr">
                      <span className="pb-2 text-neutral-500" data-id="element-12304" data-mp="1uyzqtzx76571">Home</span>
                      <span className="pb-2 -mb-px border-b-2 border-[#333333] font-semibold text-[#333333]" data-id="element-12305" data-mp="1v12c66nena5f">
                        Messages
                      </span>
                      <span className="pb-2 text-neutral-500" data-id="element-12306" data-mp="24xxaopjohajp">About</span>
                    </div>
                  </div>

                  {/* Messages */}
                  <div className="flex-1 overflow-auto px-4 py-4" data-id="element-12307" data-mp="2dxkbpa8hwec8">
                    <div className="text-center text-[11px] text-neutral-400 mb-4" data-id="element-12308" data-mp="2dvr3hqi8hm24">
                      This is the very beginning of your direct message history
                      with <span className="text-[#1164A3]" data-id="element-12309" data-mp="2nmc058eckgie">@Giga</span>
                    </div>
                    <div className="flex items-start gap-2.5" data-id="element-12310" data-mp="2snrk0x4sggz7">
                      <span className="w-9 h-9 rounded-lg bg-[#F97316] flex items-center justify-center shrink-0" data-id="element-12311" data-mp="2wo22h99rozuy">
                        <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="" aria-hidden="true" className="w-5 h-5 object-contain [filter:brightness(0)_invert(1)]" data-id="element-12312" data-mp="xj20d7rtshok" />
                        
                      </span>
                      <div className="min-w-0" data-id="element-12313" data-mp="11kdhsq0jpwlo">
                        <div className="flex items-center gap-1.5 mb-0.5" data-id="element-12314" data-mp="1ga2unii03p1s">
                          <span className="font-bold text-sm text-[#333333]" data-id="element-12315" data-mp="1gc5xj3495cnx">
                            Giga
                          </span>
                          <span className="text-[10px] font-bold uppercase tracking-wide text-neutral-500 bg-neutral-200 px-1.5 py-0.5 rounded border border-neutral-200" data-id="element-12316" data-mp="1pb9fg3ronm0u">
                            App
                          </span>
                          <span className="text-[11px] text-neutral-400 hover:underline cursor-pointer" data-id="element-12317" data-mp="3r2miena3bju9">
                            9:16 AM
                          </span>
                        </div>
                        <p className="text-sm text-[#333333] leading-snug mb-2" data-id="element-12318" data-mp="3cx40l5bb7cks">
                          Hi 👋, great to meet you. I'm Giga, your new AI
                          coworker. Here are two ways to work with me:
                        </p>
                        <p className="text-sm text-[#333333] leading-snug mb-1.5" data-id="element-12319" data-mp="385c9mto8r4u4">
                          💬 <span className="font-bold" data-id="element-12320" data-mp="2t8uzu6vp9y9o">DM me here</span> —
                          just message me like a coworker. Research, analysis,
                          reports, automation.
                        </p>
                        <p className="text-sm text-[#333333] leading-snug" data-id="element-12321" data-mp="2k8oniobqn4c3">
                          <span className="text-[#1164A3] font-medium" data-id="element-12322" data-mp="2p6q6l995z3nk">
                            @Giga
                          </span>{' '}
                          <span className="font-bold" data-id="element-12323" data-mp="ih3a0jkokdan">in any channel</span> —
                          mention me in context and I'll jump in with the full
                          thread as background.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Right — Slack mock (Mobile) */}
            <motion.div initial={{
            opacity: 0,
            y: 30,
            scale: 0.97
          }} animate={{
            opacity: 1,
            y: 0,
            scale: 1
          }} transition={{
            duration: 0.6,
            delay: 0.25
          }} className="lg:hidden rounded-2xl overflow-hidden shadow-2xl ring-1 ring-black/5 bg-white flex flex-col w-full max-w-md mx-auto" data-id="element-12324" data-mp="29momex39qcro">
              
              {/* Mobile Slack Header */}
              <div className="bg-[#3F0E40] text-white px-4 py-3 flex items-center gap-3" data-id="element-12325" data-mp="1lw2hvfsh3amf">
                <div className="w-9 h-9 rounded bg-white/20 flex items-center justify-center shrink-0" data-id="element-12326" data-mp="1hse5mjswbquu">
                  <span className="font-bold text-base" data-id="element-12327" data-mp="1cve0qc7s8u5b">
                    {WORKSPACE_INITIAL}
                  </span>
                </div>
                <div className="flex-1 min-w-0" data-id="element-12328" data-mp="3ok0yvce2tvxu">
                  <div className="text-xs font-medium text-white/70 truncate" data-id="element-12329" data-mp="3tfq33t11k10h">
                    {WORKSPACE_NAME}
                  </div>
                  <div className="font-bold text-[15px] truncate leading-tight" data-id="element-12330" data-mp="1avx9ovx8drj">
                    Giga
                  </div>
                </div>
                <Search size={20} className="text-white/80" data-id="element-12331" data-mp="35rztdr6sr7fg" />
              </div>

              {/* Mobile Messages Area */}
              <div className="flex-1 bg-white p-5" data-id="element-12332" data-mp="39tbky298e2qa">
                <div className="text-center text-[11px] text-neutral-400 mb-5" data-id="element-12333" data-mp="3es4yfuoiptqg">
                  This is the very beginning of your direct message history with{' '}
                  <span className="text-[#1164A3]" data-id="element-12334" data-mp="3jkocesr4aqvs">@Giga</span>
                </div>
                <div className="flex items-start gap-3" data-id="element-12335" data-mp="2m2o8d3odeoxe">
                  <span className="w-10 h-10 rounded-lg bg-[#F97316] flex items-center justify-center shrink-0" data-id="element-12336" data-mp="2r1sgrs3h0imo">
                    <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="" aria-hidden="true" className="w-6 h-6 object-contain [filter:brightness(0)_invert(1)]" data-id="element-12337" data-mp="3uc6dmkiuee95" />
                    
                  </span>
                  <div className="min-w-0 pt-0.5" data-id="element-12338" data-mp="2ztxc6zlkr87">
                    <div className="flex items-center gap-1.5 mb-1" data-id="element-12339" data-mp="3be2gkog8c5np">
                      <span className="font-bold text-[15px] text-[#333333]" data-id="element-12340" data-mp="3bgga2sv9h9t5">
                        Giga
                      </span>
                      <span className="text-[10px] font-bold uppercase tracking-wide text-neutral-500 bg-neutral-200 px-1.5 py-0.5 rounded border border-neutral-200" data-id="element-12341" data-mp="3kes65aewczsz">
                        App
                      </span>
                      <span className="text-xs text-neutral-400" data-id="element-12342" data-mp="znrtde7fj0h3">9:16 AM</span>
                    </div>
                    <p className="text-[15px] text-[#333333] leading-snug mb-2.5" data-id="element-12343" data-mp="uooopuvd2w9n">
                      Hi 👋, great to meet you. I'm Giga, your new AI coworker.
                      Here are two ways to work with me:
                    </p>
                    <p className="text-[15px] text-[#333333] leading-snug mb-2" data-id="element-12344" data-mp="175zeu2dtmi70">
                      💬 <span className="font-bold" data-id="element-12345" data-mp="tanjptvr6ppk">DM me here</span> — just
                      message me like a coworker. Research, analysis, reports,
                      automation.
                    </p>
                    <p className="text-[15px] text-[#333333] leading-snug" data-id="element-12346" data-mp="1lzkp4wev8s0w">
                      <span className="text-[#1164A3] font-medium" data-id="element-12347" data-mp="1qvruamzftzhs">@Giga</span>{' '}
                      <span className="font-bold" data-id="element-12348" data-mp="1c6m2hk2xjbjx">in any channel</span> —
                      mention me in context and I'll jump in with the full
                      thread as background.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </motion.div>;
  }
  if (step === 9) {
    const navItems = [{
      label: 'Threads',
      icon: MessageSquare
    }, {
      label: 'Huddles',
      icon: Headphones
    }, {
      label: 'Recap',
      icon: ArrowRight
    }, {
      label: 'Directories',
      icon: Users
    }];
    return <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} className="h-screen w-full bg-white font-body flex flex-col overflow-hidden" data-id="element-12349" data-mp="3vy1ltddrch63">
        
        {/* macOS chrome & Slack header */}
        <div className="flex items-center gap-4 px-4 py-2 bg-[#3F0E40] border-b border-white/10 shrink-0" data-id="element-12350" data-mp="nkb5gbeamf7h">
          <div className="flex items-center gap-2 w-16" data-id="element-12351" data-mp="jih6q8nnrl9y">
            <span className="w-3 h-3 rounded-full bg-[#ff5f57]" data-id="element-12352" data-mp="jh662hpietn2" />
            <span className="w-3 h-3 rounded-full bg-[#febc2e]" data-id="element-12353" data-mp="ekfnnnq8flyh" />
            <span className="w-3 h-3 rounded-full bg-[#28c840]" data-id="element-12354" data-mp="348yfzt682yrc" />
          </div>
          <div className="flex-1 flex justify-center" data-id="element-12355" data-mp="346vi6mytw1c2">
            <div className="flex items-center gap-2 rounded-md bg-white/20 hover:bg-white/30 transition-colors px-4 py-1.5 text-white/80 text-sm w-full max-w-2xl cursor-text" data-id="element-12356" data-mp="2z8txxypo1vz0">
              <Search size={14} data-id="element-12357" data-mp="1jqe78e3lh3kh" />
              Search {WORKSPACE_NAME}
            </div>
          </div>
          <div className="w-16 flex justify-end" data-id="element-12358" data-mp="1nrze9itg6nmr">
            <div className="w-7 h-7 rounded bg-white/20" data-id="element-12359" data-mp="1nr7vj8ky8kdw" />
          </div>
        </div>

        <div className="flex flex-1 min-h-0" data-id="element-12360" data-mp="1sksje7stvxzd">
          {/* Sidebar */}
          <div className="w-64 shrink-0 bg-[#3F0E40] text-white/80 py-4 flex flex-col overflow-y-auto" data-id="element-12361" data-mp="1xh97j2012ccg">
            <div className="px-4 pb-4 mb-2 border-b border-white/10 flex items-center justify-between font-bold text-white text-lg" data-id="element-12362" data-mp="22gcggxj4p2pp">
              <span className="flex items-center gap-1.5" data-id="element-12363" data-mp="27edwtap66nxh">
                {WORKSPACE_NAME}
                <ChevronDown size={16} data-id="element-12364" data-mp="2bhajbchtz46p" />
              </span>
              <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center" data-id="element-12365" data-mp="d38z0n0rl5q2">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-12366" data-mp="h3aqy4gp6ks5">
                  
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" data-id="element-12367" data-mp="3a4xaq45ckz86"></path>
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" data-id="element-12368" data-mp="3ew5o1b9st4qm"></path>
                </svg>
              </div>
            </div>
            <div className="px-3 space-y-0.5 text-[15px]" data-id="element-12369" data-mp="3jugy9n409h9d">
              {navItems.map(n => <div key={n.label} className="flex items-center gap-3 px-3 py-1.5 rounded-md hover:bg-white/10 transition-colors cursor-pointer" data-id="element-12370" data-mp="2m89ugffizsy2">
                
                  <n.icon size={18} className="opacity-70" data-id="element-12371" data-mp="2w5fl5mk89hxr" />
                  {n.label}
                </div>)}
              <div className="h-px bg-white/10 my-4 mx-3" data-id="element-12372" data-mp="306r9zpva1z4w" />

              <div className="flex items-center justify-between px-3 py-1.5 group cursor-pointer" data-id="element-12373" data-mp="304wqmfznmf2x">
                <span className="flex items-center gap-1 opacity-70 hover:opacity-100 transition-opacity" data-id="element-12374" data-mp="bh9nr3gx2d8f">
                  <ChevronDown size={14} data-id="element-12375" data-mp="fimudfru2kge" />
                  Channels
                </span>
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-md hover:bg-white/10 transition-colors cursor-pointer ml-4" data-id="element-12376" data-mp="1vx7pql4h9wy4">
                <Hash size={16} className="opacity-70" data-id="element-12377" data-mp="1r5pso85ksgif" />
                general
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-md hover:bg-white/10 transition-colors cursor-pointer ml-4" data-id="element-12378" data-mp="1m6d3hgr8exco">
                <Hash size={16} className="opacity-70" data-id="element-12379" data-mp="1h9mpr02wgduf" />
                engineering
              </div>

              <div className="h-px bg-white/10 my-4 mx-3" data-id="element-12380" data-mp="1cbcs8itpd15n" />

              <div className="flex items-center justify-between px-3 py-1.5 group cursor-pointer" data-id="element-12381" data-mp="188oga29oze4g">
                <span className="flex items-center gap-1 opacity-70 hover:opacity-100 transition-opacity" data-id="element-12382" data-mp="139lnbg6zkycu">
                  <ChevronDown size={14} data-id="element-12383" data-mp="32kd2mpi96iyv" />
                  Direct messages
                </span>
              </div>

              {/* Active Giga DM */}
              <div className="flex items-center justify-between gap-2 px-3 py-1.5 rounded-md bg-[#1164A3] text-white mt-1 cursor-pointer" data-id="element-12384" data-mp="3grgcnqfvlx31">
                <span className="flex items-center gap-3" data-id="element-12385" data-mp="3liq0qmls0sct">
                  <span className="relative w-5 h-5 rounded bg-[#F97316] flex items-center justify-center" data-id="element-12386" data-mp="37qrymvqhfwq5">
                    <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="" aria-hidden="true" className="w-3.5 h-3.5 object-contain [filter:brightness(0)_invert(1)]" data-id="element-12387" data-mp="3cp38h6gs278d" />
                    
                    <span className="absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full bg-[#2BAC76] border-2 border-[#1164A3]" data-id="element-12388" data-mp="3qhj25gnsgswm" />
                  </span>
                  Giga
                </span>
              </div>

              {[{
              name: 'Priya Patel',
              initial: 'P',
              color: 'bg-[#4A9E8E]',
              online: true
            }, {
              name: 'Marcus Lee',
              initial: 'M',
              color: 'bg-[#9B6DD6]',
              online: true
            }, {
              name: 'Jordan Kim',
              initial: 'J',
              color: 'bg-[#E0727F]',
              online: false
            }, {
              name: 'Elena Rodriguez',
              initial: 'E',
              color: 'bg-[#D9A441]',
              online: false
            }, {
              name: 'Sarah (you)',
              initial: 'S',
              color: 'bg-[#4F84D6]',
              online: true
            }].map(person => <div key={person.name} className="flex items-center gap-3 px-3 py-1.5 rounded-md hover:bg-white/10 transition-colors cursor-pointer ml-1" data-id="element-12389" data-mp="1opr0ktu7jhfq">
                
                  <span className={`relative w-5 h-5 rounded ${person.color} flex items-center justify-center text-xs font-bold text-white shrink-0`} data-id="element-12390" data-mp="1xrqkbtwlwzmd">
                  
                    {person.initial}
                    {person.online ? <span className="absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full bg-[#2BAC76] border-2 border-[#3F0E40]" data-id="element-12391" data-mp="151s14f53fwpm" /> : <span className="absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full bg-[#3F0E40] border-2 border-white/50" data-id="element-12392" data-mp="1ioqput3kup6k" />}
                  </span>
                  {person.name}
                </div>)}
            </div>
          </div>

          {/* Main Chat Area */}
          <div className="flex-1 min-w-0 bg-white flex flex-col" data-id="element-12393" data-mp="1xgsk9xmvopz7">
            {/* Header */}
            <div className="px-5 py-3 border-b border-neutral-200 flex items-center justify-between shrink-0" data-id="element-12394" data-mp="21gjlywdy0qii">
              <div className="flex items-center gap-3" data-id="element-12395" data-mp="26i8vu8pfiy50">
                <span className="w-8 h-8 rounded bg-[#F97316] flex items-center justify-center" data-id="element-12396" data-mp="2bez7mju7b7km">
                  <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="" aria-hidden="true" className="w-5 h-5 object-contain [filter:brightness(0)_invert(1)]" data-id="element-12397" data-mp="2gell8udxypty" />
                  
                </span>
                <div className="flex flex-col" data-id="element-12398" data-mp="2zbm2ux30ermt">
                  <div className="flex items-center gap-2" data-id="element-12399" data-mp="2zcf119nwk8tc">
                    <span className="font-bold text-[#333333] text-[15px]" data-id="element-12400" data-mp="2uiaw0vy76auw">
                      Giga
                    </span>
                    <span className="text-[10px] font-bold uppercase tracking-wide text-neutral-500 bg-neutral-100 px-1.5 py-0.5 rounded border border-neutral-200" data-id="element-12401" data-mp="2qfwdkcyfxd45">
                      App
                    </span>
                    <span className="w-2 h-2 rounded-full bg-[#2BAC76]" data-id="element-12402" data-mp="3e84hyte8dvr7" />
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-4 text-neutral-500" data-id="element-12403" data-mp="397p3fyi7qi7n">
                <span className="font-semibold text-sm pb-1 border-b-2 border-[#333333] text-[#333333] cursor-pointer" data-id="element-12404" data-mp="34a6ui84742zn">
                  Messages
                </span>
                <span className="font-semibold text-sm pb-1 border-b-2 border-transparent hover:text-[#333333] cursor-pointer" data-id="element-12405" data-mp="1xr8o056ns1ks">
                  About
                </span>
              </div>
            </div>

            {/* Messages Scroll Area */}
            <div className="flex-1 overflow-y-auto p-5 flex flex-col" data-id="element-12406" data-mp="1z9pco0mji02h">
              {/* Intro Block */}
              <div className="mb-8 mt-4" data-id="element-12407" data-mp="1la7n3j1f6is2">
                <div className="flex items-center gap-4 mb-4" data-id="element-12408" data-mp="1ldc0n70ryqve">
                  <div className="w-16 h-16 rounded-2xl bg-[#F97316] flex items-center justify-center shadow-sm" data-id="element-12409" data-mp="1q8ixbuvmmvat">
                    <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="" aria-hidden="true" className="w-10 h-10 object-contain [filter:brightness(0)_invert(1)]" data-id="element-12410" data-mp="1bj37ch3x6tw6" />
                    
                  </div>
                  <div data-id="element-12411" data-mp="16jqjpu585isj">
                    <div className="flex items-center gap-2 mb-1" data-id="element-12412" data-mp="slsa121umajd">
                      <span className="font-bold text-2xl text-[#333333]" data-id="element-12413" data-mp="soo7yqdp4z7t">
                        Giga
                      </span>
                      <span className="text-[11px] font-bold uppercase tracking-wide text-neutral-500 bg-neutral-100 px-1.5 py-0.5 rounded border border-neutral-200" data-id="element-12414" data-mp="dz3c34n8w5f3">
                        App
                      </span>
                      <span className="w-2.5 h-2.5 rounded-full bg-[#2BAC76]" data-id="element-12415" data-mp="nykftgixs98f" />
                    </div>
                  </div>
                </div>
                <p className="text-[15px] text-[#333333] mb-2" data-id="element-12416" data-mp="wyh9jwk6nyac">
                  This is the very beginning of your direct message history with{' '}
                  <span className="text-[#1164A3] bg-[#1164A3]/10 px-1 rounded" data-id="element-12417" data-mp="rymhvqsl34o8">
                    @Giga
                  </span>
                </p>
                <div className="flex items-center gap-1.5 text-[#1164A3] text-[15px] hover:underline cursor-pointer w-fit" data-id="element-12418" data-mp="11p7dd5cynt2x">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-12419" data-mp="1fo73khg4gg17">
                    
                    <circle cx="12" cy="12" r="10" data-id="element-12420" data-mp="2i2l682lg6i9n"></circle>
                    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" data-id="element-12421" data-mp="2i235asnubfve"></path>
                    <line x1="12" y1="17" x2="12.01" y2="17" data-id="element-12422" data-mp="2n04kv3m24x24"></line>
                  </svg>
                  How does Giga work?
                </div>
              </div>

              {/* Date Divider */}
              <div className="relative flex items-center justify-center mb-6" data-id="element-12423" data-mp="2w0sxbtvsypcw">
                <div className="absolute inset-0 flex items-center" data-id="element-12424" data-mp="1zcmrx4r23sxk">
                  <div className="w-full border-t border-neutral-200" data-id="element-12425" data-mp="24aojyf6gn2l6"></div>
                </div>
                <div className="relative bg-white px-4" data-id="element-12426" data-mp="28cjgl6dg4rsm">
                  <span className="text-[13px] font-bold text-[#333333] border border-neutral-200 rounded-full px-4 py-1 shadow-sm" data-id="element-12427" data-mp="28ayrhw9wi125">
                    Today <ChevronDown size={12} className="inline ml-1" data-id="element-12428" data-mp="1glc44wgmnw9w" />
                  </span>
                </div>
              </div>

              {/* Giga Onboarding Message */}
              <div className="flex items-start gap-3 hover:bg-neutral-50 -mx-5 px-5 py-2 transition-colors" data-id="element-12429" data-mp="1kkuo9q0mk3bo">
                <div className="w-10 h-10 rounded-xl bg-[#F97316] flex items-center justify-center shrink-0 mt-0.5" data-id="element-12430" data-mp="184bq8jcjzwzu">
                  <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="" aria-hidden="true" className="w-6 h-6 object-contain [filter:brightness(0)_invert(1)]" data-id="element-12431" data-mp="142hverp1hn7l" />
                  
                </div>
                <div className="min-w-0 flex-1" data-id="element-12432" data-mp="pdby599ri72c">
                  <div className="flex items-center gap-2 mb-1" data-id="element-12433" data-mp="pcitcdud5rmu">
                    <span className="font-bold text-[15px] text-[#333333]" data-id="element-12434" data-mp="kfsh61lojg6p">
                      Giga
                    </span>
                    <span className="text-[10px] font-bold uppercase tracking-wide text-neutral-500 bg-neutral-100 px-1.5 py-0.5 rounded border border-neutral-200" data-id="element-12435" data-mp="gf06r512n58u">
                      App
                    </span>
                    <span className="text-xs text-neutral-500 hover:underline cursor-pointer" data-id="element-12436" data-mp="6hc29ndnyjdh">
                      3:27 PM
                    </span>
                  </div>

                  <div className="text-[15px] text-[#333333] leading-relaxed space-y-3" data-id="element-12437" data-mp="37gpi1w61bgtq">
                    <p data-id="element-12438" data-mp="3lg8ehrphh3q7">
                      Hi 👋, great to meet you. I am Giga, your new AI coworker.
                      Here are three ways to work with me:
                    </p>

                    <div className="space-y-1.5" data-id="element-12439" data-mp="2ssupqgvxveh6">
                      <p data-id="element-12440" data-mp="2nszzmfjzmsx6">
                        💬 <span className="font-bold" data-id="element-12441" data-mp="2ntrjit48327m">DM me here</span> — just
                        message me like a coworker. Research, analysis, reports,
                        automation — anything.
                      </p>
                      <p data-id="element-12442" data-mp="2xpl5t47qua2l">
                        📣{' '}
                        <span className="text-[#1164A3] bg-[#1164A3]/10 px-1 rounded font-medium cursor-pointer" data-id="element-12443" data-mp="2a11s295xd534">
                          @Giga
                        </span>{' '}
                        <span className="font-bold" data-id="element-12444" data-mp="kkpu47u1e1vr">in any channel</span> —
                        mention me in context and I'll jump in with the full
                        thread as background.
                      </p>
                      <p data-id="element-12445" data-mp="bkknhzwo37in">
                        🔌{' '}
                        <span className="font-bold" data-id="element-12446" data-mp="bhygq2hlow1h">
                          I connect to 3000+ tools
                        </span>{' '}
                        — no tools are connected yet. Need GitHub, Stripe,
                        HubSpot, Google Ads, or anything else? Just tell me and
                        I'll figure out access.
                      </p>
                      <p data-id="element-12447" data-mp="u9r85sh0u973">
                        🔍{' '}
                        <span className="font-bold" data-id="element-12448" data-mp="z8udzf4tsjg4">
                          I'm now reading through the workspace history
                        </span>{' '}
                        — I'll propose ideas for how I can help over the next
                        hours.
                      </p>
                    </div>

                    <p data-id="element-12449" data-mp="1pibc2lgab61u">
                      Try one of these now — just copy, paste, and hit send:
                    </p>

                    <div className="space-y-4" data-id="element-12450" data-mp="1kl1olbcgh4gn">
                      <div data-id="element-12451" data-mp="1ft1i101vrdb3">
                        <p className="italic text-neutral-600 mb-1" data-id="element-12452" data-mp="1fs8jungzlw4k">
                          1. In a channel:
                        </p>
                        <div className="border-l-4 border-neutral-200 pl-3 py-0.5" data-id="element-12453" data-mp="1bq561s6uifg4">
                          <span className="text-[#1164A3] bg-[#1164A3]/10 px-1 rounded font-medium" data-id="element-12454" data-mp="16u69vif9zt55">
                            @Giga
                          </span>{' '}
                          summarize what's been discussed in this channel this
                          week
                        </div>
                      </div>

                      <div data-id="element-12455" data-mp="2r98bdgvig6xj">
                        <p className="italic text-neutral-600 mb-1" data-id="element-12456" data-mp="2r7f2rw1c7cmw">
                          2. Or right here:
                        </p>
                        <div className="border-l-4 border-neutral-200 pl-3 py-0.5" data-id="element-12457" data-mp="2n5ac9666qw4h">
                          Research the top 5 trends in our industry and put
                          together a brief report
                        </div>
                      </div>
                    </div>

                    <p data-id="element-12458" data-mp="ir9q15vbizmw">
                      Or just tell me what you need — I'll take it from there 🚀
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Message Composer */}
            <div className="px-5 pb-6 pt-2 shrink-0" data-id="element-12459" data-mp="3r47017zqke3w">
              <div className="border border-neutral-400 rounded-xl overflow-hidden focus-within:border-neutral-500 focus-within:ring-1 focus-within:ring-neutral-500 transition-all shadow-sm" data-id="element-12460" data-mp="3r6thq0gcc9zu">
                {/* Toolbar */}
                <div className="bg-neutral-50 border-b border-neutral-200 px-2 py-1.5 flex items-center gap-1" data-id="element-12461" data-mp="1agl8cgv2dlcb">
                  <button className="p-1.5 text-neutral-500 hover:bg-neutral-200 rounded" data-id="element-12462" data-mp="1femsz6851spw">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-12463" data-mp="1dxqtekvsykyo">
                      
                      <path d="M6 4h8a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z" data-id="element-12464" data-mp="1myp2hhl3lio4"></path>
                      <path d="M6 12h9a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z" data-id="element-12465" data-mp="1mwm1k2ieq54m"></path>
                    </svg>
                  </button>
                  <button className="p-1.5 text-neutral-500 hover:bg-neutral-200 rounded" data-id="element-12466" data-mp="1wwl9bx705rgf">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-12467" data-mp="1rybc7h1pwgs6">
                      
                      <line x1="19" y1="4" x2="10" y2="4" data-id="element-12468" data-mp="21nuvgntcecnk"></line>
                      <line x1="14" y1="20" x2="5" y2="20" data-id="element-12469" data-mp="21omts6ebqohj"></line>
                      <line x1="15" y1="4" x2="9" y2="20" data-id="element-12470" data-mp="2fmujpucy0pj7"></line>
                    </svg>
                  </button>
                  <button className="p-1.5 text-neutral-500 hover:bg-neutral-200 rounded" data-id="element-12471" data-mp="2apudnjg3fms3">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-12472" data-mp="2ubcpvbht5mxl">
                      
                      <path d="M6 3v7a6 6 0 0 0 6 6 6 6 0 0 0 6-6V3" data-id="element-12473" data-mp="2l8ldn9h3yn4w"></path>
                      <line x1="4" y1="21" x2="20" y2="21" data-id="element-12474" data-mp="2payjryzron27"></line>
                    </svg>
                  </button>
                  <div className="w-px h-4 bg-neutral-300 mx-1" data-id="element-12475" data-mp="2bjjx9i6ni3qe" />
                  <button className="p-1.5 text-neutral-500 hover:bg-neutral-200 rounded" data-id="element-12476" data-mp="2gajukojomntf">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-12477" data-mp="2gcd5wh58pw7r">
                      
                      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" data-id="element-12478" data-mp="26inytk52j9sd"></path>
                      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" data-id="element-12479" data-mp="1sn291ar0glyw"></path>
                    </svg>
                  </button>
                  <button className="p-1.5 text-neutral-500 hover:bg-neutral-200 rounded" data-id="element-12480" data-mp="343ukka97sx6">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-12481" data-mp="3ui9iafbxs476">
                      
                      <line x1="8" y1="6" x2="21" y2="6" data-id="element-12482" data-mp="3liczuvg541kk"></line>
                      <line x1="8" y1="12" x2="21" y2="12" data-id="element-12483" data-mp="3lfqk48j3ifr9"></line>
                      <line x1="8" y1="18" x2="21" y2="18" data-id="element-12484" data-mp="m1erhy8eya9g"></line>
                      <line x1="3" y1="6" x2="3.01" y2="6" data-id="element-12485" data-mp="h33ghk6dttpn"></line>
                      <line x1="3" y1="12" x2="3.01" y2="12" data-id="element-12486" data-mp="h1ioa17jimuy"></line>
                      <line x1="3" y1="18" x2="3.01" y2="18" data-id="element-12487" data-mp="d0ovatf8qhv7"></line>
                    </svg>
                  </button>
                </div>

                {/* Input Area */}
                <div className="p-3 min-h-[80px] bg-white" data-id="element-12488" data-mp="2xsineezqgf0u">
                  <p className="text-neutral-400 text-[15px]" data-id="element-12489" data-mp="2xpw81t6lov82">Message Giga</p>
                </div>

                {/* Bottom Actions */}
                <div className="bg-white px-2 pb-2 flex items-center justify-between" data-id="element-12490" data-mp="18bwmlphpba6d">
                  <div className="flex items-center gap-1" data-id="element-12491" data-mp="1d9ooncc28coa">
                    <button className="p-1.5 text-neutral-500 hover:bg-neutral-100 rounded-full" data-id="element-12492" data-mp="1d83uhntnqvqy">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-12493" data-mp="1h9p1wtnfahtr">
                        
                        <line x1="12" y1="5" x2="12" y2="19" data-id="element-12494" data-mp="1r3pdned7mlbg"></line>
                        <line x1="5" y1="12" x2="19" y2="12" data-id="element-12495" data-mp="1vzmqvbt6fbk0"></line>
                      </svg>
                    </button>
                    <button className="p-1.5 text-neutral-500 hover:bg-neutral-100 rounded-full" data-id="element-12496" data-mp="210k9tqm0g9mk">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-12497" data-mp="250l0c9mdwb9z">
                        
                        <circle cx="12" cy="12" r="10" data-id="element-12498" data-mp="anv43l8jmpzf"></circle>
                        <path d="M8 14s1.5 2 4 2 4-2 4-2" data-id="element-12499" data-mp="3nvp758flwon3"></path>
                        <line x1="9" y1="9" x2="9.01" y2="9" data-id="element-12500" data-mp="3nu5qvhtyadlb"></line>
                        <line x1="15" y1="9" x2="15.01" y2="9" data-id="element-12501" data-mp="3iww1u3ah43y0"></line>
                      </svg>
                    </button>
                    <button className="p-1.5 text-neutral-500 hover:bg-neutral-100 rounded-full" data-id="element-12502" data-mp="l92jnbty2qq">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-12503" data-mp="3spl2020kpve1">
                        
                        <circle cx="12" cy="12" r="4" data-id="element-12504" data-mp="300mjh3sjgr87"></circle>
                        <path d="M16 8v5a3 3 0 0 0 6 0v-1a10 10 0 1 0-3.92 7.94" data-id="element-12505" data-mp="3dzdv8jjxi0t6"></path>
                      </svg>
                    </button>
                  </div>
                  <div className="flex items-center gap-1" data-id="element-12506" data-mp="t7tt4lkheat8">
                    <button className="p-1.5 text-neutral-400 hover:text-neutral-600 rounded" data-id="element-12507" data-mp="t690j1hq91y0">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-12508" data-mp="o8qzdx9ybr0k">
                        
                        <line x1="22" y1="2" x2="11" y2="13" data-id="element-12509" data-mp="10t5z5honb0vz"></line>
                        <polygon points="22 2 15 22 11 13 2 9 22 2" data-id="element-12510" data-mp="msmyivmiex3u"></polygon>
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>;
  }
  // Step 10: Welcome back (signed out / sign in to another workspace)
  if (step === 10) {
    return <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="relative min-h-screen bg-[#FAF8F4] font-body flex flex-col items-center px-6 overflow-hidden" data-id="element-12511" data-mp="1khews78yjz2v">
        
        {/* Brand cream surface with a soft warm bloom so it stays consistent
                                                                  with the rest of the auth flow (the "Try Giga free" screen). The
                                                                  orange primary CTA reads boldly against the cream. */}
        <div aria-hidden="true" className="pointer-events-none absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[760px] h-[760px] rounded-full bg-[#EA580C]/[0.07] blur-[160px]" data-id="element-12512" data-mp="1n16updcnz3j4" />
        
        <div aria-hidden="true" className="pointer-events-none absolute -bottom-40 -right-32 w-[520px] h-[520px] rounded-full bg-[#EA580C]/[0.05] blur-[150px]" data-id="element-12513" data-mp="1x0ecqducfmck" />
        

        {/* Return home */}
        <Link to="/" className="absolute z-20 top-6 left-6 flex items-center gap-2 text-sm font-medium text-neutral-500 hover:text-[#2B211A] transition-colors" data-id="element-12514" data-mp="14cs4gsa67tjd">
          
          <ArrowLeft size={16} data-id="element-12515" data-mp="1dauetyfsnyjb" />
          Back to home
        </Link>

        <div className="relative z-10 w-full max-w-md flex-1 flex flex-col items-center justify-center text-center" data-id="element-12516" data-mp="pm2v7vg9soi5">
          {/* Logo */}
          <div className="w-16 h-16 rounded-2xl bg-[#F97316] flex items-center justify-center shadow-lg shadow-[#F97316]/20 mb-8" data-id="element-12517" data-mp="ljy2cyxnbvwk">
            <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="Giga" className="w-9 h-9 object-contain [filter:brightness(0)_invert(1)]" data-id="element-12518" data-mp="bey46nqltoe7" />
            
          </div>

          <h1 className="text-4xl md:text-5xl font-title font-bold text-[#2B211A] tracking-tight mb-4" data-id="element-12519" data-mp="6i7mjvz5ikqo">
            {signedOut ? 'Sign in to Giga' : 'Welcome back'}
          </h1>

          <p className="text-sm text-neutral-500 mb-10" data-id="element-12520" data-mp="z5bvgc1d7j7q">
            {signedOut ? "You've been signed out of all workspaces." : 'Pick up right where you left off.'}
          </p>

          <div className="w-full space-y-3" data-id="element-12521" data-mp="p7ebw1ugaalg">
            <button onClick={() => navigate('/app-screen-mockups/dashboard')} className="w-full flex items-center justify-center gap-3 py-3.5 px-4 rounded-xl bg-[#EA580C] hover:bg-[#d24e09] shadow-lg shadow-[#EA580C]/20 transition-all" data-id="element-12522" data-mp="p6cqb3ft65ph">
              
              <SlackIcon size={20} data-id="element-12523" data-mp="1gfz2s8m6c9nt" />
              <span className="font-semibold text-white" data-id="element-12524" data-mp="1gf60pky5u8c0">
                Continue with Slack
              </span>
            </button>
            <button onClick={() => navigate('/app-screen-mockups/dashboard')} className="w-full flex items-center justify-center gap-3 py-3.5 px-4 rounded-xl border border-[#EAE4D9] bg-white transition-all hover:border-[#D4D0C8] hover:shadow-lg hover:shadow-black/5" data-id="element-12525" data-mp="16pwa69iysxq0">
              <GoogleIcon size={20} data-id="element-12526" data-mp="wt1gq51jtnil" />
              <span className="font-semibold text-[#2B211A]" data-id="element-12527" data-mp="sqd2tixz9qer">
                Continue with Google
              </span>
            </button>
            {teamsWaitlistDone ? <div className="w-full flex items-center justify-center gap-2.5 py-3.5 px-4 rounded-xl bg-[#EA580C]/[0.08] border border-[#EA580C]/20 text-[#2B211A]" data-id="element-12528" data-mp="nra9gvrd18mm">
                <Check size={18} className="text-[#EA580C]" strokeWidth={2.5} data-id="element-12529" data-mp="2i403ddhqahu0" />
                <span className="text-sm font-medium" data-id="element-12530" data-mp="2d5gilgevfc38">
                  You're on the Microsoft Teams waitlist — we'll email you when
                  it's ready.
                </span>
              </div> : teamsWaitlistOpen ? <form onSubmit={submitTeamsWaitlist} className="w-full flex flex-col sm:flex-row gap-2" data-id="element-12531" data-mp="2pnite3pe3bc3">
              
                <label htmlFor="teams-waitlist-email" className="sr-only" data-id="element-12532" data-mp="2ufsri70nrhpg">
                  Work email for the Microsoft Teams waitlist
                </label>
                <input id="teams-waitlist-email" type="email" required autoFocus value={teamsWaitlistEmail} onChange={e => setTeamsWaitlistEmail(e.target.value)} placeholder="you@company.com" className="flex-1 py-3.5 px-4 rounded-xl bg-white border border-neutral-200 text-[#2B211A] placeholder:text-neutral-400 shadow-sm focus:outline-none focus:border-[#EA580C] focus:ring-2 focus:ring-[#EA580C]/30 transition-all" data-id="element-12533" data-mp="21r4zztpbfw3g" />
              
                <button type="submit" className="shrink-0 flex items-center justify-center gap-2 py-3.5 px-5 rounded-xl bg-[#EA580C] hover:bg-[#d24e09] text-white font-semibold shadow-lg shadow-[#EA580C]/20 transition-all" data-id="element-12534" data-mp="3n36gniy478z0">
                
                  Notify me
                  <ArrowRight size={18} data-id="element-12535" data-mp="3lnavegy6jyh6" />
                </button>
              </form> : <button onClick={() => setTeamsWaitlistOpen(true)} className="w-full flex items-center justify-center gap-3 py-3.5 px-4 rounded-xl bg-white border border-neutral-200 text-[#2B211A] shadow-sm hover:bg-neutral-50 hover:border-neutral-300 transition-colors" data-id="element-12536" data-mp="3h66a297zpmw">
              
                <TeamsIcon size={20} data-id="element-12537" data-mp="cfhujxmm6bc6" />
                <span className="font-medium" data-id="element-12538" data-mp="8h2c871h9r53">
                  Join Microsoft Teams waitlist
                </span>
              </button>}
          </div>

          {/* Your Workspaces Section — hidden once the user signs out of all
                                                              workspaces, leaving a clean sign-in with just the providers above. */}
          {!signedOut && <div className="w-full mt-10" data-id="element-12539" data-mp="r70ichlpta63">
              <div className="flex items-center gap-4 mb-6" data-id="element-12540" data-mp="r81s8iqzvqm8">
                <div className="h-px bg-neutral-200 flex-1" data-id="element-12541" data-mp="1as0qiquizrt4" />
                <span className="text-[11px] font-bold tracking-wider text-neutral-400 uppercase" data-id="element-12542" data-mp="1aqfv6z0e04u7">
                  Your Workspaces
                </span>
                <div className="h-px bg-neutral-200 flex-1" data-id="element-12543" data-mp="10tb6osj2jxa8" />
              </div>

              <button onClick={() => navigate('/app-screen-mockups/dashboard')} className="w-full flex items-center justify-between p-4 rounded-xl border border-neutral-200 hover:border-[#EA580C] hover:bg-white transition-all group text-left bg-white shadow-sm" data-id="element-12544" data-mp="15sxv7y4ckzyc">
              
                <div className="flex items-center gap-4" data-id="element-12545" data-mp="i3v6657dw4tt">
                  <div className="w-10 h-10 rounded-lg bg-[#2B211A] border border-black/10 text-white flex items-center justify-center font-bold text-sm" data-id="element-12546" data-mp="i5fzjrhyphq3">
                    FT
                  </div>
                  <div className="flex flex-col" data-id="element-12547" data-mp="868ml5f53p3i">
                    <span className="font-semibold text-[#2B211A]" data-id="element-12548" data-mp="d4ig7bl7uhn0">
                      Faith Test
                    </span>
                    <span className="text-sm text-neutral-500" data-id="element-12549" data-mp="3q6maman7zl60">
                      alqahtani.faith03@gmail.com
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-3" data-id="element-12550" data-mp="3geygu336xs1d">
                  <Check size={20} className="text-[#EA580C]" strokeWidth={2.5} data-id="element-12551" data-mp="3gdey85u2b4wf" />
                
                  <ArrowRight size={20} className="text-neutral-400 group-hover:text-[#EA580C] transition-colors" data-id="element-12552" data-mp="3betw20v92b5c" />
                
                </div>
              </button>

              <button onClick={() => setSignedOut(true)} className="mt-6 text-sm text-neutral-500 hover:text-[#2B211A] transition-colors" data-id="element-12553" data-mp="80ptb9fizrg2">
              
                Sign out of all workspaces
              </button>
            </div>}

          <p className="mt-10 text-sm text-neutral-500" data-id="element-12554" data-mp="2nr2i6su29q1y">
            No account?{' '}
            <button onClick={() => {
            // Send the user to the free-trial start ("Try Giga free")
            // screen — step 1 is the default signup entry.
            setSignedOut(false);
            setStep(1);
            navigate('/app-screen-mockups/signup');
          }} className="font-bold text-[#2B211A] underline hover:text-[#EA580C] transition-colors" data-id="element-12555" data-mp="2nopwq5p3gxk5">
              
              Sign up
            </button>
          </p>
        </div>

        <p className="relative z-10 max-w-md text-center text-xs text-neutral-400 pb-8" data-id="element-12556" data-mp="1d0foivlkb6z3">
          By logging in, you agree to the Giga{' '}
          <a href="#" className="underline hover:text-[#2B211A] transition-colors" data-id="element-12557" data-mp="1lzj8s2wgtsf6">
            
            Privacy Policy
          </a>{' '}
          and{' '}
          <a href="#" className="underline hover:text-[#2B211A] transition-colors" data-id="element-12558" data-mp="1qz5ku90k52me">
            
            Terms of Service
          </a>
          .
        </p>
      </motion.div>;
  }
  // Step 11: Trial Paywall
  if (step === 11) {
    const plans = [{
      id: 'plus',
      name: 'Plus',
      desc: 'The ultimate AI work assistant.',
      price: '$49.99/mo'
    }, {
      id: 'pro',
      name: 'Pro',
      desc: 'More usage for power users.',
      price: '$99.99/mo',
      popular: true
    }];
    const selectedPrice = plans.find(p => p.id === plan)?.price.split('/')[0] || '$49.99';
    const futureDate = new Date(Date.now() + 7 * 864e5).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
    const selectedPlan = plans.find(p => p.id === plan) || plans[0];
    return <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="relative min-h-screen w-full bg-white font-body" data-id="element-12559" data-mp="1kim86k8hf99t">
        
        {/* Real Stripe Checkout anatomy: full-height split, dark merchant
         summary rail on the left, white payment pane on the right. */}
        <div className="mx-auto grid min-h-screen w-full grid-cols-1 lg:grid-cols-2" data-id="element-12560" data-mp="35vgn0m26c3bw">
          {/* ── Left: dark order summary ─────────────────────────────────── */}
          <aside className="flex flex-col bg-[#1B130D] px-5 py-5 text-white sm:px-10 lg:py-16 lg:pl-[max(2.5rem,calc((100vw-1180px)/2+2.5rem))] lg:pr-16" data-id="element-12561" data-mp="30yq6jxmgj5py">
            <div className="flex flex-1 flex-col" data-id="element-12562" data-mp="3dg9bw3oii91o">
              {/* back + merchant (logo in a white circle, exactly like Stripe) */}
              <div className="flex items-center gap-3" data-id="element-12563" data-mp="2zhjfyrdfifew">
                <button type="button"
              // In the signup flow the paywall is reached from the tool
              // connections screen (step 7), not the sign-in screen
              // (step 10) — back should return to connections.
              onClick={() => setStep(7)} aria-label="Back" className="text-white/50 transition-colors hover:text-white" data-id="element-12564" data-mp="33ga6ugrax0tk">
                  
                  <ArrowLeft size={18} data-id="element-12565" data-mp="3mfe996oc7wc9" />
                </button>
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-white" data-id="element-12566" data-mp="3mdjobsd2g486">
                  <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="" aria-hidden="true" className="h-4 w-4 object-contain" data-id="element-12567" data-mp="dy9qiwg7ll67" />
                  
                </span>
                <span className="text-[15px] font-medium text-white" data-id="element-12568" data-mp="1o7vd5l6ie5is">Giga</span>
              </div>

              {/* headline price */}
              <div className="mt-6 lg:mt-12" data-id="element-12569" data-mp="1xz83278qylm6">
                <p className="text-[14px] text-white/60 lg:text-[15px]" data-id="element-12570" data-mp="1xxwqbhxbq3ix">
                  Subscribe to Giga {selectedPlan.name}
                </p>
                <div className="mt-1 flex items-start gap-2" data-id="element-12571" data-mp="15akeo2spbg4e">
                  <span className="text-[32px] font-bold leading-none tracking-tight lg:text-[44px]" data-id="element-12572" data-mp="15b2grg21ook8">
                    {selectedPrice}
                  </span>
                  <span className="mt-0.5 text-[13px] leading-tight text-white/60 lg:mt-1 lg:text-[15px]" data-id="element-12573" data-mp="1f906y93gsvfh">
                    per
                    <br data-id="element-12574" data-mp="2loaid8fws2di" />
                    month
                  </span>
                </div>
                <p className="mt-2 text-[13px] text-white/50 lg:mt-3 lg:text-[14px]" data-id="element-12575" data-mp="2qmc1ftdc41oz">
                  7-day free trial · US$0.00 due today
                </p>

                {/* plan segmented control */}
                <div className="mt-5 inline-flex items-center rounded-lg border border-white/15 p-0.5 text-[13px]" data-id="element-12576" data-mp="a28ot51arumf">
                  {plans.map(p => <button key={p.id} onClick={() => setPlan(p.id)} className={`flex items-center gap-1.5 rounded-[6px] px-3 py-1.5 font-medium transition-colors ${plan === p.id ? 'bg-white text-[#1B130D]' : 'text-white/60 hover:text-white'}`} data-id="element-12577" data-mp="bnguqzqhdqu">
                    
                      {p.name}
                      {p.popular && <span className={`rounded px-1 text-[10px] font-bold uppercase ${plan === p.id ? 'bg-[#EA580C] text-white' : 'text-[#EA580C]'}`} data-id="element-12578" data-mp="3igqqmp0nadxl">
                      
                          Popular
                        </span>}
                    </button>)}
                </div>
              </div>

              {/* mobile-only details disclosure */}
              <button type="button" onClick={() => setPayDetailsOpen(o => !o)} aria-expanded={payDetailsOpen} className="mt-4 flex items-center gap-1.5 self-start text-[13px] font-medium text-white/60 transition-colors hover:text-white lg:hidden" data-id="element-12579" data-mp="1bopwnju0dz9y">
                
                {payDetailsOpen ? 'Hide details' : 'View details'}
                <ChevronDown size={15} className={`transition-transform ${payDetailsOpen ? 'rotate-180' : ''}`} data-id="element-12580" data-mp="317of0ldvzcgy" />
                
              </button>

              {/* breakdown */}
              <div className={`${payDetailsOpen ? 'block' : 'hidden'} lg:block`} data-id="element-12581" data-mp="3twllzscndmue">
                <div className="mt-6 flex items-start justify-between gap-4 lg:mt-12" data-id="element-12582" data-mp="3tuiwrajbjxqq">
                  <div data-id="element-12583" data-mp="3fwkkcaok6bdv">
                    <p className="text-[15px] font-medium text-white" data-id="element-12584" data-mp="3jyflnf6hoorn">
                      Giga {selectedPlan.name}
                    </p>
                    <p className="mt-0.5 text-[13px] text-white/50" data-id="element-12585" data-mp="24gm6ltifr16b">
                      Billed monthly
                    </p>
                  </div>
                  <p className="text-[15px] font-medium text-white" data-id="element-12586" data-mp="20zrh8npc48a4">
                    {selectedPrice}
                  </p>
                </div>

                <div className="mt-6 flex items-center justify-between border-t border-white/10 pt-6" data-id="element-12587" data-mp="1vzvcgm8tccti">
                  <span className="text-[15px] font-medium text-white" data-id="element-12588" data-mp="2awmhbb19agqi">
                    Subtotal
                  </span>
                  <span className="text-[15px] font-medium text-white" data-id="element-12589" data-mp="25wrnozq3jd1r">
                    {selectedPrice}
                  </span>
                </div>

                <div className="mt-5" data-id="element-12590" data-mp="1daykip0w1weg">
                  <button type="button" className="rounded-lg bg-white/[0.07] px-4 py-2.5 text-[14px] font-medium text-white transition-colors hover:bg-white/10" data-id="element-12591" data-mp="1d82jgrtqurls">
                    
                    Add promotion code
                  </button>
                </div>

                <div className="mt-5 flex items-center justify-between border-t border-white/10 pt-5" data-id="element-12592" data-mp="zhhabuu3dw13">
                  <span className="text-[15px] font-medium text-white" data-id="element-12593" data-mp="uiw67kbpys7d">
                    Total due today
                  </span>
                  <span className="text-[15px] font-medium text-white" data-id="element-12594" data-mp="3tvvdyf2bbpj7">
                    US$0.00
                  </span>
                </div>
              </div>

              {/* footer pinned bottom — desktop only */}
              <div className="mt-auto hidden items-center gap-4 pt-14 text-[12px] text-white/40 lg:flex" data-id="element-12595" data-mp="bjfgvnmou95x">
                <span className="flex items-center gap-1.5" data-id="element-12596" data-mp="gjbkvmvdy0lh">
                  Powered by{' '}
                  <span className="font-bold text-white/70" data-id="element-12597" data-mp="lhdcwxasha93">stripe</span>
                </span>
                <span className="h-3 w-px bg-white/20" data-id="element-12598" data-mp="pjq9av3nbvt7" />
                <button type="button" className="hover:text-white/70" data-id="element-12599" data-mp="pk9mxzm9jy1f">
                  Terms
                </button>
                <button type="button" className="hover:text-white/70" data-id="element-12600" data-mp="ug75mcl01ghd">
                  Privacy
                </button>
              </div>
            </div>
          </aside>

          {/* ── Right: white payment form ─────────────────────────────────── */}
          <div className="px-5 py-8 sm:px-10 lg:py-16 lg:pl-16 lg:pr-[max(2.5rem,calc((100vw-1180px)/2+2.5rem))]" data-id="element-12601" data-mp="2or6xisanu6ap">
            <div className="mx-auto w-full max-w-md" data-id="element-12602" data-mp="2jyduq9yr5819">
              {/* Express checkout — wallets above the card form */}
              <div className="grid grid-cols-2 gap-3" data-id="element-12603" data-mp="2ey9e8jxugdb1">
                <button type="button" onClick={() => setStep(finalStep)} aria-label="Pay with Apple Pay" className="flex h-11 items-center justify-center gap-1 rounded-lg bg-black text-white transition-opacity hover:opacity-85" data-id="element-12604" data-mp="2a2twvi0mnz9v">
                  
                  <svg viewBox="0 0 384 512" className="h-[17px] w-[17px] fill-current" aria-hidden="true" data-id="element-12605" data-mp="2xr3f69ymu4w9">
                    
                    <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" data-id="element-12606" data-mp="2tq9qvfha2o2u" />
                  </svg>
                  <span className="text-[16px] font-medium" data-id="element-12607" data-mp="19tucholsdesx">Pay</span>
                </button>
                <button type="button" onClick={() => setStep(finalStep)} aria-label="Pay with Google Pay" className="flex h-11 items-center justify-center gap-2 rounded-lg border border-neutral-300 bg-white text-[#3c4043] transition-colors hover:bg-neutral-50" data-id="element-12608" data-mp="1erw26sdpwcdd">
                  
                  <img src="https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/google.svg" alt="" className="h-[18px] w-[18px]" data-id="element-12609" data-mp="r2usz2x28x71" />
                  
                  <span className="text-[15px] font-medium" data-id="element-12610" data-mp="w2fo12940leh">Pay</span>
                </button>
              </div>

              <div className="my-6 flex items-center gap-4 text-[14px] text-[#6B7280]" data-id="element-12611" data-mp="i2y4p2el20ci">
                <span className="h-px flex-1 bg-neutral-200" data-id="element-12612" data-mp="m389ibqn2kps" />
                Or pay with card
                <span className="h-px flex-1 bg-neutral-200" data-id="element-12613" data-mp="3fzgrhexpo2j" />
              </div>

              <div className="space-y-4" data-id="element-12614" data-mp="3un2aafi8w6fx">
                <div data-id="element-12615" data-mp="3uo3noqmqh50t">
                  <label className="block text-[13px] font-medium text-[#30313D]" data-id="element-12616" data-mp="cfvy0xyzvknk">
                    Email
                  </label>
                  <input type="email" placeholder="faith@company.com" className="mt-1.5 w-full rounded-lg border border-neutral-300 px-3 py-2.5 text-[15px] text-[#30313D] outline-none transition-shadow placeholder:text-neutral-400 focus:border-[#EA580C] focus:ring-2 focus:ring-[#EA580C]/30" data-id="element-12617" data-mp="7g9ph1u42sl3" />
                  
                </div>

                <div data-id="element-12618" data-mp="v8geth1f5zb0">
                  <label className="block text-[13px] font-medium text-[#30313D]" data-id="element-12619" data-mp="r5ad8mp94wav">
                    Card information
                  </label>
                  <div className="mt-1.5 overflow-hidden rounded-lg border border-neutral-300 focus-within:border-[#EA580C] focus-within:ring-2 focus-within:ring-[#EA580C]/30" data-id="element-12620" data-mp="155bbrvi1nrn6">
                    <div className="flex items-center border-b border-neutral-200" data-id="element-12621" data-mp="27h4c6qj5tbxc">
                      <input placeholder="1234 1234 1234 1234" inputMode="numeric" className="w-full bg-transparent px-3 py-2.5 text-[15px] text-[#30313D] outline-none placeholder:text-neutral-400" data-id="element-12622" data-mp="2bj7zqdj3s6yz" />
                      
                      <div className="flex items-center gap-1 pr-3" aria-hidden="true" data-id="element-12623" data-mp="2lgm61kbbbfdk">
                        
                        {['visa', 'mastercard', 'amex', 'discover'].map(brand => <img key={brand} src={`https://cdn.jsdelivr.net/gh/aaronfagan/svg-credit-card-payment-icons/flat/${brand}.svg`} alt="" className="h-4 w-auto rounded-[2px]" data-id="element-12624" data-mp="1ssqbal5azxjf" />)}
                      </div>
                    </div>
                    <div className="flex" data-id="element-12625" data-mp="22junxe7ib6b3">
                      <input placeholder="MM / YY" className="w-1/2 bg-transparent px-3 py-2.5 text-[15px] text-[#30313D] outline-none placeholder:text-neutral-400" data-id="element-12626" data-mp="153ox0h9shs68" />
                      
                      <input placeholder="CVC" inputMode="numeric" className="w-1/2 border-l border-neutral-200 bg-transparent px-3 py-2.5 text-[15px] text-[#30313D] outline-none placeholder:text-neutral-400" data-id="element-12627" data-mp="1a0fffb92gzut" />
                      
                    </div>
                  </div>
                </div>

                <div data-id="element-12628" data-mp="nm08zkjg0cok">
                  <label className="block text-[13px] font-medium text-[#30313D]" data-id="element-12629" data-mp="nmi3abmjogtu">
                    Name on card
                  </label>
                  <input placeholder="Faith Anderson" className="mt-1.5 w-full rounded-lg border border-neutral-300 px-3 py-2.5 text-[15px] text-[#30313D] outline-none transition-shadow placeholder:text-neutral-400 focus:border-[#EA580C] focus:ring-2 focus:ring-[#EA580C]/30" data-id="element-12630" data-mp="jly472iev352" />
                  
                </div>

                <div data-id="element-12631" data-mp="4te7okprntwl">
                  <label className="block text-[13px] font-medium text-[#30313D]" data-id="element-12632" data-mp="3w1jyq9kwncyv">
                    Country or region
                  </label>
                  <div className="mt-1.5 overflow-hidden rounded-lg border border-neutral-300 focus-within:border-[#EA580C] focus-within:ring-2 focus-within:ring-[#EA580C]/30" data-id="element-12633" data-mp="3s1hxg1j30lk4">
                    <div className="relative border-b border-neutral-200" data-id="element-12634" data-mp="2bxf8419h29d5">
                      <select defaultValue="United States" className="w-full appearance-none bg-transparent px-3 py-2.5 pr-9 text-[15px] text-[#30313D] outline-none" data-id="element-12635" data-mp="27vtsi85jzgz9">
                        
                        <option data-id="element-12636" data-mp="1xxw67q797u9a">United States</option>
                        <option data-id="element-12637" data-mp="1xzgwv4qg6t1v">Canada</option>
                        <option data-id="element-12638" data-mp="1t7fr81nsc0kt">United Kingdom</option>
                      </select>
                      <ChevronDown size={16} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400" data-id="element-12639" data-mp="1t5ctevge535j" />
                      
                    </div>
                    <input placeholder="ZIP" className="w-full bg-transparent px-3 py-2.5 text-[15px] text-[#30313D] outline-none placeholder:text-neutral-400" data-id="element-12640" data-mp="1k4elw36qudi7" />
                    
                  </div>
                </div>
              </div>

              <button onClick={() => setStep(finalStep)} className="mt-6 w-full rounded-lg bg-[#EA580C] py-3 text-[15px] font-semibold text-white transition-colors hover:bg-[#d24e08]" data-id="element-12641" data-mp="3lwge8ft7205u">
                
                Subscribe
              </button>

              <p className="mt-4 text-center text-[12px] leading-relaxed text-[#8792A2]" data-id="element-12642" data-mp="3vrqdwww4dvgg">
                By confirming your subscription, you allow Giga to charge your
                card for this payment and future payments in accordance with
                their terms. Your 7-day trial is free — you won't be charged
                until {futureDate}, and you can cancel anytime.
              </p>

              {/* mobile-only Stripe footer */}
              <div className="mt-8 flex items-center justify-center gap-4 text-[12px] text-neutral-400 lg:hidden" data-id="element-12643" data-mp="3gxlwziavkjnu">
                <span className="flex items-center gap-1.5" data-id="element-12644" data-mp="2kdwc9ky1uqk9">
                  Powered by{' '}
                  <span className="font-bold text-neutral-500" data-id="element-12645" data-mp="2oeyfsdz01os1">stripe</span>
                </span>
                <span className="h-3 w-px bg-neutral-200" data-id="element-12646" data-mp="3rpvrufcmzuou" />
                <button type="button" className="hover:text-neutral-600" data-id="element-12647" data-mp="3msdfsg3bozcm">
                  Terms
                </button>
                <button type="button" className="hover:text-neutral-600" data-id="element-12648" data-mp="5bvpjiqms9oh">
                  Privacy
                </button>
              </div>
            </div>
          </div>
        </div>
      </motion.div>;
  }
  // Step 1: Landing
  const LANDING_TESTIMONIALS = [{
    quote: "Nothing short of revolutionary. The best attempt I've ever had as a CEO to turn every employee into an AI-first operator.",
    name: 'Tony Ennis',
    title: 'CEO, Brightwave',
    image: "https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/image_14.png"
  }, {
    quote: "Giga has completely transformed how our engineering team accesses context. It's like having a senior engineer who knows everything.",
    name: 'Sarah Chen',
    title: 'VP Engineering, Northwind',
    image: 'https://i.pravatar.cc/150?u=sarah'
  }, {
    quote: 'The seamless integration with Slack means my team actually uses it. No new tabs, just instant answers where we already work.',
    name: 'Marcus Lee',
    title: 'Product Lead, Acme Corp',
    image: 'https://i.pravatar.cc/150?u=marcus'
  }];
  return <div className="min-h-screen flex font-body bg-white" data-id="element-12649" data-mp="vnpb1hqm9yun">
      {/* Teams isn't live — the hover state on that button opens this. */}
      <AnimatePresence data-id="element-12650" data-mp="1oatiruh3gra4">
        {showWaitlist && <WaitlistModal referredBy={new URLSearchParams(location.search).get('ref')?.trim().toLowerCase()} onClose={() => setShowWaitlist(false)} data-id="element-12651" data-mp="1o9quzvf2j4yf" />}
      </AnimatePresence>

      {/* Left Pane - Product Scenery */}
      <div className="hidden lg:flex w-1/2 bg-[#2B211A] border-r border-neutral-800 relative overflow-hidden flex-col p-12" data-id="element-12652" data-mp="1einq0ij0q5tv">
        {/* Shared dark-green grid + subtle glow */}
        <DarkGreenTexture glow="top-left" data-id="element-12653" data-mp="271jk3er0o60v" />

        <Link to="/" aria-label="Giga home" className="relative z-10 flex items-center gap-2 mb-20 md:mb-24" data-id="element-12654" data-mp="jcggga3eyrze">
          
          <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="" aria-hidden="true" className="h-7 w-7 object-contain" data-id="element-12655" data-mp="ectyxogjypks" />
          
          <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/giga4x.png" alt="Giga" className="h-6 object-contain [filter:brightness(0)_invert(1)]" data-id="element-12656" data-mp="4gqq5zr73sov" />
          
        </Link>

        {/* Product story — auto-cycling feature showcase (Integrations,
         Smart Memory, Autonomous work) */}
        <div className="relative z-10 flex flex-col items-center w-full" data-id="element-12657" data-mp="nda2exxapwpn">
          <SignupShowcase compactCounter counterPosition="bottom" headlinePosition="bottom" data-id="element-12658" data-mp="1ktyysc3h86rn" />
          
          {false && <motion.div initial="hidden" animate="show" variants={{
          hidden: {},
          show: {
            transition: {
              staggerChildren: 0.12,
              delayChildren: 0.1
            }
          }
        }} className="w-full max-w-sm flex flex-col items-center gap-6" data-id="element-12659" data-mp="1g1o0tesaqv1q">
            
              {/* Speed chip */}
              <motion.div variants={{
            hidden: {
              opacity: 0,
              y: -8
            },
            show: {
              opacity: 1,
              y: 0
            }
          }} className="inline-flex items-center gap-2 rounded-full bg-[#F97316] px-4 py-1.5 text-[#2B211A] text-xs font-bold shadow-lg" data-id="element-12660" data-mp="1xee40c0xw8ug">
              
                <motion.span animate={{
              scale: [1, 1.3, 1],
              opacity: [1, 0.6, 1]
            }} transition={{
              duration: 1.4,
              repeat: Infinity
            }} className="w-2 h-2 rounded-full bg-[#2B211A]" data-id="element-12661" data-mp="2g3u7n87x8hf3" />
              
                Live in under 60 seconds
              </motion.div>

              {/* Setup checklist — ticks off fast */}
              <motion.div variants={{
            hidden: {
              opacity: 0,
              y: 12
            },
            show: {
              opacity: 1,
              y: 0
            }
          }} className="w-full bg-white border border-neutral-200 rounded-2xl p-5 shadow-xl shadow-black/5 space-y-3" data-id="element-12662" data-mp="25cfu8rqucqum">
              
                {[{
              label: 'Connected to Slack',
              activeLabel: 'Connecting to Slack…',
              icon: 'slack'
            }, {
              label: 'Synced your tools',
              activeLabel: 'Syncing your tools…',
              tools: true
            }, {
              label: 'Giga is live',
              activeLabel: 'Booting Giga…',
              giga: true
            }].map((item, i) => {
              const isDone = setupStep > i;
              const isActive = setupStep === i;
              return <motion.div key={item.label} variants={{
                hidden: {
                  opacity: 0,
                  x: -10
                },
                show: {
                  opacity: 1,
                  x: 0
                }
              }} className={`relative flex items-center justify-between gap-3 rounded-xl px-2 py-1 transition-colors ${item.giga && isDone ? 'bg-[#F97316]/10' : ''}`} data-id="element-12663" data-mp="ynhbvbe6vqng" data-mpi={i}>
                    
                      {/* Lime glow payoff on the final 'Giga is live' row */}
                      {item.giga && isDone && <motion.span aria-hidden="true" initial={{
                  opacity: 0
                }} animate={{
                  opacity: [0, 1, 0.5]
                }} transition={{
                  duration: 0.8
                }} className="pointer-events-none absolute inset-0 rounded-xl ring-1 ring-[#F97316]/50" data-id="element-12664" data-mp="2riibx6ag8v2d" />}
                      <div className="relative flex items-center gap-3" data-id="element-12665" data-mp="fuiy8qtfjmkn">
                        <div className="w-6 h-6 shrink-0 flex items-center justify-center" data-id="element-12666" data-mp="fvbzxddj7lvx">
                          <AnimatePresence mode="wait" data-id="element-12667" data-mp="ksu7p0ftbv2c">
                            {isDone ? <motion.div key="done" initial={{
                        scale: 0
                      }} animate={{
                        scale: 1
                      }} transition={{
                        type: 'spring',
                        stiffness: 500,
                        damping: 18
                      }} className="w-6 h-6 rounded-full bg-[#F97316] flex items-center justify-center" data-id="element-12668" data-mp="33r3lj9l7r2e1">
                            
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#2B211A" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-12669" data-mp="2kzauvltm9t9h">
                              
                                  <path d="M20 6L9 17l-5-5" data-id="element-12670" data-mp="271vzm0gomr9l" />
                                </svg>
                              </motion.div> : isActive ? <motion.div key="active" className="w-5 h-5 rounded-full border-2 border-neutral-200 border-t-[#EA580C] animate-spin" data-id="element-12671" data-mp="aa5hockchdzj" /> : <motion.div key="pending" className="w-5 h-5 rounded-full border-2 border-neutral-200" data-id="element-12672" data-mp="5buak9lfphl0" />}
                          </AnimatePresence>
                        </div>
                        <span className={`font-medium text-sm transition-colors ${isDone ? 'text-neutral-700' : isActive ? 'text-neutral-500' : 'text-neutral-300'}`} data-id="element-12673" data-mp="hs3fhjt5z0mx">
                        
                          {isActive ? item.activeLabel : item.label}
                        </span>
                      </div>

                      {/* Right-side badges fade in once the row completes */}
                      <AnimatePresence data-id="element-12674" data-mp="3v0q14nmvlmgl">
                        {isDone && item.icon === 'slack' && <motion.div initial={{
                    opacity: 0,
                    scale: 0.8
                  }} animate={{
                    opacity: 1,
                    scale: 1
                  }} className="w-7 h-7 rounded-lg bg-white flex items-center justify-center shadow-sm border border-neutral-100" data-id="element-12675" data-mp="3suazhebluys">
                        
                            <SlackIcon size={16} data-id="element-12676" data-mp="1jc772vvutxmy" />
                          </motion.div>}

                        {isDone && item.tools && <div className="flex -space-x-1.5" data-id="element-12677" data-mp="2ae9t7ekak663">
                            {['notion', 'linear', 'google-drive', 'figma'].map((slug, j) => <motion.div key={slug} initial={{
                      opacity: 0,
                      scale: 0.8
                    }} animate={{
                      opacity: 1,
                      scale: 1
                    }} transition={{
                      delay: j * 0.06
                    }} className="w-7 h-7 rounded-full bg-white border-2 border-white shadow-sm flex items-center justify-center overflow-hidden" data-id="element-12678" data-mp="20mnjjmnlqf5g" data-mpi={j}>
                            
                                  <img src={`https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/${slug}.svg`} alt={slug} className="w-4 h-4 object-contain" data-id="element-12679" data-mp="1mm2zkn5yadad" />
                            
                                </motion.div>)}
                          </div>}
                      </AnimatePresence>
                    </motion.div>;
            })}
              </motion.div>
            </motion.div>}
        </div>

        {/* Social proof — anchored to the bottom of the brand pane so the
         form column stays focused on the CTAs and workspace list. */}
        <div className="relative z-10 mt-auto flex items-center gap-4 pt-10" data-id="element-12680" data-mp="1f44t6rj2fibd">
          <div className="flex -space-x-2.5" data-id="element-12681" data-mp="1abbno1frw5y8">
            {["https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Frame_26.png", "https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Frame_25.png", "https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Frame_24.png", "https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Frame_23.png"].map((src, i) => <img key={i} src={src} alt="" aria-hidden="true" className="w-8 h-8 rounded-full object-cover border-2 border-[#2B211A] shadow-sm relative" style={{
            zIndex: 4 - i
          }} data-id="element-12682" data-mp="1yqtexwbfp8hn" data-mpi={i} />)}
          </div>
          <div className="flex flex-col gap-0.5" data-id="element-12683" data-mp="27qzuroulukjz">
            <div className="flex items-center gap-0.5" aria-hidden="true" data-id="element-12684" data-mp="27tm4nv4yxc5d">
              {[0, 1, 2, 3, 4].map(i => <Star key={i} className="w-3 h-3 fill-[#F59E0B] text-[#F59E0B]" data-id="element-12685" data-mp="2cohy8zpus7kl" />)}
            </div>
            <span className="text-[11px] font-bold uppercase tracking-wider text-white/60" data-id="element-12686" data-mp="2hnl1qfq6q5qg">
              Loved by early adopters
            </span>
          </div>
        </div>
      </div>

      {/* Right Pane - Signup Form */}
      <div className="w-full lg:w-1/2 max-lg:min-h-screen flex flex-col items-center justify-start pt-0 lg:p-12 relative bg-[#FAF8F4] overflow-hidden" data-id="element-12687" data-mp="2vguace6b6n7x">
        {/* Mobile-only brand texture — a square-dot halftone field that fades in
         at the top and bottom edges and clears through the middle so the form
         stays clean. Hidden on desktop, where the left pane carries the brand. */}
        <div aria-hidden="true" className="lg:hidden dot-matrix z-0" style={{
        WebkitMaskImage: 'linear-gradient(to bottom, black 0%, transparent 28%, transparent 72%, black 100%)',
        maskImage: 'linear-gradient(to bottom, black 0%, transparent 28%, transparent 72%, black 100%)'
      }} data-id="element-12688" data-mp="3akdirsil0i2t" />
        

        {/* Mobile-only brand lockup — pinned to the very top-left of the screen.
         On desktop (lg+) the left pane already shows the Giga logo. */}
        <Link to="/" aria-label="Giga home" className="flex lg:hidden items-center gap-2 absolute top-6 left-6 z-20" data-id="element-12689" data-mp="3tcoft5qk1lt0">
          
          <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="" aria-hidden="true" className="w-8 h-8 object-contain" data-id="element-12690" data-mp="3kijhk193d83g" />
          
          <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/giga4x.png" alt="Giga" className="h-5 object-contain" data-id="element-12691" data-mp="g39yys75r49l" />
          
        </Link>

        {/* Small top spacer on desktop — the pane now carries the workspaces
         list too, so the content block starts higher to fit evenly. */}
        <div className="hidden lg:block h-4 mb-4" aria-hidden="true" data-id="element-12692" data-mp="3rrl9xb19od9k" />

        <div className="relative z-10 w-full max-w-[400px] flex flex-col flex-1 lg:flex-none justify-center lg:justify-start px-6 md:px-8 lg:px-0 pt-16 lg:pt-0 pb-5 lg:pb-0" data-id="element-12693" data-mp="jctdtr9ud0mw">
          {/* The eyebrow and the separate offer badge are gone — three stacked
           labels above the headline was the source of the crowding. The offer
           now lives in the headline itself, where it gets real weight, with
           the amount in orange so it still reads at a glance. */}
          <h1 className="font-title font-medium text-2xl md:text-4xl tracking-tight text-[#2B211A] mb-3 text-center lg:text-left" data-id="element-12694" data-mp="129kt7pm9ivlo">
            {isLogin ? <>
                Your entire work stack,
                <br data-id="element-12695" data-mp="2rq6ndw9sh276" />
                one chat away.
              </> : <>
                Try Giga free, with
                <br data-id="element-12696" data-mp="31iclzoujqhvc" />
                <span className="text-[#EA580C]" data-id="element-12697" data-mp="31im3sp5ftipy">$100 in credits</span> included.
              </>}
          </h1>

          {/* Short product line rather than the old three-clause sentence.
           The headline now carries the offer, so this only has to say what
           Giga is — and the task feed beside it already demonstrates the
           "connect, ask, act" story better than a sentence can. */}
          <p className="text-[15px] md:text-base text-neutral-500 leading-relaxed mb-6 text-center lg:text-left" data-id="element-12698" data-mp="2is4obm9m8jmj">
            Every tool your company runs on, one brain.
          </p>

          {/* Mobile-only notification illustration. The avatar/star social
           proof that used to sit here was removed — on a screen whose only job
           is "pick an auth method", it was a third competing block above the
           fold. Desktop still carries it in the left brand pane. */}
          <div className="lg:hidden w-full flex justify-center mb-5" data-id="element-12699" data-mp="54apa1id4cco">
            <SignupShowcase variant="light" counterPosition="bottom" compactCounter data-id="element-12700" data-mp="56n4i6wqk8m1" />
          </div>

          <div className="flex flex-col gap-3" data-id="element-12701" data-mp="f2zyl5w70ohb">
            <motion.button whileHover={{
            scale: 1.01,
            y: -1
          }} whileTap={{
            scale: 0.99
          }} transition={{
            duration: 0.2,
            ease: [0.22, 1, 0.36, 1]
          }} onClick={() => {
            setAuthProvider('slack');
            if (isLogin) {
              navigate('/app-screen-mockups/dashboard');
            } else {
              setStep(2);
            }
          }} className="w-full flex items-center justify-center gap-3 px-4 py-3 md:py-3.5 rounded-xl bg-[#2B211A] text-white font-bold transition-shadow hover:shadow-lg hover:shadow-black/5" data-id="element-12702" data-mp="f1owrbmb5qsu">
              
              <SlackIcon size={20} data-id="element-12703" data-mp="2zqw7k5u3xu32" />
              Continue with Slack
            </motion.button>
            {/* Google is a full second front door, not a fallback — plenty of
             teams have no Slack at all. It sits directly under Slack (equal
             weight, lighter fill) so neither reads as the only way in. */}
            <motion.button whileHover={{
            scale: 1.01,
            y: -1
          }} whileTap={{
            scale: 0.99
          }} transition={{
            duration: 0.2,
            ease: [0.22, 1, 0.36, 1]
          }} onClick={() => {
            setAuthProvider('google');
            if (isLogin) {
              navigate('/app-screen-mockups/dashboard');
            } else {
              setStep(20);
            }
          }} className="w-full flex items-center justify-center gap-3 px-4 py-3 md:py-3.5 rounded-xl border border-[#EAE4D9] bg-white font-bold text-[#2B211A] transition-all hover:border-[#D4D0C8] hover:shadow-lg hover:shadow-black/5" data-id="element-12704" data-mp="12yv0yg2sgowx">
              <GoogleIcon size={20} data-id="element-12705" data-mp="1z3vacyienmlk" />
              Continue with Google
            </motion.button>
            {/* Teams isn't live. Rather than a dead disabled row, hovering
             turns it into the one thing you can actually do: join the list. */}
            <button type="button" onClick={() => setShowWaitlist(true)} className="group relative flex w-full items-center justify-center gap-3 rounded-xl border border-[#EFEAE1] bg-white/60 px-4 py-3 font-bold text-neutral-400 transition-all hover:border-[#D4D0C8] hover:bg-white hover:text-[#2B211A] md:py-3.5" data-id="element-12706" data-mp="1p55rb2ervw3q">
              <img src="https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/microsoft-teams.svg" alt="" aria-hidden="true" className="h-5 w-5 opacity-40 transition-opacity group-hover:opacity-100" data-id="element-12707" data-mp="1k8p5iev1pttt" />
              <span className="group-hover:hidden" data-id="element-12708" data-mp="1bc6gvetuks31">Continue with Teams</span>
              <span className="hidden group-hover:inline" data-id="element-12709" data-mp="whjovxha6xig">Join the waitlist</span>
              <span className="absolute right-4 rounded-full bg-[#F0EBE2] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-neutral-500 transition-colors group-hover:bg-[#FFEDD5] group-hover:text-[#B4470F]" data-id="element-12710" data-mp="11f1q11p248fw">
                Soon
              </span>
            </button>
          </div>

          {/* Book a demo — a real third option for buyers who want a walkthrough
           before connecting a workspace, but outlined and set apart by an "or"
           rule so the two auth buttons stay the primary path. */}
          {!isLogin && <>
              <div className="mt-4 flex items-center gap-4" aria-hidden="true" data-id="element-12711" data-mp="wrk3vn1jhpwm">
                <div className="h-px flex-1 bg-neutral-200" data-id="element-12712" data-mp="wphdv30dzwrw" />
                <span className="text-[13px] text-neutral-400" data-id="element-12713" data-mp="1ahnl1zlkatf7">or</span>
                <div className="h-px flex-1 bg-neutral-200" data-id="element-12714" data-mp="15jdq9q3r1uu4" />
              </div>
              <a href={TALK_TO_SALES_URL} target="_blank" rel="noopener noreferrer" className="mt-4 flex w-full items-center justify-center rounded-xl border border-[#EAE4D9] bg-white px-4 py-3 font-bold text-[#2B211A] transition-all hover:border-[#D4D0C8] hover:bg-neutral-50" data-id="element-12715" data-mp="1kh650g1h2f79">
                Book a demo
              </a>
              <p className="mt-4 text-center text-[13px] text-neutral-400" data-id="element-12716" data-mp="1i9aoh8hm66hl">
                No credit card required
              </p>
            </>}

          {/* Your Workspaces — returning users with remembered sessions can
           jump straight into a workspace instead of re-authing. Only the most
           recent one shows by default: three near-identical rows made the
           screen feel busy, and the other two are one click away. */}
          {!signedOut && <div className="w-full mt-5 md:mt-6" data-id="element-12717" data-mp="21yuxok1m5gq5">
              <div className="flex items-center gap-4 mb-3" data-id="element-12718" data-mp="2fuykvte2kot4">
                <div className="h-px bg-neutral-200 flex-1" data-id="element-12719" data-mp="2axg9lwckxxhy" />
                <span className="text-[11px] font-bold tracking-wider text-neutral-400 uppercase" data-id="element-12720" data-mp="2axzpz8ml0dtv">
                  Continue as
                </span>
                <div className="h-px bg-neutral-200 flex-1" data-id="element-12721" data-mp="gjp6lforabvh" />
              </div>

              <div className="rounded-2xl border border-neutral-200 bg-white divide-y divide-neutral-100 overflow-hidden" data-id="element-12722" data-mp="2s6smijpu2sjl">
                {[{
              initials: 'T',
              name: 'Tonic',
              email: 'faith@tonichq.com'
            }, ...(showAllWorkspaces ? [{
              initials: 'B',
              name: 'Brightwave',
              email: 'sarah.chen@brightwave.io'
            }, {
              initials: 'N',
              name: 'Northwind Labs',
              email: 'marcus.lee@northwindlabs.com'
            }] : [])].map(ws => <button key={ws.name} onClick={() => navigate('/app-screen-mockups/dashboard')} className="w-full flex items-center gap-3.5 px-4 py-3 hover:bg-[#FFFCF8] transition-colors group text-left" data-id="element-12723" data-mp="2egp5c75t9p10">
                    <div className="w-9 h-9 shrink-0 rounded-lg bg-[#2B211A] text-white flex items-center justify-center font-bold text-[13px]" data-id="element-12724" data-mp="24h6npxddifc4">
                      {ws.initials}
                    </div>
                    <div className="flex min-w-0 flex-1 flex-col" data-id="element-12725" data-mp="2os5ex6x38v6">
                      <span className="text-sm font-semibold text-[#2B211A] truncate" data-id="element-12726" data-mp="19fog93pmo8w">
                        {ws.name}
                      </span>
                      <span className="text-[13px] text-neutral-500 truncate" data-id="element-12727" data-mp="3sh1ycjh0vmy7">
                        {ws.email}
                      </span>
                    </div>
                    <div className="flex w-6 items-center justify-end shrink-0" data-id="element-12728" data-mp="3jflr2kli80y7">
                      <ArrowRight size={16} className="text-neutral-300 group-hover:text-[#EA580C] group-hover:translate-x-0.5 transition-all" data-id="element-12729" data-mp="jxkph8szfv40" />
                    </div>
                  </button>)}
              </div>

              <div className="mt-3 flex items-center justify-center gap-2 text-[13px] text-neutral-400" data-id="element-12730" data-mp="a7jdhfdwm35g">
                {!showAllWorkspaces && <>
                    <button onClick={() => setShowAllWorkspaces(true)} className="transition-colors hover:text-[#2B211A]" data-id="element-12731" data-mp="12px1yodcocrv">
                      Use another workspace
                    </button>
                    <span aria-hidden="true" data-id="element-12732" data-mp="2glomp4nizn3g">·</span>
                  </>}
                <button onClick={() => setSignedOut(true)} className="transition-colors hover:text-[#2B211A]" data-id="element-12733" data-mp="2gnrmggehculd">
                  Sign out
                </button>
              </div>
            </div>}

          {/* Returning users — quiet sign-in link, but ONLY when there are no
           remembered workspaces on this device (the Your Workspaces list IS
           the signed-in path, so showing both would be redundant). Hidden on
           /login, which already is the sign-in. */}
          {!isLogin && signedOut && <p className="mt-8 text-center text-sm text-neutral-500" data-id="element-12734" data-mp="39a2l3lij0lfj">
              Already have an account?{' '}
              <Link to="/app-screen-mockups/sign-in" className="font-bold text-[#2B211A] underline underline-offset-2 hover:text-[#EA580C] transition-colors" data-id="element-12735" data-mp="3dbfprs9vuikv">
              
                Sign in
              </Link>
            </p>}

          <p className="text-center text-[11px] text-neutral-400 mt-3 pt-0 mb-0 leading-relaxed max-w-[320px] mx-auto" data-id="element-12736" data-mp="3nan6mpftsvcq">
            By continuing, you agree to Giga's{' '}
            <a href="#" className="underline hover:text-[#2B211A] transition-colors" data-id="element-12737" data-mp="34ol6886uwyeu">
              
              Terms of Service
            </a>{' '}
            and{' '}
            <a href="#" className="underline hover:text-[#2B211A] transition-colors" data-id="element-12738" data-mp="3edv11vt34v6n">
              
              Privacy Policy
            </a>
            .
          </p>
        </div>
      </div>
    </div>;
}